<template>
    <div class="pl-page" :style="{'max-width': '99%', 'width': '98%', 'margin-left': 'auto', 'margin-right': 'auto', 'margin-top': '10px'}">

        <!-- ============================================== -->
        <!-- CSV Upload Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-csv-upload" scrollable title="UPLOAD PRODUCTS VIA CSV" size="xl" hide-footer>
            <div class="csv-upload-container">
                <b-alert variant="info" show>
                    <h6>CSV Upload Instructions:</h6>
                    <ul>
                        <li>CSV must contain these 12 columns in order:</li>
                        <li><strong>SKU, DESCRIPTION, CATEGORY, SUB CATEGORY, VENDOR, QTY PER BOX, FOB PRICE, LANDED CST, WHLSL PRICE, SELL PRICE, CUBES, SPIFF</strong></li>
                        <li>First row should be headers</li>
                        <li>SKU must be unique and without spaces</li>
                    </ul>
                </b-alert>
                <b-form-file v-model="csvFile" :state="Boolean(csvFile)" placeholder="Choose a CSV file..." accept=".csv" @input="onFileSelect"></b-form-file>
                <div v-if="csvPreview.length > 0" class="mt-3">
                    <h6>Preview (First 5 rows):</h6>
                    <b-table striped hover small :items="csvPreview" :fields="csvFields"></b-table>
                </div>
                <div class="mt-3 text-right">
                    <b-button variant="secondary" @click="clearCsvUpload" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="processCsvUpload" :disabled="!csvFile || uploadingCsv">{{ uploadingCsv ? 'Uploading...' : 'Upload Products' }}</b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- EDIT PRODUCT MODAL -->
        <!-- ============================================== -->
        <b-modal
            content-class="pl-modal"
            dialog-class="pl-dialog"
            id="modal-edit-product"
            :title="'Edit Product — ' + (editForm.sku || '')"
            size="lg"
            centered
            hide-footer
            @hidden="resetEditForm"
        >
            <div v-if="editForm.sku" class="edit-product-form">
                <div class="edit-identity mb-3">
                    <div class="edit-identity-img">
                        <img v-if="editForm._image" :src="editForm._image" alt="" />
                        <b-icon v-else icon="image" font-scale="2"></b-icon>
                    </div>
                    <div class="edit-identity-meta">
                        <strong>{{ editForm.product_name }}</strong>
                        <span class="edit-identity-sku">SKU <b>{{ editForm.sku }}</b></span>
                    </div>
                </div>

                <b-tabs v-model="editActiveTab" content-class="mt-3" nav-class="edit-tabs" fill>
                    <!-- ============ MAIN DETAILS TAB ============ -->
                    <b-tab title="Main Details" active>
                        <div class="row">
                            <div class="col-md-12 mb-3">
                                <label class="filter-label required">Product Name</label>
                                <b-form-input v-model="editForm.product_name" size="sm"></b-form-input>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label required">Category</label>
                                <b-form-select
                                    v-model="editForm.category_id"
                                    :options="packet.categories"
                                    size="sm"
                                    @change="onEditCategoryChange"
                                ></b-form-select>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Sub Category</label>
                                <b-form-select
                                    v-model="editForm.subcategory_id"
                                    :options="editSubcategoryOptions"
                                    size="sm"
                                    :disabled="!editForm.category_id"
                                ></b-form-select>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label required">Vendor</label>
                                <b-form-select
                                    v-model="editForm.vendor_id"
                                    :options="packet.vendors"
                                    size="sm"
                                ></b-form-select>
                            </div>
                            <div class="col-md-6 mb-3" v-if="!showSimplifiedColumns">
                                <label class="filter-label">Qty / Box</label>
                                <b-form-input
                                    v-model.number="editForm.quantity_per_box"
                                    type="number"
                                    min="1"
                                    size="sm"
                                ></b-form-input>
                            </div>
                            <div class="col-md-6 mb-3" v-if="!showSimplifiedColumns">
                                <label class="filter-label">Cubes</label>
                                <b-form-input
                                    v-model.number="editForm.cubes"
                                    type="number"
                                    step="0.01"
                                    size="sm"
                                ></b-form-input>
                            </div>
                            <div class="col-md-6 mb-3" v-if="!showSimplifiedColumns">
                                <label class="filter-label">Spiff</label>
                                <b-form-input
                                    v-model.number="editForm.spiff"
                                    type="number"
                                    step="0.01"
                                    size="sm"
                                ></b-form-input>
                            </div>

                            <div class="col-12"><hr /></div>

                            <div class="col-md-4 mb-3">
                                <label class="filter-label">Type</label>
                                <b-form-select v-model="editForm.type_id" :options="typeOptions" size="sm"></b-form-select>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label class="filter-label">Color</label>
                                <b-form-select v-model="editForm.color_id" :options="colorOptions" size="sm"></b-form-select>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label class="filter-label">Size</label>
                                <b-form-select v-model="editForm.size_id" :options="sizeOptions" size="sm"></b-form-select>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Material</label>
                                <b-form-select v-model="editForm.material_id" :options="materialOptions" size="sm"></b-form-select>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Functionality</label>
                                <b-form-select v-model="editForm.functionality_id" :options="functionalityOptions" size="sm"></b-form-select>
                            </div>
                        </div>
                    </b-tab>

                    <!-- ============ PRICING TAB ============ -->
                    <b-tab title="Pricing">
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label class="filter-label required">Selling Price</label>
                                <b-input-group size="sm" prepend="$">
                                    <b-form-input v-model.number="editForm.selling_price" type="number" step="0.01" min="0"></b-form-input>
                                </b-input-group>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">FOB Price</label>
                                <b-input-group size="sm" prepend="$">
                                    <b-form-input v-model.number="editForm.fob_price" type="number" step="0.01" min="0"></b-form-input>
                                </b-input-group>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Landed Cost</label>
                                <b-input-group size="sm" prepend="$">
                                    <b-form-input v-model.number="editForm.landed_cost" type="number" step="0.01" min="0"></b-form-input>
                                </b-input-group>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Wholesale Price</label>
                                <b-input-group size="sm" prepend="$">
                                    <b-form-input v-model.number="editForm.wholesale_price" type="number" step="0.01" min="0"></b-form-input>
                                </b-input-group>
                            </div>

                            <div class="col-12">
                                <div class="alert alert-info mb-0" v-if="editProfitMargin !== null">
                                    <b-icon icon="graph-up"></b-icon>
                                    Profit Margin:
                                    <strong :class="editProfitMargin >= 0 ? 'is-good' : 'is-bad'">{{ editProfitMargin }}%</strong>
                                    &nbsp;·&nbsp;
                                    Suggested Cost (2× landed):
                                    <strong>${{ formatPriceNum((parseFloat(editForm.landed_cost) || 0) * 2) }}</strong>
                                </div>
                            </div>
                        </div>
                    </b-tab>

                    <!-- ============ SALE TAB ============ -->
                    <b-tab title="Sale">
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Special Discount Price</label>
                                <b-input-group size="sm" prepend="$">
                                    <b-form-input
                                        v-model.number="editForm.special_discount_price"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                    ></b-form-input>
                                </b-input-group>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Max Discount %</label>
                                <b-input-group size="sm" append="%">
                                    <b-form-input
                                        v-model.number="editForm.max_discount_percentage"
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="100"
                                    ></b-form-input>
                                </b-input-group>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Sale Starts</label>
                                <date-picker
                                    v-model="editForm.special_discount_start_date"
                                    format="MM-DD-YYYY"
                                    value-type="format"
                                    :clearable="false"
                                    style="width:100%; padding: 8px; border: 1px solid #ced4da; border-radius: 6px;"
                                ></date-picker>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="filter-label">Sale Ends</label>
                                <date-picker
                                    v-model="editForm.special_discount_end_date"
                                    format="MM-DD-YYYY"
                                    value-type="format"
                                    :clearable="false"
                                    style="width:100%; padding: 8px; border: 1px solid #ced4da; border-radius: 6px;"
                                ></date-picker>
                            </div>
                        </div>

                        <div class="mt-3">
                            <b-alert variant="light" show class="mb-0">
                                <b-icon icon="info-circle" class="mr-1"></b-icon>
                                Clearing the special discount price will remove this product from sale.
                            </b-alert>
                        </div>
                    </b-tab>
                </b-tabs>

                <div class="d-flex justify-content-end mt-4 pt-3 edit-footer">
                    <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('modal-edit-product')">Cancel</b-button>
                    <b-button variant="primary" @click="saveEditedProduct" :disabled="savingEditProduct">
                        <b-spinner v-if="savingEditProduct" small class="mr-2"></b-spinner>
                        <b-icon v-else icon="check2" class="mr-1"></b-icon>
                        Save Changes
                    </b-button>
                </div>
            </div>
            <div v-else class="text-center py-5">
                <b-spinner></b-spinner>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Low Inventory Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" v-model="showLowInventoryModal" title="Low Inventory Alert Configuration" size="lg" @ok="saveLowInventoryAlert" @hidden="resetLowInventoryModal" centered>
            <div v-if="currentAlertProduct" class="low-inventory-form">
                <div class="form-group mb-3">
                    <label class="form-label required">Product</label>
                    <b-input :value="currentAlertProduct.product_name || currentAlertProduct.sku" readonly class="bg-light"></b-input>
                    <small class="text-muted">SKU: {{ currentAlertProduct.sku }}</small>
                </div>
                <div class="form-group mb-3">
                    <label class="form-label required">Alert Type</label>
                    <b-form-radio-group v-model="alertConfig.alert_type" :options="alertTypeOptions" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
                </div>
                <div class="form-group mb-3">
                    <label class="form-label required">Threshold Quantity</label>
                    <b-input v-model.number="alertConfig.threshold_quantity" type="number" min="1" step="1"></b-input>
                </div>
                <div class="form-group mb-3">
                    <label class="form-label">Notification Method</label>
                    <b-form-checkbox-group v-model="alertConfig.notification_methods">
                        <b-form-checkbox value="email">Email Alert</b-form-checkbox>
                        <b-form-checkbox value="dashboard">Dashboard Notification</b-form-checkbox>
                        <b-form-checkbox value="sms">SMS Alert</b-form-checkbox>
                    </b-form-checkbox-group>
                </div>
                <div class="form-group mb-3" v-if="alertConfig.notification_methods.includes('email')">
                    <label class="form-label">Alert Email(s)</label>
                    <b-input v-model="alertConfig.alert_emails" placeholder="comma separated"></b-input>
                </div>
                <div class="form-group mb-3">
                    <label class="form-label">Additional Notes</label>
                    <b-textarea v-model="alertConfig.notes" rows="2"></b-textarea>
                </div>
                <div v-if="currentInventoryLevel !== null" class="alert alert-info">
                    <b-icon-info-circle></b-icon-info-circle> Current inventory: <strong>{{ currentInventoryLevel }} units</strong>
                </div>
            </div>
            <div v-else class="text-center py-4">
                <b-icon-exclamation-triangle-fill variant="warning" font-scale="3"></b-icon-exclamation-triangle-fill>
                <h5 class="mt-3">Loading...</h5>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="showLowInventoryModal = false">Cancel</b-button>
                <b-button variant="warning" @click="saveLowInventoryAlert" :disabled="!isAlertConfigValid || !currentAlertProduct">
                    <b-icon-bell></b-icon-bell> Save Alert
                </b-button>
            </template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Bulk Status Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-bulk-status" title="Change Status" @ok="executeBulkStatusChange" @cancel="bulkStatusModalVisible = false">
            <div class="text-center">
                <b-alert show variant="info"><b-icon icon="info-circle" class="mr-2"></b-icon>Changing status for <strong>{{ selectedProducts.length }}</strong> product(s)</b-alert>
                <p class="mt-3">
                    <strong>Action:</strong>
                    <span v-if="bulkStatusAction === 'active'" class="text-success">ACTIVATE</span>
                    <span v-if="bulkStatusAction === 'notactive'" class="text-danger">DISABLE</span>
                </p>
                <b-form-group label="Products:">
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 5px;">
                        <div v-for="sku in selectedProducts" :key="sku" class="text-left"><small>{{ sku }}</small></div>
                    </div>
                </b-form-group>
                <b-form-checkbox v-model="updateChildren" class="mt-3">Also update child products</b-form-checkbox>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-status')">Cancel</b-button>
                <b-button variant="primary" @click="executeBulkStatusChange">Confirm</b-button>
            </template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Bulk Discontinued Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-bulk-discontinued" :title="bulkDiscontinuedAction ? 'Mark Discontinued' : 'Remove Discontinued'" @ok="executeBulkDiscontinuedChange" @hidden="discontinuedModalVisible = false" centered>
            <div class="text-center">
                <b-alert show :variant="bulkDiscontinuedAction ? 'warning' : 'info'">
                    <b-icon :icon="bulkDiscontinuedAction ? 'archive' : 'arrow-counterclockwise'" class="mr-2"></b-icon>
                    {{ bulkDiscontinuedAction ? 'Marking as discontinued' : 'Removing discontinued' }} for <strong>{{ selectedProducts.length }}</strong> product(s)
                </b-alert>
                <div v-if="bulkDiscontinuedAction" class="mt-3">
                    <b-form-group label="Reason (Optional)"><b-textarea v-model="discontinuedReason" rows="2"></b-textarea></b-form-group>
                </div>
                <b-form-checkbox v-model="updateChildrenDiscontinued" class="mt-3">Also update child products</b-form-checkbox>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-discontinued')">Cancel</b-button>
                <b-button :variant="bulkDiscontinuedAction ? 'warning' : 'info'" @click="executeBulkDiscontinuedChange">Confirm</b-button>
            </template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Bulk New Arrival Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-bulk-new-arrival" :title="bulkNewArrivalAction ? 'Mark New Arrival' : 'Remove New Arrival'" @ok="executeBulkNewArrivalChange" @hidden="newArrivalModalVisible = false" centered>
            <div class="text-center">
                <b-alert show :variant="bulkNewArrivalAction ? 'success' : 'info'">
                    <b-icon :icon="bulkNewArrivalAction ? 'star' : 'star-slash'" class="mr-2"></b-icon>
                    {{ bulkNewArrivalAction ? 'Marking new arrival' : 'Removing new arrival' }} for <strong>{{ selectedProducts.length }}</strong> product(s)
                </b-alert>
                <div v-if="bulkNewArrivalAction" class="mt-3">
                    <b-form-group label="Duration (Days)"><b-form-input v-model.number="newArrivalDays" type="number" min="1" max="365" class="text-center"></b-form-input></b-form-group>
                </div>
                <b-form-checkbox v-model="updateChildrenNewArrival" class="mt-3">Also update child products</b-form-checkbox>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-new-arrival')">Cancel</b-button>
                <b-button :variant="bulkNewArrivalAction ? 'success' : 'info'" @click="executeBulkNewArrivalChange">Confirm</b-button>
            </template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Barcode Viewer Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-barcode-viewer" size="md" hide-footer centered title="Product Barcode">
            <div class="text-center">
                <b-img :src="currentBarcodeImage" fluid style="max-height: 70vh; object-fit: contain;" />
                <div class="mt-3"><strong>{{ currentProductName }}</strong><div class="small text-muted">{{ currentProductSku }}</div></div>
                <div class="mt-2">
                    <b-button size="sm" variant="outline-primary" @click="downloadBarcodeImage(currentBarcodeImage, currentProductSku)"><b-icon icon="download"></b-icon> Download</b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Barcode Progress Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-barcode-progress" title="Barcode Generation Progress" size="lg" :hide-footer="true" :no-close-on-backdrop="true" :no-close-on-esc="true" :busy="generatingBarcodes">
            <div class="text-center">
                <b-progress :value="barcodeProgress.percentage" :max="100" variant="success" animated class="mb-3" :show-value="true"></b-progress>
                <div class="mb-2"><strong>Status:</strong> {{ barcodeProgress.message }}</div>
                <div class="row">
                    <div class="col-6"><div class="border rounded p-2"><small class="text-muted">Processed</small><h5>{{ barcodeProgress.processed }} / {{ barcodeProgress.total }}</h5></div></div>
                    <div class="col-6"><div class="border rounded p-2"><small class="text-muted">Results</small><h5><span class="text-success">{{ barcodeProgress.successful }}</span> / <span class="text-danger">{{ barcodeProgress.failed }}</span></h5></div></div>
                </div>
                <div v-if="barcodeProgress.errors.length > 0" class="mt-3">
                    <b-alert variant="danger" show>
                        <div style="max-height: 200px; overflow-y: auto;">
                            <div v-for="(e, i) in barcodeProgress.errors.slice(0, 5)" :key="i" class="small">{{ e.sku }}: {{ e.error }}</div>
                        </div>
                    </b-alert>
                </div>
                <div class="mt-3">
                    <b-spinner v-if="generatingBarcodes" small class="mr-2"></b-spinner>
                    <b-button v-if="!generatingBarcodes" variant="primary" @click="$bvModal.hide('modal-barcode-progress')">Close</b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Image Viewer Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-image-viewer" size="lg" hide-footer centered title="Product Image">
            <div class="text-center">
                <b-img :src="currentFullSizeImage" fluid style="max-height: 70vh; object-fit: contain;" />
                <div class="mt-3"><strong>{{ currentProductName }}</strong></div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Product Stats Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-product-stats" scrollable title="PRODUCT STATS" size="xl" hide-footer style="min-height: 700px;">
            <ProductStatsModal v-on:done-product-stats="close_item_stats" v-on:changed-product="monitor_modal_change" style="min-height: 500px;" :viewdata="stats_item" />
        </b-modal>

        <!-- ============================================== -->
        <!-- Sold Details Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-sold-details" scrollable :title="`SOLD DETAILS - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="lg" hide-footer>
            <div class="details-container">
                <b-table striped hover :items="soldDetails" :fields="detailFields">
                    <template #cell(value)="data"><b-badge variant="success">{{ data.value }}</b-badge></template>
                </b-table>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Order History Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-order-details" scrollable :title="`ORDER HISTORY - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="xl" hide-footer>
            <div v-if="isLoadingOrderDetails" class="text-center py-5"><b-spinner variant="primary"></b-spinner><p class="mt-2">Loading...</p></div>
            <div v-else-if="orderHistory && orderHistory.length > 0">
                <b-row class="mb-3">
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ orderStats.total_orders }}</h5><small>Total Orders</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ orderStats.total_quantity }}</h5><small>Total Quantity</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ Object.keys(orderStats.by_status).join(', ') }}</h5><small>Status</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ moment(orderHistory[0].timeline[0].date).fromNow() }}</h5><small>Last</small></b-card></b-col>
                </b-row>
                <b-row><b-col md="12">
                    <h6>Complete Order History</h6>
                    <div style="max-height: 400px; overflow-y: auto;">
                        <b-table striped hover small :items="allOrderEvents" :fields="eventFields">
                            <template #cell(date)="data">{{ moment(data.value).format('YYYY-MM-DD HH:mm') }}</template>
                            <template #cell(status)="data"><b-badge :variant="getStatusVariant(data.value)">{{ data.value }}</b-badge></template>
                            <template #cell(quantity)="data"><b-badge variant="info">{{ data.value }}</b-badge></template>
                        </b-table>
                    </div>
                </b-col></b-row>
            </div>
            <div v-else class="text-center py-5"><b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon><h5 class="mt-3">No order history</h5></div>
            <template #modal-footer><b-button variant="secondary" @click="$bvModal.hide('modal-order-details')">Close</b-button></template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Received History Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-received-details" scrollable :title="`RECEIVED HISTORY - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="xl" hide-footer>
            <div v-if="isLoadingReceivedDetails" class="text-center py-5"><b-spinner variant="primary"></b-spinner><p class="mt-2">Loading...</p></div>
            <div v-else-if="receivedHistory && receivedHistory.length > 0">
                <b-row class="mb-3">
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ receivedStats.total_received }}</h5><small>Events</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ receivedStats.total_quantity }}</h5><small>Qty</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ Object.keys(receivedStats.by_type).join(', ') }}</h5><small>Types</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ receivedHistory[0] && receivedHistory[0].timeline && receivedHistory[0].timeline.length > 0 ? moment(receivedHistory[0].timeline[0].date).fromNow() : 'N/A' }}</h5><small>Last</small></b-card></b-col>
                </b-row>
                <b-row><b-col md="12">
                    <h6>Complete Received History</h6>
                    <div style="max-height: 400px; overflow-y: auto;">
                        <b-table striped hover small :items="allReceivedEvents" :fields="eventFields">
                            <template #cell(date)="data">{{ moment(data.value).format('YYYY-MM-DD HH:mm') }}</template>
                        </b-table>
                    </div>
                </b-col></b-row>
            </div>
            <div v-else class="text-center py-5"><b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon><h5 class="mt-3">No received history</h5></div>
            <template #modal-footer><b-button variant="secondary" @click="$bvModal.hide('modal-received-details')">Close</b-button></template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Scheduled History Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-scheduled-details" scrollable :title="`SCHEDULED HISTORY - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="xl" hide-footer>
            <div v-if="isLoadingScheduledDetails" class="text-center py-5"><b-spinner variant="primary"></b-spinner><p class="mt-2">Loading...</p></div>
            <div v-else-if="scheduledHistory && scheduledHistory.length > 0">
                <b-row class="mb-3">
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ scheduledStats.total_scheduled }}</h5><small>Total</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ scheduledStats.total_quantity }}</h5><small>Qty</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ Object.keys(scheduledStats.by_status).join(', ') }}</h5><small>Status</small></b-card></b-col>
                    <b-col md="3"><b-card class="text-center" bg-variant="light"><h5>{{ scheduledHistory[0] && scheduledHistory[0].timeline && scheduledHistory[0].timeline.length > 0 ? moment(scheduledHistory[0].timeline[0].date).fromNow() : 'N/A' }}</h5><small>Last</small></b-card></b-col>
                </b-row>
                <b-row><b-col md="12">
                    <h6>Complete Scheduled History</h6>
                    <div style="max-height: 400px; overflow-y: auto;">
                        <b-table striped hover small :items="allScheduledEvents" :fields="eventFields">
                            <template #cell(date)="data">{{ moment(data.value).format('YYYY-MM-DD HH:mm') }}</template>
                        </b-table>
                    </div>
                </b-col></b-row>
            </div>
            <div v-else class="text-center py-5"><b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon><h5 class="mt-3">No scheduled history</h5></div>
            <template #modal-footer><b-button variant="secondary" @click="$bvModal.hide('modal-scheduled-details')">Close</b-button></template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Bulk Sale Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-bulk-sale" title="Add Products to Sale" @ok="executeBulkSaleChange" @hidden="saleModalVisible = false" centered size="lg">
            <div class="text-center">
                <b-alert show variant="success"><b-icon icon="tag" class="mr-2"></b-icon>Adding <strong>{{ selectedProducts.length }}</strong> product(s) to Sale</b-alert>
                <div class="mt-3">
                    <b-form-group label="Sale Price (per unit)" class="text-left"><b-form-input v-model="salePrice" type="number" min="0.01" step="0.01" class="text-center"></b-form-input></b-form-group>
                    <b-form-group label="Max Discount Percentage" class="text-left"><b-form-input v-model="saleMaxDiscount" type="number" min="0" max="100" step="0.5" class="text-center"></b-form-input></b-form-group>
                    <b-form-group label="Start Date" class="text-left">
                        <date-picker v-model="saleStartDate" format="MM-DD-YYYY" value-type="format" style="width: 100%; padding: 8px; border: 1px solid #ced4da; border-radius: 4px;" :clearable="false"></date-picker>
                    </b-form-group>
                    <b-form-group label="Valid Until" class="text-left">
                        <date-picker v-model="saleEndDate" format="MM-DD-YYYY" value-type="format" style="width: 100%; padding: 8px; border: 1px solid #ced4da; border-radius: 4px;" :clearable="false"></date-picker>
                    </b-form-group>
                </div>
                <b-form-group label="Products:">
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 5px;">
                        <div v-for="sku in selectedProducts" :key="sku" class="text-left"><small>{{ sku }}</small></div>
                    </div>
                </b-form-group>
                <b-form-checkbox v-model="updateChildrenSale" class="mt-3">Also update child products</b-form-checkbox>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-sale')">Cancel</b-button>
                <b-button variant="success" @click="executeBulkSaleChange" :disabled="!salePrice || salePrice <= 0 || !saleMaxDiscount || !saleStartDate || !saleEndDate"><b-icon icon="tag"></b-icon> Add to Sale</b-button>
            </template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Bulk Clearance Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-bulk-clearance" title="Add Products to Clearance" @ok="executeBulkClearanceChange" @hidden="clearanceModalVisible = false" centered size="lg">
            <div class="text-center">
                <b-alert show variant="danger"><b-icon icon="archive" class="mr-2"></b-icon>Adding <strong>{{ selectedProducts.length }}</strong> product(s) to Clearance</b-alert>
                <div class="mt-3">
                    <b-form-group label="Clearance Price (per unit)" class="text-left"><b-form-input v-model="clearancePrice" type="number" min="0.01" step="0.01" class="text-center"></b-form-input></b-form-group>
                    <b-form-group label="Max Discount Percentage" class="text-left"><b-form-input v-model="clearanceMaxDiscount" type="number" min="0" max="100" step="0.5" class="text-center"></b-form-input></b-form-group>
                    <b-form-group label="Start Date" class="text-left">
                        <date-picker v-model="clearanceStartDate" format="MM-DD-YYYY" value-type="format" style="width: 100%; padding: 8px; border: 1px solid #ced4da; border-radius: 4px;" :clearable="false"></date-picker>
                    </b-form-group>
                    <b-alert variant="warning" show class="mt-2"><b-icon icon="info-circle"></b-icon> Clearance items have 90-day validity</b-alert>
                </div>
                <b-form-group label="Products:">
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 5px;">
                        <div v-for="sku in selectedProducts" :key="sku" class="text-left"><small>{{ sku }}</small></div>
                    </div>
                </b-form-group>
                <b-form-checkbox v-model="updateChildrenClearance" class="mt-3">Also update child products</b-form-checkbox>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-clearance')">Cancel</b-button>
                <b-button variant="danger" @click="executeBulkClearanceChange" :disabled="!clearancePrice || clearancePrice <= 0 || !clearanceMaxDiscount || !clearanceStartDate"><b-icon icon="archive"></b-icon> Add to Clearance</b-button>
            </template>
        </b-modal>

        <!-- ============================================== -->
        <!-- Bulk Image Upload Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-bulk-image-upload" title="BULK IMAGE UPLOAD" size="xl" @hidden="resetBulkImageUpload" hide-footer>
            <div class="bulk-image-upload-container">
                <b-alert variant="info" show>
                    <h6>Instructions:</h6>
                    <ul>
                        <li>Each filename MUST match the product SKU exactly</li>
                        <li>Example: <code>SKU12345.jpg</code></li>
                        <li>Max size per image: 5MB</li>
                    </ul>
                </b-alert>
                <b-form-file v-model="bulkImages" :state="Boolean(bulkImages.length)" placeholder="Choose images or drop..." accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" multiple @input="handleBulkImageSelect"></b-form-file>
                <div v-if="bulkImagePreview.length > 0" class="mt-3">
                    <h6>Selected Images ({{ bulkImagePreview.length }}):</h6>
                    <div class="image-preview-grid">
                        <div v-for="(img, idx) in bulkImagePreview" :key="idx" class="preview-item">
                            <img :src="img.preview" class="preview-thumb" />
                            <div class="preview-info"><strong>{{ img.sku }}</strong><small>{{ img.fileName }}</small></div>
                        </div>
                    </div>
                </div>
                <div class="mt-3 text-right">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-image-upload')" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="processBulkImageUpload" :disabled="bulkImages.length === 0 || uploadingBulkImages">{{ uploadingBulkImages ? 'Uploading...' : `Upload ${bulkImages.length} Images` }}</b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Bulk Image Progress Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-bulk-image-progress" title="Bulk Upload Progress" size="lg" :hide-footer="true" :no-close-on-backdrop="true" :no-close-on-esc="true" :busy="uploadingBulkImages">
            <div class="text-center">
                <b-progress :value="(bulkImageProgress.processed / bulkImageProgress.total) * 100" :max="100" variant="success" animated class="mb-3" :show-value="true"></b-progress>
                <div class="mb-2">Processing {{ bulkImageProgress.current }} of {{ bulkImageProgress.total }}</div>
                <div class="row">
                    <div class="col-4"><div class="border rounded p-2"><small>Total</small><h5>{{ bulkImageProgress.total }}</h5></div></div>
                    <div class="col-4"><div class="border rounded p-2"><small>OK</small><h5 class="text-success">{{ bulkImageProgress.successful }}</h5></div></div>
                    <div class="col-4"><div class="border rounded p-2"><small>Failed</small><h5 class="text-danger">{{ bulkImageProgress.failed }}</h5></div></div>
                </div>
                <div class="mt-3">
                    <b-spinner v-if="uploadingBulkImages" small class="mr-2"></b-spinner>
                    <b-button v-if="!uploadingBulkImages" variant="primary" @click="$bvModal.hide('modal-bulk-image-progress')">Close</b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Image Upload Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-image-upload" size="md" hide-footer centered :title="`Upload Image - ${uploadProduct ? uploadProduct.sku : ''}`" @hidden="resetImageUpload">
            <div class="image-upload-container">
                <div v-if="!imagePreview" class="upload-area" @click="triggerFileInput">
                    <div class="upload-placeholder"><b-icon icon="cloud-upload" font-scale="3"></b-icon><div class="mt-2">Click to select an image</div></div>
                </div>
                <div v-else class="image-preview-container">
                    <img :src="imagePreview" class="preview-image" />
                    <div class="preview-actions mt-2"><b-button size="sm" variant="secondary" @click="clearImage">Remove</b-button></div>
                </div>
                <input ref="fileInput" type="file" accept="image/*" style="display: none" @change="handleFileSelect" />
                <div class="mt-4 text-right">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-image-upload')" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="uploadProductImage" :disabled="!selectedFile || uploadingImage">{{ uploadingImage ? 'Uploading...' : 'Upload Image' }}</b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- Print Barcodes Modal -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-print-barcodes" title="PRINT BARCODE LABELS" size="xl" centered hide-footer @hidden="resetPrintModal">
            <div v-if="printProducts.length > 0">
                <b-alert :variant="printProducts.length > 24 ? 'danger' : 'info'" show>
                    <div class="d-flex justify-content-between align-items-center">
                        <span><strong>{{ printProducts.length }}</strong> of 24</span>
                        <b-button size="sm" variant="outline-danger" @click="clearPrintAll">Clear All</b-button>
                    </div>
                </b-alert>
                <div class="print-grid">
                    <div v-for="(p, idx) in printProducts.slice(0,24)" :key="p.sku" class="print-card">
                        <div class="print-card-header"><span class="print-number">{{ idx + 1 }}</span><b-button size="sm" variant="link" class="p-0 text-danger" @click="removePrintItem(idx)">✕</b-button></div>
                        <img :src="p.barcodeDataUrl" class="print-barcode-preview" />
                        <div class="print-card-text">
                            <div class="print-sku"><strong>{{ p.sku }}</strong></div>
                            <div class="print-name">{{ truncateText(p.product_name, 20) }}</div>
                            <div v-if="printSettings.showPrice" class="print-price">${{ formatPriceNum(p.selling_price) }}</div>
                        </div>
                    </div>
                </div>
                <b-row class="mt-3">
                    <b-col md="3"><b-form-group label="Label Size"><b-form-select v-model="printSettings.labelSize" :options="labelSizeOptions"></b-form-select></b-form-group></b-col>
                    <b-col md="3"><b-form-group label="Copies"><b-form-input type="number" v-model="printSettings.copies" min="1" max="10"></b-form-input></b-form-group></b-col>
                    <b-col md="3"><b-form-group label="Method"><b-form-select v-model="printSettings.printMethod" :options="printMethodOptions"></b-form-select></b-form-group></b-col>
                    <b-col md="3"><b-form-group label="Layout"><b-form-select v-model="printSettings.layout" :options="layoutOptions"></b-form-select></b-form-group></b-col>
                </b-row>
                <b-row class="mt-2"><b-col md="12">
                    <b-form-checkbox v-model="printSettings.showPrice" class="mr-3">Show Price</b-form-checkbox>
                    <b-form-checkbox v-model="printSettings.showProductName" class="mr-3">Show Name</b-form-checkbox>
                    <b-form-checkbox v-model="printSettings.showSku">Show SKU</b-form-checkbox>
                </b-col></b-row>
                <div class="mt-4 text-right">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-print-barcodes')" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="executePrint" :disabled="printProducts.length === 0 || printProducts.length > 24 || isPrinting">
                        <b-spinner v-if="isPrinting" small class="mr-2"></b-spinner>
                        {{ isPrinting ? 'Printing...' : `Print ${printProducts.length} Label(s)` }}
                    </b-button>
                </div>
            </div>
            <div v-else class="text-center py-5"><b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon><h5 class="mt-3">No products selected</h5></div>
        </b-modal>

        <!-- ============================================== -->
        <!-- SINGLE-ITEM SALE / PROMO / CLEARANCE MODAL    -->
        <!-- ============================================== -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-manager-sale" :title="managerSaleForm.actionType + ' — ' + (managerSaleItem ? managerSaleItem.sku : '')" size="md" centered hide-footer @hidden="resetManagerSaleForm">
            <div v-if="managerSaleItem" class="p-2">
                <div class="form-group mb-3">
                    <label class="filter-label">Product</label>
                    <b-form-input :value="managerSaleItem.product_name" readonly class="bg-light"></b-form-input>
                </div>
                <div class="form-group mb-3">
                    <label class="filter-label">Original price</label>
                    <b-form-input :value="'$' + formatPriceNum(managerSaleItem.selling_price)" readonly class="bg-light"></b-form-input>
                </div>
                <div class="form-group mb-3">
                    <label class="filter-label">Sale type</label>
                    <b-form-radio-group v-model="managerSaleForm.actionType" :options="managerSaleTypeOptions" buttons button-variant="outline-primary" size="sm" @change="$nextTick(recalcManagerPreview)"></b-form-radio-group>
                </div>
                <div class="form-group mb-3" v-if="managerSaleForm.actionType !== 'Promo'">
                    <label class="filter-label">Discount %</label>
                    <b-input-group>
                        <b-form-input v-model.number="managerSaleForm.discountPercentage" type="number" min="0.01" max="100" step="0.01" @input="recalcManagerPreview"></b-form-input>
                        <b-input-group-append><b-input-group-text>%</b-input-group-text></b-input-group-append>
                    </b-input-group>
                </div>
                <div class="form-group mb-3" v-else>
                    <label class="filter-label">Discount amount ($)</label>
                    <b-input-group>
                        <b-input-group-prepend><b-input-group-text>$</b-input-group-text></b-input-group-prepend>
                        <b-form-input v-model.number="managerSaleForm.discountAmount" type="number" min="0.01" step="0.01" @input="recalcManagerPreview"></b-form-input>
                    </b-input-group>
                </div>
                <div class="form-group mb-3">
                    <label class="filter-label">Max discount %</label>
                    <b-input-group>
                        <b-form-input v-model.number="managerSaleForm.maxDiscount" type="number" min="0" max="100" step="0.5"></b-form-input>
                        <b-input-group-append><b-input-group-text>%</b-input-group-text></b-input-group-append>
                    </b-input-group>
                </div>
                <div class="form-group mb-3">
                    <label class="filter-label">Start date</label>
                    <date-picker v-model="managerSaleForm.startDate" format="MM-DD-YYYY" value-type="format" :clearable="false" style="width:100%; padding: 8px; border: 1px solid #ced4da; border-radius: 6px;"></date-picker>
                </div>
                <div class="form-group mb-3">
                    <label class="filter-label">End date</label>
                    <date-picker v-model="managerSaleForm.endDate" format="MM-DD-YYYY" value-type="format" :clearable="false" style="width:100%; padding: 8px; border: 1px solid #ced4da; border-radius: 6px;"></date-picker>
                </div>
                <div v-if="managerSalePreview !== null" class="alert alert-info">
                    <b-icon icon="eye-fill"></b-icon> Preview: <strong>${{ formatPriceNum(managerSalePreview) }}</strong>
                </div>
                <div class="text-right mt-3">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-manager-sale')" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="saveManagerSale" :disabled="savingManagerSale">
                        <b-spinner v-if="savingManagerSale" small class="mr-2"></b-spinner> Save
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- STOCK BREAKDOWN DRILL-DOWN MODAL              -->
        <!-- ============================================== -->
        <StockBreakdownModal
            modal-id="modal-stock-breakdown"
            :product-sku="stockModalSku || ''"
            :product-name="stockModalName || ''"
            :location-id="location_id"
            :initial-tab="stockModalTab"
            @closed="closeStockBreakdown"
        />

        <!-- ============================================== -->
        <!-- Main Content -->
        <!-- ============================================== -->
        <div v-if="items_being_loaded == true">
            <loading :active="items_being_loaded" :can-cancel="false" :is-full-page="true"></loading>
        </div>

        <div v-else>
            <!-- COMPACT TOOLBAR -->
            <div class="compact-toolbar">
                <div class="toolbar-group">
                    <b-button variant="outline-success" @click="fetch_init(false, true)" v-b-tooltip.hover.top="'Fetch Products'" size="sm">
                        <b-icon icon="arrow-repeat"></b-icon>
                    </b-button>
                    <b-dropdown v-if="packet.userDetails.userrole === 'admin'" variant="outline-primary" :disabled="downloadingExcel" v-b-tooltip.hover.top="'Download CSV'" size="sm" no-caret toggle-class="text-decoration-none">
                        <template #button-content><b-icon icon="download"></b-icon></template>
                        <b-dropdown-item @click="downloadCSV(false)">Download Current View ({{ filtered_final.length }} items)</b-dropdown-item>
                        <b-dropdown-item @click="downloadCSV(true)">Download All Filtered Data</b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="downloadByCategory" v-if="category_id">Download Entire Category</b-dropdown-item>
                        <b-dropdown-item @click="downloadByVendor" v-if="vendor_id">Download Entire Vendor</b-dropdown-item>
                    </b-dropdown>
                    <b-button v-if="packet.userDetails.userrole === 'admin'" variant="outline-info" @click="showCsvUpload" v-b-tooltip.hover.top="'Upload CSV'" size="sm">
                        <b-icon icon="cloud-upload"></b-icon>
                    </b-button>
                    <b-button v-if="packet.userDetails.userrole === 'admin'" variant="outline-info" @click="showBulkImageUpload" v-b-tooltip.hover.top="'Bulk Image Upload'" size="sm">
                        <b-icon icon="images"></b-icon>
                    </b-button>
                    <b-button v-if="packet.userDetails.userrole === 'admin'" variant="outline-warning" @click="generateAllBarcodes" :disabled="generatingBarcodes" v-b-tooltip.hover.top="'Generate Barcodes'" size="sm">
                        <b-icon icon="upc-scan"></b-icon>
                    </b-button>
                    <b-button v-if="packet.userDetails.userrole === 'admin'" variant="outline-danger" @click="fetchDiscountedProducts" v-b-tooltip.hover.top="'Show Discounted Products'" size="sm">
                        <b-icon icon="tag"></b-icon>
                        <span class="toolbar-label">DISCOUNTS</span>
                        <b-badge v-if="isShowingDiscounted" variant="danger" class="ml-1">Active</b-badge>
                    </b-button>
                    <b-button v-if="packet.userDetails.userrole === 'admin'" variant="outline-success" @click="openPrintModal" :disabled="selectedProducts.length === 0 || selectedProducts.length > 24" v-b-tooltip.hover.top="'Print Barcodes'" size="sm">
                        <b-icon icon="printer"></b-icon>
                        <b-badge v-if="selectedProducts.length > 0" variant="primary" class="ml-1">{{ selectedProducts.length }}</b-badge>
                    </b-button>
                    <b-dropdown v-if="showSelectionMode && selectedProducts.length > 0" variant="primary" :text="'Bulk (' + selectedProducts.length + ')'" size="sm" class="ml-2">
                        <template #button-content><b-icon icon="gear"></b-icon><span class="toolbar-label">Bulk ({{ selectedProducts.length }})</span></template>
                        <b-dropdown-item @click="openBulkStatusModal('active')"><b-icon icon="play-btn" variant="success"></b-icon> Activate</b-dropdown-item>
                        <b-dropdown-item @click="openBulkStatusModal('notactive')"><b-icon icon="pause-btn" variant="danger"></b-icon> Disable</b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="openBulkDiscontinuedModal(true)"><b-icon icon="archive" variant="warning"></b-icon> Mark Discontinued</b-dropdown-item>
                        <b-dropdown-item @click="openBulkDiscontinuedModal(false)"><b-icon icon="arrow-counterclockwise" variant="info"></b-icon> Remove Discontinued</b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="openBulkNewArrivalModal(true)"><b-icon icon="star" variant="success"></b-icon> Mark New Arrival</b-dropdown-item>
                        <b-dropdown-item @click="openBulkNewArrivalModal(false)"><b-icon icon="star-slash" variant="secondary"></b-icon> Remove New Arrival</b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="openBulkSaleModal"><b-icon icon="tag" variant="success"></b-icon> Add to Sale</b-dropdown-item>
                        <b-dropdown-item @click="openBulkClearanceModal"><b-icon icon="archive" variant="danger"></b-icon> Add to Clearance</b-dropdown-item>
                        <b-dropdown-item @click="removeBulkSaleClearance"><b-icon icon="x-circle" variant="secondary"></b-icon> Remove Sale/Clearance</b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="clearSelection"><b-icon icon="x-circle"></b-icon> Clear Selection</b-dropdown-item>
                    </b-dropdown>
                    <b-badge v-if="showSelectionMode" variant="info" class="ml-2">
                        <b-icon icon="check2-square"></b-icon> {{ selectedProducts.length }} selected
                    </b-badge>
                    <b-button v-if="packet.userDetails.userrole === 'admin'" :variant="showSelectionMode ? 'primary' : 'outline-secondary'" @click="toggleSelectionMode" v-b-tooltip.hover.top="'Select Products'" size="sm">
                        <b-icon :icon="showSelectionMode ? 'check2-square' : 'square'"></b-icon>
                        <b-badge v-if="showSelectionMode && selectedProducts.length > 0" variant="light" class="ml-1">{{ selectedProducts.length }}</b-badge>
                    </b-button>
                </div>

                <div class="toolbar-group">
                    <b-button :variant="Showimage ? 'primary' : 'outline-secondary'" @click="toggleimage" v-b-tooltip.hover.top="'Image'" size="sm">
                        <b-icon :icon="Showimage ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">IMAGE</span>
                    </b-button>
                    <b-button :variant="ShowBarcode ? 'primary' : 'outline-secondary'" @click="toggleBarcode" v-b-tooltip.hover.top="'Barcode'" size="sm">
                        <b-icon :icon="ShowBarcode ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">BARCODE</span>
                    </b-button>
                    <b-button :variant="showMainColumns ? 'primary' : 'outline-secondary'" @click="toggleMainColumns" v-b-tooltip.hover.top="'Main'" size="sm">
                        <b-icon :icon="showMainColumns ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">MAIN</span>
                    </b-button>
                    <b-button :variant="showPriceColumns ? 'primary' : 'outline-secondary'" @click="togglePriceColumns" v-b-tooltip.hover.top="'Price'" size="sm">
                        <b-icon :icon="showPriceColumns ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">PRICE</span>
                    </b-button>
                    <b-button :variant="showSaleColumns ? 'primary' : 'outline-secondary'" @click="showSaleColumns = !showSaleColumns" v-b-tooltip.hover.top="'Sale Column'" size="sm">
                        <b-icon :icon="showSaleColumns ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">SALE</span>
                    </b-button>
                    <b-button :variant="showStockColumns ? 'primary' : 'outline-secondary'" @click="showStockColumns = !showStockColumns" v-b-tooltip.hover.top="'Stock Breakdown'" size="sm">
                        <b-icon :icon="showStockColumns ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">STOCK</span>
                    </b-button>
                    <b-button :variant="showDiscountColumns ? 'primary' : 'outline-secondary'" @click="toggleDiscountColumns" v-b-tooltip.hover.top="'Discount'" size="sm">
                        <b-icon :icon="showDiscountColumns ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">DISCOUNT</span>
                    </b-button>
                    <b-button :variant="showDetailColumns ? 'primary' : 'outline-secondary'" @click="toggleDetailColumns" v-b-tooltip.hover.top="'Details'" size="sm">
                        <b-icon :icon="showDetailColumns ? 'eye' : 'eye-slash'"></b-icon><span class="toolbar-label">DETAILS</span>
                    </b-button>
                </div>
            </div>

            <!-- Mobile Filters Toggle -->
            <div v-if="isMobile" class="mobile-filters-toggle mb-2">
                <b-button size="sm" variant="outline-primary" @click="filtersVisible = !filtersVisible" block>
                    <b-icon-funnel-fill class="mr-1" />
                    {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
                    <b-badge v-if="activeFiltersCount > 0" variant="primary" class="ml-1">{{ activeFiltersCount }}</b-badge>
                </b-button>
            </div>

            <!-- Filters -->
            <b-collapse :visible="!isMobile || filtersVisible" style="width: 100%;">
                <b-card class="filter-card" no-body>
                    <div class="filter-primary p-3">
                        <b-row class="align-items-end g-2">
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">VENDOR</label>
                                <b-form-select :options="packet.vendors" v-model="vendor_id" v-on:change="fetch_vendor" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">CATEGORY</label>
                                <b-form-select :options="packet.categories" v-model="category_id" v-on:change="fetch_category" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">SUB CATEGORY</label>
                                <b-form-select :options="subcategoryOptions" v-model="subcategory_id" @change="fetchSubcategory" size="sm" class="filter-select" :disabled="!category_id"></b-form-select>
                            </b-col>
                            <b-col md="3" class="mb-2">
                                <label class="filter-label">SEARCH</label>
                                <b-input-group size="sm" class="filter-search">
                                    <b-form-input v-model="product_query" @keyup.enter="fetch_product_name_sku" placeholder="Name or SKU..." class="filter-input"></b-form-input>
                                    <b-input-group-append>
                                        <b-button variant="outline-secondary" @click="fetch_product_name_sku" size="sm"><b-icon icon="search"></b-icon></b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>
                            <b-col v-if="packet.userDetails.userrole === 'admin'" md="2" class="mb-2">
                                <label class="filter-label">LOCATION</label>
                                <b-form-select :options="packet.locations" v-model="location_id" v-on:change="fetch_location" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            <b-col md="1" class="mb-2 text-right">
                                <b-button variant="link" size="sm" @click="showMoreFilters = !showMoreFilters" class="filter-toggle-btn p-0">
                                    <b-icon :icon="showMoreFilters ? 'chevron-up' : 'chevron-down'"></b-icon> {{ showMoreFilters ? 'Less' : 'More' }}
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>

                    <b-collapse v-model="showMoreFilters">
                        <div class="filter-more p-3 border-top">
                            <b-row class="align-items-end g-2">
                                <b-col md="2" class="mb-2"><label class="filter-label">ITEM TYPE</label><b-form-select :options="itemTypeOptions" v-model="itemTypeFilter" @change="applyFilters" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">ITEM STATUS</label><b-form-select :options="itemStatusOptions" v-model="itemStatusFilter" @change="applyItemStatusFilter" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">PRODUCT STATUS</label><b-form-select :options="statusOptions" v-model="statusFilter" v-on:change="applyStatusFilter" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">DISCONTINUED</label><b-form-select :options="discontinuedOptions" v-model="discontinuedFilter" @change="applyDiscontinuedFilter" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">NEW ARRIVAL</label><b-form-select :options="newArrivalOptions" v-model="newArrivalFilter" @change="applyNewArrivalFilter" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">RECEIVED STATUS</label><b-form-select :options="receivedStatusOptions" v-model="receivedStatusFilter" @change="applyReceivedStatusFilter" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">TYPE</label><b-form-select :options="typeOptions" v-model="typeFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">COLOR</label><b-form-select :options="colorOptions" v-model="colorFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">SIZE</label><b-form-select :options="sizeOptions" v-model="sizeFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select></b-col>
                            </b-row>
                            <b-row class="align-items-end g-2 mt-2">
                                <b-col md="2" class="mb-2"><label class="filter-label">MATERIAL</label><b-form-select :options="materialOptions" v-model="materialFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">FUNCTIONALITY</label><b-form-select :options="functionalityOptions" v-model="functionalityFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">SPIFF</label><b-form-select :options="spiffFilterOptions" v-model="spiffFilter" @change="applySpiffFilter" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="3" class="mb-2">
                                    <label class="filter-label">BEST SELLING</label>
                                    <div class="d-flex align-items-center">
                                        <b-form-select :options="bestSellingOptions" v-model="bestSellingFilter" @change="applyBestSellingFilter" size="sm" class="filter-select flex-grow-1"></b-form-select>
                                        <b-badge v-if="bestSellingFilter" variant="warning" class="ml-2 filter-badge">Essential</b-badge>
                                    </div>
                                </b-col>
                                <b-col md="3" class="mb-2">
                                    <label class="filter-label">SLOWEST SELLING</label>
                                    <div class="d-flex align-items-center">
                                        <b-form-select :options="slowestSellingOptions" v-model="slowestSellingFilter" @change="applySlowestSellingFilter" size="sm" class="filter-select flex-grow-1"></b-form-select>
                                        <b-badge v-if="slowestSellingFilter" variant="danger" class="ml-2 filter-badge">Essential</b-badge>
                                    </div>
                                </b-col>
                                <b-col md="2" class="mb-2"><label class="filter-label">DISCOUNT TYPE</label><b-form-select :options="discountTypeOptions" v-model="discountFilter" @change="fetchDiscountedProducts" size="sm" class="filter-select"></b-form-select></b-col>
                                <b-col md="2" class="mb-2 text-right">
                                    <b-button variant="outline-secondary" size="sm" @click="resetAllFilters" class="reset-filters-btn">
                                        <b-icon icon="arrow-repeat"></b-icon> Reset
                                    </b-button>
                                </b-col>
                            </b-row>
                        </div>
                    </b-collapse>

                    <div v-if="activeFiltersCount > 0" class="filter-active-badges p-2 border-top bg-light">
                        <small class="text-muted mr-2">Active filters:</small>
                        <b-badge v-for="filter in getActiveFiltersList" :key="filter.key" variant="info" class="mr-1 mb-1 filter-badge-active">
                            {{ filter.label }}: {{ filter.value }}
                            <b-icon icon="x" size="xs" class="ml-1" style="cursor: pointer;" @click="removeFilter(filter.key)"></b-icon>
                        </b-badge>
                        <b-button variant="link" size="sm" class="p-0 ml-2" @click="clearAllFilters">Clear all</b-button>
                    </div>

                    <div class="filter-stats-actions p-2 border-top bg-light" v-if="showStatsButton">
                        <b-row class="align-items-center">
                            <b-col md="12" class="text-right">
                                <b-button variant="info" size="sm" @click="fetchCategoryStockStats" :disabled="isLoadingCategoryStats" class="px-4">
                                    <b-icon icon="bar-chart-fill" class="mr-2"></b-icon>
                                    <span v-if="isLoadingCategoryStats"><b-spinner small class="mr-1"></b-spinner>Loading...</span>
                                    <span v-else>Show Stats for Stock</span>
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                </b-card>
            </b-collapse>

            <!-- Products List -->
            <div v-if="displayedItems.length > 0" class="diver pc-list mt-2" @scroll="handleScroll">
                <div class="pc-bar">
                    <div class="pc-bar-sort">
                        <span class="pc-bar-label"><span class="pc-ico"><b-icon icon="arrow-down-up"></b-icon></span> Sort by</span>
                        <select class="pc-bar-select" :value="activeSortKey || ''" @change="onSortSelect($event.target.value)">
                            <option value="" disabled>Choose a field</option>
                            <option v-for="opt in sortOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
                        </select>
                        <button v-if="activeSortKey" type="button" class="pc-bar-btn" @click="sortby(activeSortKey)">
                            <span class="pc-ico"><b-icon :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></span>
                            {{ activeSortAsc ? 'Ascending' : 'Descending' }}
                        </button>
                    </div>
                    <div class="pc-bar-view">
                        <span class="pc-bar-count">{{ filtered_final.length }} products</span>
                        <div class="pc-seg" role="group">
                            <button type="button" :class="{ 'is-on': pcExpandAll }" @click="pcSetExpandAll(true)">
                                <span class="pc-ico"><b-icon icon="list-ul"></b-icon></span> Detailed
                            </button>
                            <button type="button" :class="{ 'is-on': !pcExpandAll }" @click="pcSetExpandAll(false)">
                                <span class="pc-ico"><b-icon icon="list"></b-icon></span> Compact
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Column Header -->
                <div class="pc-head" :style="pcGridStyle">
                    <div v-if="pcShow.image" class="pc-head-cell">
                        <label v-if="showSelectionMode" class="pc-check pc-check--head">
                            <b-form-checkbox v-model="selectAll" @change="toggleSelectAll" size="sm"></b-form-checkbox>
                            <span>All</span>
                        </label>
                    </div>
                    <div class="pc-head-cell">
                        <button type="button" :class="pcSortClass('product_name')" @click="sortby('product_name')">Product<b-icon v-if="activeSortKey === 'product_name'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                        <button type="button" :class="pcSortClass('sku')" @click="sortby('sku')">SKU<b-icon v-if="activeSortKey === 'sku'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                    </div>
                  
                    <div class="pc-head-cell is-num">
                        <button type="button" :class="pcSortClass('selling_price')" @click="sortby('selling_price')">Price<b-icon v-if="activeSortKey === 'selling_price'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                    </div>
                    <div v-if="showSaleColumns" class="pc-head-cell is-num">
                        <span class="pc-head-static">Sale / Promo</span>
                    </div>
                    <div v-if="showStockColumns" class="pc-head-cell is-num">
                        <span class="pc-head-static">Stock</span>
                    </div>
                    <div class="pc-head-cell is-num">
                        <span class="pc-head-static">Totals</span>
                    </div>
                    <div class="pc-head-cell"></div>
                </div>

                <!-- Category Totals -->
                <div v-if="showCategoryStats && categoryStats.category_id" class="pc-stats">
                    <div class="pc-stats-title">
                        <span class="pc-ico"><b-icon icon="bar-chart-fill"></b-icon></span>
                        <div><span>Category totals</span><strong>{{ categoryStats.category_name }}</strong></div>
                    </div>
                    <div class="pc-stats-grid">
                        <div><span>In stock</span><b>{{ categoryStats.total_in_stock }}</b></div>
                        <div><span>Reserved</span><b>{{ categoryStats.total_reserved }}</b></div>
                        <div class="is-good"><span>Available</span><b>{{ categoryStats.total_available }}</b></div>
                        <div v-if="!showSimplifiedColumns"><span>Layawayed</span><b>{{ categoryStats.total_layawayed }}</b></div>
                        <div v-if="!showSimplifiedColumns" class="is-bad"><span>Missing</span><b>{{ categoryStats.total_missing }}</b></div>
                        <div><span>Sold so far</span><b>{{ categoryStats.total_sold_so_far }}</b></div>
                        <div><span>Ordered</span><b>{{ categoryStats.total_ordered }}</b></div>
                        <div><span>Scheduled</span><b>{{ categoryStats.total_scheduled }}</b></div>
                    </div>
                </div>

                <!-- Product Cards -->
                <article v-for="(item, idx) in filtered_final" :key="item.sku + '-' + idx"
                         class="pc-card"
                         :class="[
                             'is-' + pcStockState(item),
                             {
                                 'is-open': pcIsOpen(item),
                                 'is-selected': showSelectionMode && selectedProducts.includes(item.sku),
                                 'is-inactive': item.status && item.status !== 'active'
                             }
                         ]">
                    <div class="pc-sum" :style="pcGridStyle">
                        <!-- Image -->
                        <div v-if="pcShow.image" class="pc-img">
                            <label v-if="showSelectionMode" class="pc-check" @click.stop>
                                <b-form-checkbox v-model="selectedProducts" :value="item.sku" @change="updateSelectAll" size="sm"></b-form-checkbox>
                            </label>
                            <button v-if="getProductImage(item) && !item._imgError" type="button" class="pc-img-btn" @click="showFullImage(item)">
                                <img :src="getProductImage(item)" :alt="item.product_name || item.sku" @error="$set(item, '_imgError', true)" />
                            </button>
                            <div v-else class="pc-img-empty"><span class="pc-ico"><b-icon icon="image"></b-icon></span></div>
                        </div>

                        <!-- Product -->
                        <div class="pc-prod">
                            <label v-if="showSelectionMode && !pcShow.image" class="pc-check pc-check--inline">
                                <b-form-checkbox v-model="selectedProducts" :value="item.sku" @change="updateSelectAll" size="sm"></b-form-checkbox>
                            </label>
                            <div class="pc-name" :title="item.product_name">{{ item.product_name }}</div>
                            <div class="pc-prod-meta">
                                <span class="pc-sku">SKU <b>{{ item.sku }}</b></span>
                                <template v-if="ShowBarcode && !showSimplifiedColumns">
                                    <button v-if="item.barcode_image && !item._barcodeError" type="button" class="pc-barcode" @click="showFullBarcode(item)">
                                        <img :src="item.barcode_image" :alt="'Barcode for ' + item.sku" @error="$set(item, '_barcodeError', true)" />
                                    </button>
                                    <button v-else type="button" class="pc-barcode-gen" @click="generateSingleBarcode(item)" :disabled="generatingSingleBarcode">
                                        <span class="pc-ico"><b-icon icon="upc-scan"></b-icon></span> Generate barcode
                                    </button>
                                </template>
                            </div>
                            <div class="pc-badges">
                                <span v-if="item.is_parent" class="pc-badge is-dark">Group</span>
                                <span v-if="hasActiveClearance(item)" class="pc-badge is-danger">Clearance</span>
                                <span v-else-if="hasActiveSale(item)" class="pc-badge is-orange">On sale</span>
                                <span v-else-if="hasActivePromo(item)" class="pc-badge is-info">Promo</span>
                                <span v-if="hasScheduledSale(item)" class="pc-badge is-info">Sale starts {{ formatDate(item.special_discount_start_date) }}</span>
                                <span v-if="item.discontinued" class="pc-badge is-danger">Discontinued</span>
                                <span v-if="item.is_new_arrival" class="pc-badge is-good">New arrival</span>
                                <span v-if="item.recently_received" class="pc-badge is-info">Recently received</span>
                                <span v-if="item.status && item.status !== 'active'" class="pc-badge is-muted">Disabled</span>
                                <span v-if="isLowInventoryTriggered(item)" class="pc-badge is-warn">Low stock</span>
                            </div>
                            
                            <div class="pc-prod-taxonomy">
                                <span class="pc-tax-item" :title="packet.categoriesdict[item.category_id]">
                                    <span class="pc-ico"><b-icon icon="folder"></b-icon></span>
                                    {{ packet.categoriesdict[item.category_id] || '—' }}
                                    <span v-if="getSubcategoryLabel(item.subcategory_id)" class="pc-tax-sub">
                                        › {{ getSubcategoryLabel(item.subcategory_id) }}
                                    </span>
                                </span>
                                <span class="pc-tax-item" :title="packet.vendorsdict[item.vendor_id]">
                                    <span class="pc-ico"><b-icon icon="shop"></b-icon></span>
                                    {{ packet.vendorsdict[item.vendor_id] || '—' }}
                                </span>
                            </div>
                        </div>

                        <!-- Price -->
                        <div class="pc-sum-cell is-num pc-sum-price">
                            <div class="pc-sum-k">{{ hasActiveDiscount(item) ? 'Sale price' : 'Price' }}</div>
                            <div class="pc-big is-price">${{ formatPriceNum(hasActiveDiscount(item) ? calculateDiscountedPrice(item) : item.selling_price) }}</div>
                            <div v-if="hasActiveDiscount(item)" class="pc-sum-note">was <s>${{ formatPriceNum(item.selling_price) }}</s></div>
                            <div class="pc-sum-note pc-sum-costs">
                                <span>FOB: <b>${{ formatPriceNum(item.fob_price) }}</b></span>
                                <span>Landed: <b>${{ formatPriceNum(item.landed_cost) }}</b></span>
                                <span>Whlsl: <b>${{ formatPriceNum(item.wholesale_price) }}</b></span>
                                <span class="pc-avg-cost">Avg Cost: <b>${{ formatPriceNum(item.avg_cost) }}</b></span>
                                <span>Suggested Cost: <b>${{ formatPriceNum(suggestedCost(item)) }}</b></span>
                               <span :class="calculateProfitMargin(item.landed_cost, item.selling_price) >= 0 ? 'is-good' : 'is-bad'">Profit Margin:{{ calculateProfitMargin(item.landed_cost, item.selling_price) }}%</span>
                                <span>All Time Profit Margin <b>${{ item.all_time_profit_margin }}%</b></span>
                            </div>
                        </div>

                        <!-- ============================================ -->
                        <!-- SALE / PROMO COLUMN                           -->
                        <!-- ============================================ -->
                        <div v-if="showSaleColumns" class="pc-sum-cell pc-sum-sale" :class="'is-' + saleState(item)">
                            <div class="pc-sale-head">
                                <span class="pc-sale-title">Sale / Promo</span>
                                <span class="pc-sale-pill" :class="salePillClass(item)">{{ salePillLabel(item) }}</span>
                            </div>

                            <!-- Active sale -->
                            <template v-if="saleState(item) === 'active'">
                                <div class="pc-sale-row">
                                    <span class="pc-sale-k">Discount</span>
                                    <span class="pc-sale-v">
                                        <span class="pc-sale-off">{{ getDiscountLabel(item) }}</span>
                                    </span>
                                </div>
                                <div v-if="item.special_discount_start_date" class="pc-sale-row ">
                                    <span class="pc-sale-k">Starts</span>
                                    <span class="pc-sale-v">{{ formatDate(item.special_discount_start_date) }}</span>
                                </div>
                                <div class="pc-sale-row">
                                    <span class="pc-sale-k">Ends</span>
                                    <span class="pc-sale-v">
                                        {{ formatDate(item.special_discount_end_date) }}
                                    </span>
                                </div>
                                <div v-if="item.max_discount_percentage" class="pc-sale-row">
                                    <span class="pc-sale-k">Max disc.</span>
                                    <span class="pc-sale-v">{{ item.max_discount_percentage }}%</span>
                                </div>
                            </template>

                            <!-- Scheduled (not started yet) -->
                            <div v-else-if="saleState(item) === 'scheduled'" class="pc-sale-empty">
                                <span class="pc-ico"><b-icon icon="calendar-plus"></b-icon></span>
                                <div>
                                    <b>{{ item.sale_type || 'Sale' }} scheduled</b>
                                    <small>
                                        {{ formatDate(item.special_discount_start_date) }} →
                                        {{ formatDate(item.special_discount_end_date) }}
                                        · ${{ formatPriceNum(item.special_discount_price) }}
                                    </small>
                                </div>
                            </div>

                            <!-- Expired sale -->
                            <div v-else-if="saleState(item) === 'expired'" class="pc-sale-empty">
                                <span class="pc-ico"><b-icon icon="clock-history"></b-icon></span>
                                <div>
                                    <b>{{ item.sale_type || 'Sale' }} ended</b>
                                    <small>{{ formatDate(item.special_discount_end_date) }} · was ${{ formatPriceNum(item.special_discount_price) }}</small>
                                </div>
                            </div>

                            <!-- No sale -->
                            <div v-else class="pc-sale-empty">
                                <span class="pc-ico"><b-icon icon="tag"></b-icon></span>
                                <div>
                                    <b>No active sale</b>
                                    <small>Regular price ${{ formatPriceNum(item.selling_price) }}</small>
                                </div>
                            </div>

                            <div class="pc-sale-actions">
                                <button type="button" class="pc-sale-btn" @click="openItemSaleModal(item)">
                                    <span class="pc-ico"><b-icon :icon="saleState(item) === 'none' ? 'plus' : 'pencil'"></b-icon></span>
                                    {{ saleState(item) === 'active' ? 'Edit' : (saleState(item) === 'scheduled' ? 'Edit' : (saleState(item) === 'expired' ? 'Renew' : 'Set sale')) }}
                                </button>
                                <button v-if="saleState(item) !== 'none'" type="button" class="pc-sale-btn is-danger" @click="removeItemSale(item)">
                                    <span class="pc-ico"><b-icon icon="x"></b-icon></span>
                                    {{ saleState(item) === 'active' ? 'Remove' : 'Clear' }}
                                </button>
                            </div>
                        </div>

                        <!-- ============================================ -->
                        <!-- STOCK COLUMN — STORES | WH | ALL + clickable  -->
                        <!-- ============================================ -->
                        <div v-if="showStockColumns" class="pc-sum-cell pc-sum-stock" :class="'is-' + pcStockState(item)">
                            <div class="pc-stock-head">
                                <span class="pc-stock-title">Stock</span>
                                <span class="pc-stock-col-lbl">STORES</span>
                                <span class="pc-stock-col-lbl">WH</span>
                                <span class="pc-stock-col-lbl">ALL</span>
                            </div>

                            <div class="pc-stock-row" role="button" tabindex="0"
                                 @click="openStockBreakdown(item, 'in_stock')" @keyup.enter="openStockBreakdown(item, 'in_stock')">
                                <span class="pc-stock-k">IN STOCK</span>
                                <span class="pc-stock-v">{{ getStock(item).in_stock.store }}</span>
                                <span class="pc-stock-v is-wh">{{ getStock(item).in_stock.warehouse }}</span>
                                <span class="pc-stock-v is-all">{{ getStock(item).in_stock.total }}</span>
                            </div>

                            <div class="pc-stock-row" role="button" tabindex="0"
                                 @click="openStockBreakdown(item, 'available')" @keyup.enter="openStockBreakdown(item, 'available')">
                                <span class="pc-stock-k">AVAILABLE</span>
                                <span class="pc-stock-v is-available">{{ getStock(item).available.store }}</span>
                                <span class="pc-stock-v is-wh">{{ getStock(item).available.warehouse }}</span>
                                <span class="pc-stock-v is-all">{{ getStock(item).available.total }}</span>
                            </div>

                            <div class="pc-stock-row" role="button" tabindex="0"
                                 @click="openStockBreakdown(item, 'reserved')" @keyup.enter="openStockBreakdown(item, 'reserved')">
                                <span class="pc-stock-k">RESERVED</span>
                                <span class="pc-stock-v is-reserved">{{ getStock(item).reserved.store }}</span>
                                <span class="pc-stock-v is-wh">{{ getStock(item).reserved.warehouse }}</span>
                                <span class="pc-stock-v is-all">{{ getStock(item).reserved.total }}</span>
                            </div>

                            <div class="pc-stock-row" role="button" tabindex="0"
                                 @click="openStockBreakdown(item, 'layaway')" @keyup.enter="openStockBreakdown(item, 'layaway')">
                                <span class="pc-stock-k">LAYAWAY</span>
                                <span class="pc-stock-v is-layaway">{{ getStock(item).layaway.store }}</span>
                                <span class="pc-stock-v is-wh">{{ getStock(item).layaway.warehouse }}</span>
                                <span class="pc-stock-v is-all">{{ getStock(item).layaway.total }}</span>
                            </div>

                            <div class="pc-stock-row" role="button" tabindex="0"
                                 @click="openStockBreakdown(item, 'transfer')" @keyup.enter="openStockBreakdown(item, 'transfer')">
                                <span class="pc-stock-k">TRANSFER</span>
                                <span class="pc-stock-v is-transfer">{{ getStock(item).store_transfer.store }}</span>
                                <span class="pc-stock-v is-wh">{{ getStock(item).store_transfer.warehouse }}</span>
                                <span class="pc-stock-v is-all">{{ getStock(item).store_transfer.total }}</span>
                            </div>
                        </div>

                        <!-- ============================================ -->
                        <!-- TOTALS COLUMN — vertical, click for history   -->
                        <!-- ============================================ -->
                        <div class="pc-sum-cell pc-sum-totals">
                            <div class="pc-totals-head">
                                <span class="pc-totals-title">Totals</span>
                                <span class="pc-totals-hint">tap for history</span>
                            </div>

                            <button type="button" class="pc-totals-row" :disabled="isLoadingOrderDetails"
                                    @click="showOrderedDetails(item)">
                                <span class="pc-totals-ico is-ord"><span class="pc-ico"><b-icon icon="cart"></b-icon></span></span>
                                <span class="pc-totals-k">Ordered</span>
                                <span class="pc-totals-v is-ord">{{ item.ordered_from_history || item.ordered || 0 }}</span>
                                <span class="pc-totals-arrow"><span class="pc-ico"><b-icon icon="chevron-right"></b-icon></span></span>
                            </button>

                            <button type="button" class="pc-totals-row" :disabled="isLoadingScheduledDetails"
                                    @click="showScheduledDetails(item)">
                                <span class="pc-totals-ico is-sched"><span class="pc-ico"><b-icon icon="calendar-check"></b-icon></span></span>
                                <span class="pc-totals-k">Scheduled</span>
                                <span class="pc-totals-v is-sched">{{ item.scheduled_from_history || item.scheduled || 0 }}</span>
                                <span class="pc-totals-arrow"><span class="pc-ico"><b-icon icon="chevron-right"></b-icon></span></span>
                            </button>

                            <button type="button" class="pc-totals-row" :disabled="isLoadingReceivedDetails"
                                    @click="showReceivedDetails(item)">
                                <span class="pc-totals-ico is-recv"><span class="pc-ico"><b-icon icon="box-arrow-in-down"></b-icon></span></span>
                                <span class="pc-totals-k">Received</span>
                                <span class="pc-totals-v is-recv">{{ item.received_from_history || item.received || 0 }}</span>
                                <span class="pc-totals-arrow"><span class="pc-ico"><b-icon icon="chevron-right"></b-icon></span></span>
                            </button>

                            <button type="button" class="pc-totals-row" @click="show_sold_details(item)">
                                <span class="pc-totals-ico is-sold"><span class="pc-ico"><b-icon icon="cash-stack"></b-icon></span></span>
                                <span class="pc-totals-k">Sold</span>
                                <span class="pc-totals-v is-sold">{{ item.sold_so_far || 0 }}</span>
                                <span class="pc-totals-arrow"><span class="pc-ico"><b-icon icon="chevron-right"></b-icon></span></span>
                            </button>
                        </div>

                        <!-- Actions -->
                        <div class="pc-sum-actions">
                            <button v-if="['admin', 'officemanager'].includes(packet.userDetails.userrole)" type="button" class="pc-icon-btn" @click="openEditProductModal(item)" v-b-tooltip.hover.top title="Edit product">
                                <span class="pc-ico"><b-icon icon="pencil"></b-icon></span>
                            </button>
                            <button v-if="packet.userDetails.userrole === 'admin'" type="button" class="pc-icon-btn"
                                    :class="isLowInventoryTriggered(item) ? 'is-danger' : (hasLowInventoryAlert(item) ? 'is-warn' : '')"
                                    @click="showLowInventoryAlert(item)" v-b-tooltip.hover.top title="Low inventory alert">
                                <span class="pc-ico"><b-icon :icon="isLowInventoryTriggered(item) ? 'exclamation-triangle-fill' : (hasLowInventoryAlert(item) ? 'bell-fill' : 'bell')"></b-icon></span>
                            </button>
                            <b-dropdown v-if="packet.userDetails.userrole === 'admin'" right no-caret variant="link" toggle-class="pc-icon-btn" menu-class="pc-menu" boundary="window">
                                <template #button-content>
                                    <span class="pc-ico"><b-icon icon="three-dots-vertical"></b-icon></span>
                                    <span class="sr-only">More actions</span>
                                </template>
                                <b-dropdown-item @click="openEditProductModal(item)"><span class="pc-ico"><b-icon icon="pencil"></b-icon></span> Edit product</b-dropdown-item>
                                <b-dropdown-item @click="openImageUpload(item)"><span class="pc-ico"><b-icon icon="image"></b-icon></span> Upload or change image</b-dropdown-item>
                                <b-dropdown-item @click="show_item_stats(item)"><span class="pc-ico"><b-icon icon="bar-chart-line"></b-icon></span> Product stats</b-dropdown-item>
                                <b-dropdown-item @click="openItemSaleModal(item)"><span class="pc-ico"><b-icon icon="tag"></b-icon></span> Set sale / promo</b-dropdown-item>
                                <b-dropdown-item v-if="saleState(item) !== 'none'" @click="removeItemSale(item)"><span class="pc-ico"><b-icon icon="x-circle"></b-icon></span> Remove sale</b-dropdown-item>
                                <b-dropdown-item v-if="item.status == 'active'" @click="change_product_status(item, 'notactive')"><span class="pc-ico"><b-icon icon="pause-btn"></b-icon></span> Disable product</b-dropdown-item>
                                <b-dropdown-item v-else @click="change_product_status(item, 'active')"><span class="pc-ico"><b-icon icon="play-btn"></b-icon></span> Enable product</b-dropdown-item>
                                <b-dropdown-divider></b-dropdown-divider>
                                <b-dropdown-item :disabled="item.sold_so_far > 0" link-class="pc-menu-danger" @click="delete_product(item)">
                                    <span class="pc-ico"><b-icon icon="trash-fill"></b-icon></span>
                                    {{ item.sold_so_far > 0 ? 'Delete (has sales)' : 'Delete product' }}
                                </b-dropdown-item>
                            </b-dropdown>
                            <button type="button" class="pc-icon-btn pc-toggle" @click="pcToggle(item)">
                                <span class="pc-ico"><b-icon :icon="pcIsOpen(item) ? 'chevron-up' : 'chevron-down'"></b-icon></span>
                            </button>
                        </div>
                    </div>

                    <!-- Details Panel (read-only) -->
                    <div v-if="pcIsOpen(item)" class="pc-details">
                        <section v-if="pcShow.main" class="pc-sec">
                            <h4 class="pc-sec-title">Main</h4>
                            <div v-if="!showSimplifiedColumns" class="pc-row">
                                <span class="pc-k">Cubes</span>
                                <span class="pc-v">{{ item.cubes || 0 }}</span>
                            </div>
                            <div class="pc-row"><span class="pc-k">Group</span><span class="pc-v">{{ item.is_parent ? 'Yes' : 'No' }}</span></div>
                            <div class="pc-row">
                                <span class="pc-k">Category</span>
                                <span class="pc-v">{{ packet.categoriesdict[item.category_id] || '—' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Sub category</span>
                                <span class="pc-v">{{ getSubcategoryLabel(item.subcategory_id) || '—' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Vendor</span>
                                <span class="pc-v">{{ packet.vendorsdict[item.vendor_id] || '—' }}</span>
                            </div>
                            <div v-if="!showSimplifiedColumns" class="pc-row">
                                <span class="pc-k">Qty / box</span>
                                <span class="pc-v">{{ item.quantity_per_box || (item.properties && item.properties.quantity_per_box) || 1 }}</span>
                            </div>
                            <div v-if="!showSimplifiedColumns" class="pc-row">
                                <span class="pc-k">Spiff</span>
                                <span class="pc-v" :class="{ 'is-good': parseFloat(item.spiff) > 0 }">{{ item.spiff || 0 }}</span>
                            </div>
                        </section>

                        <section v-if="pcShow.details" class="pc-sec">
                            <h4 class="pc-sec-title">Details</h4>
                            <div class="pc-row">
                                <span class="pc-k">Type</span>
                                <span class="pc-v">{{ item.type_name || item.type_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Color</span>
                                <span class="pc-v">{{ item.color_name || item.color_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Size</span>
                                <span class="pc-v">{{ item.size_name || item.size_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Material</span>
                                <span class="pc-v">{{ item.material_name || item.material_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Functionality</span>
                                <span class="pc-v">{{ item.functionality_name || item.functionality_code || 'N/A' }}</span>
                            </div>
                        </section>

                        <section class="pc-sec">
                            <h4 class="pc-sec-title">Pricing</h4>
                            <div class="pc-row">
                                <span class="pc-k">Price</span>
                                <span class="pc-v">${{ formatPriceNum(item.selling_price) }}</span>
                            </div>
                            <template v-if="pcShow.costs">
                                <div class="pc-row">
                                    <span class="pc-k">FOB price</span>
                                    <span class="pc-v">${{ formatPriceNum(item.fob_price) }}</span>
                                </div>
                                <div class="pc-row">
                                    <span class="pc-k">Landed price</span>
                                    <span class="pc-v">${{ formatPriceNum(item.landed_cost) }}</span>
                                </div>
                                <div class="pc-row">
                                    <span class="pc-k">Wholesale</span>
                                    <span class="pc-v">${{ formatPriceNum(item.wholesale_price) }}</span>
                                </div>
                                <div class="pc-row">
                                    <span class="pc-k">Profit margin</span>
                                    <span class="pc-v" :class="calculateProfitMargin(item.landed_cost, item.selling_price) >= 0 ? 'is-good' : 'is-bad'">{{ calculateProfitMargin(item.landed_cost, item.selling_price) }}%</span>
                                </div>
                            </template>
                        </section>

                        <section v-if="pcShow.sale" class="pc-sec">
                            <h4 class="pc-sec-title">Sale</h4>
                            <div class="pc-row">
                                <span class="pc-k">Sale type</span>
                                <span class="pc-v">
                                    <span v-if="hasActiveClearance(item)" class="pc-badge is-danger">Clearance</span>
                                    <span v-else-if="hasActiveSale(item)" class="pc-badge is-orange">Sale</span>
                                    <span v-else-if="hasActivePromo(item)" class="pc-badge is-info">Promo</span>
                                    <span v-else-if="saleState(item) === 'scheduled'" class="pc-badge is-info">{{ item.sale_type || 'Sale' }} scheduled</span>
                                    <span v-else-if="saleState(item) === 'expired'" class="pc-badge is-muted">{{ item.sale_type || 'Sale' }} ended</span>
                                    <template v-else>Regular</template>
                                </span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Starts</span>
                                <span class="pc-v">
                                    <template v-if="item.special_discount_start_date">
                                        {{ formatDate(item.special_discount_start_date) }}
                                        <small v-if="saleState(item) === 'scheduled'">{{ saleStartsInLabel(item) }}</small>
                                    </template>
                                    <span v-else class="pc-muted">—</span>
                                </span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Special price</span>
                                <span class="pc-v" :class="{ 'is-good': hasActiveDiscount(item) }">{{ hasActiveDiscount(item) ? '$' + formatPriceNum(item.special_discount_price) : '—' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Discount ends</span>
                                <span class="pc-v">
                                    <template v-if="hasActiveDiscount(item)">{{ formatDate(item.special_discount_end_date) }} <small>{{ saleDaysLeftLabel(item) }}</small></template>
                                    <span v-else-if="item.special_discount_end_date && isDiscountExpired(item)" class="pc-muted">Expired {{ formatDate(item.special_discount_end_date) }}</span>
                                    <span v-else class="pc-muted">—</span>
                                </span>
                            </div>
                            <div v-if="hasActiveDiscount(item) && parseFloat(item.selling_price) > parseFloat(item.special_discount_price)" class="pc-row">
                                <span class="pc-k">Customer saves</span>
                                <span class="pc-v is-good">${{ saleSavings(item) }}</span>
                            </div>
                        </section>

                        <section class="pc-sec">
                            <h4 class="pc-sec-title">Inventory</h4>
                            <div class="pc-row"><span class="pc-k">In stock</span><span class="pc-v">{{ item.in_stock || 0 }}</span></div>
                            <div class="pc-row"><span class="pc-k">Reserved</span><span class="pc-v">{{ item.reserved || 0 }}</span></div>
                            <div class="pc-row"><span class="pc-k">Available</span><span class="pc-v" :class="(item.available || 0) > 0 ? 'is-good' : 'is-bad'">{{ item.available || 0 }}</span></div>
                            <div v-if="!showSimplifiedColumns" class="pc-row"><span class="pc-k">Layawayed</span><span class="pc-v">{{ item.layawayed || 0 }}</span></div>
                            <div v-if="!showSimplifiedColumns" class="pc-row"><span class="pc-k">Missing</span><span class="pc-v" :class="{ 'is-bad': (item.missing || 0) > 0 }">{{ item.missing || 0 }}</span></div>
                        </section>

                        <section class="pc-sec">
                            <h4 class="pc-sec-title">History <small>tap for details</small></h4>
                            <button type="button" class="pc-row pc-row--btn" @click="show_sold_details(item)">
                                <span class="pc-k">Sold so far</span>
                                <span class="pc-v">{{ item.sold_so_far || 0 }}</span>
                            </button>
                            <button type="button" class="pc-row pc-row--btn" @click="showOrderedDetails(item)">
                                <span class="pc-k">Last ordered</span>
                                <span class="pc-v">{{ item.ordered_from_history || 0 }} <small>{{ item.last_order_date ? moment(item.last_order_date).format('MMM DD, YYYY') : 'never' }}</small></span>
                            </button>
                            <button type="button" class="pc-row pc-row--btn" @click="showScheduledDetails(item)">
                                <span class="pc-k">Last scheduled</span>
                                <span class="pc-v">{{ item.scheduled_from_history || 0 }} <small>{{ item.last_scheduled_date ? moment(item.last_scheduled_date).format('MMM DD, YYYY') : 'never' }}</small></span>
                            </button>
                            <button type="button" class="pc-row pc-row--btn" @click="showReceivedDetails(item)">
                                <span class="pc-k">Last received</span>
                                <span class="pc-v">{{ item.received_from_history || 0 }} <small>{{ item.last_received_date ? moment(item.last_received_date).format('MMM DD, YYYY') : 'never' }}</small></span>
                            </button>
                        </section>
                    </div>
                </article>

                <div v-if="isLoadingMore" class="pc-loadmore">
                    <b-spinner small></b-spinner>
                    <span>Loading more products…</span>
                </div>
                <div v-if="!hasMore && displayedItems.length > 0" class="pc-end">
                    All {{ totalItemsCount }} products loaded
                </div>
            </div>

            <div v-else-if="!items_being_loaded">
                <b-alert variant="warning" show>No products to show</b-alert>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import global_vars from "@/utilities/global_vars";
import Requestor from "@/utilities/Requestor";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import ProductStatsModal from "@/components/Products/ProductStatsModal";
import StockBreakdownModal from "@/components/Products/StockBreakdownModal";

export default {
    name: "ProductManagerList",
    components: { Loading, DatePicker, ProductStatsModal, StockBreakdownModal },
    data: function() {
        return {
            packet: {},
            bulkImages: [],
            bulkImagePreview: [],
            uploadingBulkImages: false,
            bulkImageProgress: { total: 0, processed: 0, successful: 0, failed: 0, current: 0 },
            showMoreFilters: false,
            showMainColumns: true,
            showPriceColumns: true,
            showDiscountColumns: true,
            showSaleColumns: true,
            showStockColumns: true,
            generatingBarcodes: false,
            discountFilter: 'active',
            saleModalVisible: false,
            salePrice: null,
            saleMaxDiscount: 10,
            saleStartDate: null,
            saleEndDate: null,
            updateChildrenSale: false,
            clearanceModalVisible: false,
            clearancePrice: null,
            clearanceMaxDiscount: 25,
            clearanceStartDate: null,
            updateChildrenClearance: false,
            discountTypeOptions: [
                { value: 'active', text: 'ACTIVE DISCOUNTS' },
                { value: 'expired', text: 'EXPIRED DISCOUNTS' },
                { value: 'all', text: 'ALL DISCOUNTS' }
            ],
            isShowingDiscounted: false,
            bulkStatusModalVisible: false,
            bulkStatusAction: 'active',
            updateChildren: false,
            ShowBarcode: true,
            orderHistory: [],
            orderStats: { total_orders: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} },
            moment: moment,
            scheduledHistory: [],
            scheduledStats: { total_scheduled: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} },
            receivedHistory: [],
            receivedStats: { total_received: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} },
            isLoadingReceivedDetails: false,
            isLoadingScheduledDetails: false,
            isLoadingOrderDetails: false,
            generatingSingleBarcode: false,
            currentBarcodeImage: null,
            currentProductSku: null,
            showLowInventoryModal: false,
            currentAlertProduct: null,
            alertConfig: { alert_type: 'threshold', threshold_quantity: 10, notification_methods: ['email', 'dashboard'], alert_emails: '', notes: '' },
            currentInventoryLevel: null,
            alertTypeOptions: [{ text: 'Threshold Alert', value: 'threshold' }, { text: 'Percentage Alert', value: 'percentage' }, { text: 'Reorder Point', value: 'reorder_point' }],
            barcodeProgress: { total: 0, processed: 0, successful: 0, failed: 0, percentage: 0, message: '', errors: [] },
            Showimage: true,
            showDetailColumns: true,
            selectedProducts: [],
            showSelectionMode: false,
            selectAll: false,
            typeFilter: null,
            colorFilter: null,
            sizeFilter: null,
            materialFilter: null,
            functionalityFilter: null,
            typeOptions: [],
            colorOptions: [],
            sizeOptions: [],
            materialOptions: [],
            functionalityOptions: [],
            itemStatusFilter: null,
            itemStatusOptions: [{ value: null, text: 'ALL ITEM STATUSES' }, { value: 'in_stock', text: 'IN STOCK' }, { value: 'out_of_stock', text: 'OUT OF STOCK' }, { value: 'new_product', text: 'NEW PRODUCT' }, { value: 'ordered_product', text: 'ORDERED PRODUCT' }],
            allItems: [],
            displayedItems: [],
            currentOffset: 0,
            subcategory_id: null,
            uploadProduct: null,
            selectedFile: null,
            imagePreview: null,
            uploadingImage: false,
            subcategories: [],
            pageSize: 100,
            isLoadingMore: false,
            hasMore: true,
            totalItemsCount: 0,
            currentFullSizeImage: null,
            currentProductName: null,
            itemTypeFilter: null,
            itemTypeOptions: [{ value: null, text: 'ALL ITEM TYPES' }, { value: 'single', text: 'SINGLE ITEMS ONLY' }, { value: 'group', text: 'GROUP ITEMS ONLY' }],
            downloadingExcel: false,
            vendor_id: null,
            category_id: null,
            location_id: null,
            showDisabled: false,
            items_being_loaded: false,
            global_vars: global_vars,
            edit_product_idx: null,
            currentPage: 1,
            totalPages: 0,
            sort_asc: true,
            activeSortKey: null,
            activeSortAsc: true,
            pcExpandAll: false,
            product_query: null,
            limit: 100,
            columns: [],
            searchValue: "",
            pageIndex: 1,
            csvFile: null,
            csvPreview: [],
            uploadingCsv: false,
            csvFields: [{ key: 'sku', label: 'SKU' }, { key: 'description', label: 'DESCRIPTION' }, { key: 'category', label: 'CATEGORY' }, { key: 'sub_category', label: 'SUB CATEGORY' }, { key: 'vendor', label: 'VENDOR' }, { key: 'quantity_per_box', label: 'QTY PER BOX' }, { key: 'fob_price', label: 'FOB PRICE' }, { key: 'landed_cost', label: 'LANDED CST' }, { key: 'wholesale_price', label: 'WHLSL PRICE' }, { key: 'selling_price', label: 'SELL PRICE' }, { key: 'cubes', label: 'CUBES' }, { key: 'spiff', label: 'SPIFF' }],
            currentProduct: null,
            detailFields: [{ key: 'period', label: 'Time Period', sortable: true }, { key: 'value', label: 'Quantity', sortable: true }],
            statusFilter: 'active',
            statusOptions: [{ value: 'all', text: 'ALL STATUSES' }, { value: 'active', text: 'ACTIVE ONLY' }, { value: 'notactive', text: 'DISABLED ONLY' }],
            bestSellingFilter: null,
            bestSellingOptions: [{ value: null, text: 'ALL PRODUCTS' }, { value: '7_days', text: 'BEST SELLERS FOR THE WEEK' }, { value: '30_days', text: 'BEST SELLERS FOR THE MONTH' }, { value: '60_days', text: 'BEST SELLERS FOR LAST 2 MONTHS' }, { value: '90_days', text: 'BEST SELLERS FOR LAST 3 MONTHS' }, { value: 'all_time', text: 'BEST SELLERS - ALL TIME' }, { value: 'profit_margin', text: 'HIGHEST PROFIT MARGIN %' }, { value: 'profit_7_days', text: 'HIGHEST PROFIT FOR THE WEEK' }, { value: 'profit_30_days', text: 'HIGHEST PROFIT FOR THE MONTH' }, { value: 'profit_60_days', text: 'HIGHEST PROFIT FOR LAST 2 MONTHS' }, { value: 'profit_90_days', text: 'HIGHEST PROFIT FOR LAST 3 MONTHS' }, { value: 'profit_all_time', text: 'HIGHEST PROFIT (ALL TIME)' }],
            slowestSellingFilter: null,
            slowestSellingOptions: [{ value: null, text: 'ALL PRODUCTS' }, { value: 'slowest_7_days', text: 'SLOWEST SELLERS FOR THE WEEK' }, { value: 'slowest_30_days', text: 'SLOWEST SELLERS FOR THE MONTH' }, { value: 'slowest_60_days', text: 'SLOWEST SELLERS FOR LAST 2 MONTHS' }, { value: 'slowest_90_days', text: 'SLOWEST SELLERS FOR LAST 3 MONTHS' }, { value: 'slowest_all_time', text: 'SLOWEST SELLERS - ALL TIME' }],
            bestSellingSortDesc: true,
            filtered_items: [],
            stats_item: null,
            user_location_id: null,
            user_location_name: "",
            locationsDict: {},
            searchTimeout: null,
            lastScrollTop: 0,
            filtersVisible: false,
            isMobile: false,
            showCategoryStats: false,
            isLoadingCategoryStats: false,
            categoryStats: {
                category_name: '', category_code: '', category_id: null,
                total_in_stock: 0, total_reserved: 0, total_available: 0, total_layawayed: 0, total_missing: 0,
                total_sold_so_far: 0, total_ordered: 0, total_scheduled: 0,
                total_products: 0, products_in_stock: 0, products_out_of_stock: 0, parent_products: 0, child_products: 0
            },
            printProducts: [],
            isPrinting: false,
            printSettings: { labelSize: '1.5x2', copies: 1, printMethod: 'browser', layout: 'single', showPrice: true, showProductName: true, showSku: true },
            labelSizeOptions: [{ value: '1.5x2', text: '1.5" x 2"' }, { value: '2x2', text: '2" x 2"' }, { value: '2x3', text: '2" x 3"' }, { value: '4x6', text: '4" x 6"' }],
            printMethodOptions: [{ value: 'browser', text: 'Browser Print' }, { value: 'newWindow', text: 'New Window' }, { value: 'direct', text: 'Direct Print' }],
            layoutOptions: [{ value: 'single', text: '1 per page' }, { value: 'double', text: '2 per page' }, { value: 'grid', text: 'Grid 2x2' }],
            labelDimensions: { '1.5x2': { w: 1.5, h: 2 }, '2x2': { w: 2, h: 2 }, '2x3': { w: 2, h: 3 }, '4x6': { w: 4, h: 6 } },
            discontinuedFilter: null,
            discontinuedOptions: [{ value: null, text: 'ALL' }, { value: 'active', text: 'ACTIVE PRODUCTS' }, { value: 'discontinued', text: 'DISCONTINUED' }],
            discontinuedModalVisible: false,
            bulkDiscontinuedAction: false,
            discontinuedReason: '',
            updateChildrenDiscontinued: false,
            newArrivalFilter: null,
            newArrivalOptions: [{ value: null, text: 'ALL' }, { value: 'new', text: 'NEW ARRIVALS' }, { value: 'expired', text: 'EXPIRED NEW ARRIVALS' }],
            newArrivalModalVisible: false,
            bulkNewArrivalAction: false,
            newArrivalDays: 30,
            updateChildrenNewArrival: false,
            spiffFilter: null,
            spiffFilterOptions: [
                { value: null, text: 'ALL ITEMS' },
                { value: 'with_spiff', text: 'WITH SPIFF (>0)' },
                { value: 'without_spiff', text: 'WITHOUT SPIFF (0)' },
                { value: 'high_spiff', text: 'HIGH SPIFF (>10)' }
            ],
            receivedStatusFilter: null,
            receivedStatusOptions: [
                { value: null, text: 'ALL' },
                { value: 'recently_received', text: 'RECENTLY RECEIVED' },
                { value: 'not_recently_received', text: 'NOT RECENTLY RECEIVED' }
            ],
            stockBreakdown: {},
            stockModalSku: null,
            stockModalName: null,
            stockModalTab: 'in_stock',
            managerSaleItem: null,
            savingManagerSale: false,
            managerSalePreview: null,
            managerSaleForm: {
                actionType: 'Sale',
                discountPercentage: 10,
                discountAmount: null,
                maxDiscount: 10,
                startDate: moment().format('MM-DD-YYYY'),
                endDate: moment().add(30, 'days').format('MM-DD-YYYY')
            },
            managerSaleTypeOptions: [
                { text: 'Sale', value: 'Sale' },
                { text: 'Clearance', value: 'Clearance' },
                { text: 'Promo', value: 'Promo' }
            ],
            // Edit modal state
            editForm: {},
            editActiveTab: 0,
            savingEditProduct: false
        };
    },
    computed: {
        pcShow() {
            const simple = this.showSimplifiedColumns;
            const isAdmin = this.packet && this.packet.userDetails && this.packet.userDetails.userrole === 'admin';
            return {
                image: this.Showimage,
                main: this.showMainColumns || simple,
                details: this.showDetailColumns && !simple,
                costs: isAdmin && this.showPriceColumns && !simple,
                sale: this.showDiscountColumns && !simple,
                actions: isAdmin,
            };
        },
        pcGridStyle() {
            const cols = [];
            if (this.pcShow.image) cols.push('250px');
            cols.push('minmax(0, 1.9fr)');
            cols.push('minmax(0, 1.5fr)');
            if (this.showSaleColumns) cols.push('minmax(0, 1.5fr)');
            if (this.showStockColumns) cols.push('minmax(0, 1.6fr)');
            cols.push('minmax(0, 1.3fr)');
            cols.push(this.pcShow.actions ? '40px' : '40px');
            return { '--pc-cols': cols.join(' ') };
        },
        sortOptions() {
            const s = this.pcShow;
            const opts = [
                { key: 'product_name', label: 'Product name' },
                { key: 'sku', label: 'SKU' },
                { key: 'selling_price', label: 'Price' },
                { key: 'in_stock', label: 'In stock' },
                { key: 'reserved', label: 'Reserved' },
                { key: 'available', label: 'Available' },
                { key: 'sold_so_far', label: 'Sold so far' },
                { key: 'ordered', label: 'Last ordered' },
                { key: 'scheduled', label: 'Last scheduled' },
                { key: 'received_from_history', label: 'Last received' },
            ];
            if (s.main) {
                opts.push(
                    { key: 'category_id', label: 'Category' },
                    { key: 'subcategory_id', label: 'Sub category' },
                    { key: 'vendor_id', label: 'Vendor' },
                    { key: 'is_parent', label: 'Group' }
                );
                if (!this.showSimplifiedColumns) {
                    opts.push({ key: 'cubes', label: 'Cubes' }, { key: 'quantity_per_box', label: 'Qty / box' });
                }
            }
            if (s.details) {
                opts.push(
                    { key: 'type_name', label: 'Type' },
                    { key: 'color_name', label: 'Color' },
                    { key: 'size_name', label: 'Size' },
                    { key: 'material_name', label: 'Material' },
                    { key: 'functionality_name', label: 'Functionality' }
                );
            }
            if (s.costs) {
                opts.push(
                    { key: 'fob_price', label: 'FOB price' },
                    { key: 'landed_cost', label: 'Landed price' },
                    { key: 'wholesale_price', label: 'Wholesale price' }
                );
            }
            return opts;
        },
        allReceivedEvents() {
            if (!this.receivedHistory || this.receivedHistory.length === 0) return [];
            const events = [];
            this.receivedHistory.forEach(order => {
                if (order.timeline && order.timeline.length > 0) {
                    order.timeline.forEach(event => {
                        events.push({
                            order_id: order.order_id,
                            order_type: order.order_type,
                            date: event.date,
                            quantity: event.quantity,
                            status: event.status || 'Received',
                            created_by: event.created_by,
                            action_type: event.action_type || 'create'
                        });
                    });
                }
            });
            return events.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        allScheduledEvents() {
            if (!this.scheduledHistory || this.scheduledHistory.length === 0) return [];
            const events = [];
            this.scheduledHistory.forEach(order => {
                if (order.timeline && order.timeline.length > 0) {
                    order.timeline.forEach(event => {
                        events.push({
                            order_id: order.order_id,
                            order_type: order.order_type,
                            date: event.date,
                            quantity: event.quantity,
                            status: event.status,
                            created_by: event.created_by,
                            action_type: event.action_type || 'create'
                        });
                    });
                }
            });
            return events.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        allOrderEvents() {
            const events = [];
            this.orderHistory.forEach(order => {
                order.timeline.forEach(event => {
                    events.push({
                        order_id: order.order_id,
                        order_type: order.order_type,
                        date: event.date,
                        quantity: event.quantity,
                        status: event.status,
                        created_by: event.created_by
                    });
                });
            });
            return events.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        eventFields() {
            return [
                { key: 'date', label: 'Date/Time', sortable: true },
                { key: 'order_id', label: 'Order ID', sortable: true },
                { key: 'order_type', label: 'Type', sortable: true },
                { key: 'quantity', label: 'Quantity', sortable: true },
                { key: 'status', label: 'Status', sortable: true },
                { key: 'created_by', label: 'By' }
            ];
        },
        activeFiltersCount() {
            let count = 0;
            if (this.vendor_id) count++;
            if (this.category_id) count++;
            if (this.subcategory_id) count++;
            if (this.product_query) count++;
            if (this.location_id && this.packet.userDetails.userrole === 'admin') count++;
            if (this.itemTypeFilter) count++;
            if (this.itemStatusFilter) count++;
            if (this.statusFilter !== 'all') count++;
            if (this.discontinuedFilter) count++;
            if (this.newArrivalFilter) count++;
            if (this.receivedStatusFilter) count++;
            if (this.typeFilter) count++;
            if (this.colorFilter) count++;
            if (this.sizeFilter) count++;
            if (this.materialFilter) count++;
            if (this.functionalityFilter) count++;
            if (this.spiffFilter) count++;
            if (this.bestSellingFilter) count++;
            if (this.slowestSellingFilter) count++;
            return count;
        },
        getActiveFiltersList() {
            const filters = [];
            if (this.vendor_id) filters.push({ key: 'vendor', label: 'Vendor', value: this.packet.vendorsdict[this.vendor_id] });
            if (this.category_id) filters.push({ key: 'category', label: 'Category', value: this.packet.categoriesdict[this.category_id] });
            if (this.subcategory_id) filters.push({ key: 'subcategory', label: 'Sub Category', value: this.getSubcategoryLabel(this.subcategory_id) });
            if (this.product_query) filters.push({ key: 'search', label: 'Search', value: this.product_query });
            if (this.location_id && this.packet.userDetails.userrole === 'admin') filters.push({ key: 'location', label: 'Location', value: this.locationsDict[this.location_id] });
            if (this.itemTypeFilter) filters.push({ key: 'itemType', label: 'Item Type', value: this.itemTypeFilter === 'single' ? 'Single' : 'Group' });
            if (this.itemStatusFilter) filters.push({ key: 'itemStatus', label: 'Item Status', value: this.itemStatusOptions.find(opt => opt.value === this.itemStatusFilter).text });
            if (this.statusFilter !== 'all') filters.push({ key: 'status', label: 'Status', value: this.statusFilter === 'active' ? 'Active' : 'Disabled' });
            if (this.discontinuedFilter) filters.push({ key: 'discontinued', label: 'Discontinued', value: this.discontinuedFilter === 'discontinued' ? 'Discontinued' : 'Active' });
            if (this.newArrivalFilter) filters.push({ key: 'newArrival', label: 'New Arrival', value: this.newArrivalFilter === 'new' ? 'New Arrivals' : 'Expired' });
            if (this.receivedStatusFilter) {
                const receivedLabels = { 'recently_received': 'Recently Received', 'not_recently_received': 'Not Recently Received' };
                filters.push({ key: 'receivedStatus', label: 'Received Status', value: receivedLabels[this.receivedStatusFilter] || this.receivedStatusFilter });
            }
            if (this.typeFilter) filters.push({ key: 'type', label: 'Type', value: this.typeOptions.find(opt => opt.value === this.typeFilter).text });
            if (this.colorFilter) filters.push({ key: 'color', label: 'Color', value: this.colorOptions.find(opt => opt.value === this.colorFilter).text });
            if (this.sizeFilter) filters.push({ key: 'size', label: 'Size', value: this.sizeOptions.find(opt => opt.value === this.sizeFilter).text });
            if (this.materialFilter) filters.push({ key: 'material', label: 'Material', value: this.materialOptions.find(opt => opt.value === this.materialFilter).text });
            if (this.functionalityFilter) filters.push({ key: 'functionality', label: 'Functionality', value: this.functionalityOptions.find(opt => opt.value === this.functionalityFilter).text });
            if (this.spiffFilter) {
                const spiffLabels = { 'with_spiff': 'With SPIFF (>0)', 'without_spiff': 'Without SPIFF (0)', 'high_spiff': 'High SPIFF (>10)' };
                filters.push({ key: 'spiff', label: 'SPIFF', value: spiffLabels[this.spiffFilter] || this.spiffFilter });
            }
            if (this.bestSellingFilter) filters.push({ key: 'bestSelling', label: 'Best Selling', value: this.bestSellingOptions.find(opt => opt.value === this.bestSellingFilter).text });
            if (this.slowestSellingFilter) filters.push({ key: 'slowestSelling', label: 'Slowest Selling', value: this.slowestSellingOptions.find(opt => opt.value === this.slowestSellingFilter).text });
            if (this.isShowingDiscounted) {
                const discountLabels = { 'active': 'Active Discounts', 'expired': 'Expired Discounts', 'all': 'All Discounts' };
                filters.push({ key: 'discount', label: 'Discount Type', value: discountLabels[this.discountFilter] || this.discountFilter });
            }
            return filters;
        },
        isAlertConfigValid() {
            return this.currentAlertProduct && this.alertConfig.threshold_quantity > 0 && this.alertConfig.alert_type;
        },
        showSimplifiedColumns() {
            return this.bestSellingFilter || this.slowestSellingFilter;
        },
        subcategoryOptions() {
            if (!this.category_id) return [{ value: null, text: 'Select Category First' }];
            const filteredSubcategories = this.subcategories.filter(subcat => subcat.category_id == this.category_id);
            return [{ value: null, text: 'ALL' }, ...filteredSubcategories.map(subcat => ({ value: subcat.id, text: subcat.subcategory }))];
        },
        editSubcategoryOptions() {
            if (!this.editForm.category_id) return [{ value: null, text: 'Select Category First' }];
            const filtered = this.subcategories.filter(sub => sub.category_id == this.editForm.category_id);
            return [{ value: null, text: 'ALL' }, ...filtered.map(s => ({ value: s.id, text: s.subcategory }))];
        },
        editProfitMargin() {
            if (!this.editForm.landed_cost && !this.editForm.selling_price) return null;
            return this.calculateProfitMargin(this.editForm.landed_cost, this.editForm.selling_price);
        },
        filtered_final() {
            if (!this.hasActiveFilters && this.displayedItems.length === 0) return [];
            let items = [...this.displayedItems];

            if (this.statusFilter !== 'all') {
                items = items.filter(item => {
                    let itemStatus = item.status;
                    let filterStatus = this.statusFilter;
                    if (filterStatus === 'notactive') return itemStatus === 'notactive' || itemStatus === 'not-active';
                    return itemStatus === filterStatus;
                });
            }
            if (this.discontinuedFilter) {
                items = items.filter(item => {
                    if (this.discontinuedFilter === 'discontinued') return item.discontinued === true;
                    else if (this.discontinuedFilter === 'active') return item.discontinued !== true;
                    return true;
                });
            }
            if (this.newArrivalFilter) {
                items = items.filter(item => {
                    if (this.newArrivalFilter === 'new') return item.is_new_arrival === true;
                    else if (this.newArrivalFilter === 'expired') return item.is_new_arrival !== true;
                    return true;
                });
            }
            if (this.receivedStatusFilter) {
                items = items.filter(item => {
                    if (this.receivedStatusFilter === 'recently_received') return item.recently_received === true;
                    else if (this.receivedStatusFilter === 'not_recently_received') return item.recently_received !== true;
                    return true;
                });
            }
            if (this.itemStatusFilter) {
                items = items.filter(item => {
                    const isOld = this.isOldProduct(item);
                    switch (this.itemStatusFilter) {
                        case 'in_stock': return item.in_stock > 0 && isOld;
                        case 'out_of_stock': return item.in_stock === 0 && isOld;
                        case 'new_product': return item.in_stock > 0 && !isOld;
                        case 'ordered_product': return item.ordered > 0 && item.in_stock === 0;
                        default: return true;
                    }
                });
            }
            if (this.itemTypeFilter === 'single') items = items.filter(item => !item.is_parent);
            else if (this.itemTypeFilter === 'group') items = items.filter(item => item.is_parent);

            if (this.spiffFilter) {
                items = items.filter(item => {
                    const spiff = parseFloat(item.spiff) || 0;
                    switch (this.spiffFilter) {
                        case 'with_spiff': return spiff > 0;
                        case 'without_spiff': return spiff === 0;
                        case 'high_spiff': return spiff > 10;
                        default: return true;
                    }
                });
            }
            if (this.bestSellingFilter) items = this.applyBestSellingSort(items);
            if (this.slowestSellingFilter) items = this.applySlowestSellingSort(items);

            if (!this.product_query) return items;
            else {
                let product_query = this.product_query.toUpperCase();
                return items.filter(d => ((d.product_name || '').toUpperCase().includes(product_query)) || ((d.sku || '').toUpperCase().includes(product_query)));
            }
        },
        hasActiveFilters() {
            return this.category_id !== null || this.vendor_id !== null || (this.packet.userDetails.userrole === 'admin' && this.location_id !== null) || this.subcategory_id !== null || this.itemTypeFilter !== null || this.itemStatusFilter !== null || this.statusFilter !== 'all' || this.discontinuedFilter !== null || this.newArrivalFilter !== null || this.receivedStatusFilter !== null || this.bestSellingFilter !== null || this.slowestSellingFilter !== null || this.spiffFilter !== null || this.hasActiveDetailFilters || (this.product_query && this.product_query.trim().length > 0);
        },
        hasActiveDetailFilters() {
            return this.typeFilter !== null || this.colorFilter !== null || this.sizeFilter !== null || this.materialFilter !== null || this.functionalityFilter !== null;
        },
        soldDetails() {
            if (!this.currentProduct) return [];
            return [{ period: 'Total Sold', value: this.currentProduct.sold_so_far || 0 }, { period: 'LAST 7 Days', value: this.currentProduct.sold_7d || 0 }, { period: 'LAST 30 Days', value: this.currentProduct.sold_30d || 0 }, { period: 'LAST 60 Days', value: this.currentProduct.sold_60d || 0 }, { period: 'LAST 90 Days', value: this.currentProduct.sold_90d || 0 }].filter(detail => detail.value > 0);
        },
        printAllPages() {
            if (!this.printSettings) return [];
            let all = [];
            for (let i = 0; i < this.printSettings.copies; i++) all.push(...this.printProducts);
            return all.slice(0, 24 * this.printSettings.copies);
        },
        showStatsButton() {
            return this.category_id !== null && this.category_id !== '';
        }
    },
    watch: {
        product_query: function(newVal) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                if (newVal && newVal.length >= 2) this.fetch_product_name_sku();
                else if (!newVal) {
                    this.currentOffset = 0;
                    this.hasMore = false;
                    this.allItems = [];
                    this.displayedItems = [];
                    this.updateDisplayedItems();
                }
            }, 5000);
        },
        category_id: function(newCategoryId) {
            this.showCategoryStats = false;
            if (newCategoryId) {
                this.fetchAllHierarchyOptions(newCategoryId);
                this.resetDetailFilters();
            } else {
                this.fetchAllHierarchyOptions();
            }
        }
    },
    methods: {
        // ============================================
        // EDIT MODAL
        // ============================================
        openEditProductModal(item) {
            this.editForm = {
                id: item.id,
                sku: item.sku,
                product_name: item.product_name || '',
                category_id: item.category_id || null,
                subcategory_id: item.subcategory_id || null,
                vendor_id: item.vendor_id || null,
                quantity_per_box: item.quantity_per_box || 1,
                cubes: parseFloat(item.cubes) || 0,
                spiff: parseFloat(item.spiff) || 0,
                type_id: item.type_id || null,
                color_id: item.color_id || null,
                size_id: item.size_id || null,
                material_id: item.material_id || null,
                functionality_id: item.functionality_id || null,
                selling_price: parseFloat(item.selling_price) || 0,
                fob_price: parseFloat(item.fob_price) || 0,
                landed_cost: parseFloat(item.landed_cost) || 0,
                wholesale_price: parseFloat(item.wholesale_price) || 0,
                special_discount_price: parseFloat(item.special_discount_price) || 0,
                max_discount_percentage: item.max_discount_percentage || null,
                special_discount_start_date: item.special_discount_start_date || null,
                special_discount_end_date: item.special_discount_end_date || null,
                _original: {
                    product_name: item.product_name,
                    selling_price: item.selling_price,
                    landed_cost: item.landed_cost,
                    fob_price: item.fob_price,
                    wholesale_price: item.wholesale_price,
                    category_id: item.category_id,
                    subcategory_id: item.subcategory_id,
                    vendor_id: item.vendor_id,
                    spiff: item.spiff,
                    cubes: item.cubes,
                    quantity_per_box: item.quantity_per_box,
                    special_discount_price: item.special_discount_price,
                    special_discount_start_date: item.special_discount_start_date,
                    special_discount_end_date: item.special_discount_end_date,
                    type_id: item.type_id,
                    color_id: item.color_id,
                    size_id: item.size_id,
                    material_id: item.material_id,
                    functionality_id: item.functionality_id,
                },
                _image: this.getProductImage(item),
            };
            this.editActiveTab = 0;
            this.$bvModal.show('modal-edit-product');
        },
        resetEditForm() {
            this.editForm = {};
            this.editActiveTab = 0;
            this.savingEditProduct = false;
        },
        onEditCategoryChange() {
            if (this.editForm.subcategory_id) {
                const match = this.subcategories.find(
                    s => s.id === this.editForm.subcategory_id && s.category_id == this.editForm.category_id
                );
                if (!match) this.editForm.subcategory_id = null;
            }
        },
        async saveEditedProduct() {
            if (!this.editForm.id) return;
            if (!this.editForm.product_name || !this.editForm.product_name.trim()) {
                this.$bvToast.toast('Product name is required', { title: 'Validation', variant: 'warning', autoHideDelay: 2000 });
                return;
            }
            if (!this.editForm.category_id || !this.editForm.vendor_id) {
                this.$bvToast.toast('Category and Vendor are required', { title: 'Validation', variant: 'warning', autoHideDelay: 2000 });
                return;
            }
            if (this.editForm.special_discount_price > 0) {
                if (this.editForm.special_discount_start_date && this.editForm.special_discount_end_date &&
                    moment(this.editForm.special_discount_start_date, 'MM-DD-YYYY')
                        .isAfter(moment(this.editForm.special_discount_end_date, 'MM-DD-YYYY'))) {
                    this.$bvToast.toast('Sale start date must be before end date', { title: 'Validation', variant: 'warning', autoHideDelay: 2000 });
                    return;
                }
            }

            this.savingEditProduct = true;
            const f = this.editForm;
            const data = {
                id: f.id,
                sku: f.sku,
                product_name: f.product_name,
                selling_price: parseFloat(f.selling_price) || 0,
                landed_cost: parseFloat(f.landed_cost) || 0,
                fob_price: parseFloat(f.fob_price) || 0,
                wholesale_price: parseFloat(f.wholesale_price) || 0,
                category_id: f.category_id,
                subcategory_id: f.subcategory_id,
                vendor_id: f.vendor_id,
                type_id: f.type_id,
                color_id: f.color_id,
                size_id: f.size_id,
                material_id: f.material_id,
                functionality_id: f.functionality_id,
                special_discount_price: parseFloat(f.special_discount_price) || 0,
                special_discount_start_date: f.special_discount_start_date || null,
                special_discount_end_date: f.special_discount_end_date || null,
                max_discount_percentage: f.max_discount_percentage,
                properties: {
                    spiff: parseFloat(f.spiff) || 0,
                    cubes: parseFloat(f.cubes) || 0,
                    quantity_per_box: parseInt(f.quantity_per_box) || 1,
                },
                original_values: f._original,
                user_details: {
                    username: this.packet.userDetails.username || 'unknown',
                    user_id: this.packet.userDetails.user_id || 'unknown',
                },
            };

            const that = this;
            Requestor.postAsyncP(
                '/api/product/update_v1_with_history/',
                data,
                this,
                function (response) {
                    that.savingEditProduct = false;
                    if (response.data.result) {
                        that.$bvToast.toast('Product updated.', { title: 'Success', variant: 'success', autoHideDelay: 1500 });
                        that.$bvModal.hide('modal-edit-product');
                        that.fetch_init(false, true);
                    } else {
                        that.$bvToast.toast(response.data.error || 'Update failed', { title: 'Error', variant: 'danger', autoHideDelay: 2500 });
                    }
                },
                function (error) {
                    that.savingEditProduct = false;
                    console.error(error);
                    that.$bvToast.toast('Error updating product.', { title: 'Error', variant: 'danger', autoHideDelay: 2500 });
                }
            );
        },

        // ============================================
        // STOCK BREAKDOWN
        // ============================================
        getStock(item) {
            const empty = {
                in_stock:       { store: 0, warehouse: 0, total: 0 },
                sold:           { store: 0, warehouse: 0, total: 0 },
                reserved:       { store: 0, warehouse: 0, total: 0 },
                layaway:        { store: 0, warehouse: 0, total: 0 },
                store_transfer: { store: 0, warehouse: 0, total: 0 },
                available:      { store: 0, warehouse: 0, total: 0 },
            };
            if (!item || !item.sku) return empty;
            const key = String(item.sku).toUpperCase();
            const b = this.stockBreakdown[key];
            if (b) return b;
            return {
                in_stock:       { store: 0, warehouse: 0, total: item.in_stock || 0 },
                sold:           { store: 0, warehouse: 0, total: 0 },
                reserved:       { store: 0, warehouse: 0, total: 0 },
                layaway:        { store: 0, warehouse: 0, total: item.layawayed || 0 },
                store_transfer: { store: 0, warehouse: 0, total: 0 },
                available:      { store: 0, warehouse: 0, total: item.available || 0 },
            };
        },

        async fetchStockBreakdown(skus) {
            if (!skus || skus.length === 0) return;
            const batchSize = 200;
            const batches = [];
            for (let i = 0; i < skus.length; i += batchSize) {
                batches.push(skus.slice(i, i + batchSize));
            }
            const merged = {};
            for (const batch of batches) {
                try {
                    const data = { skus: batch.join(','), limit: batch.length };
                    if (this.location_id) data.location_id = this.location_id;
                    const response = await new Promise((resolve, reject) => {
                        Requestor.getSyncP("/api/product/get_product_stock_breakdown_v3/", data, this, resolve, reject);
                    });
                    if (response.data && response.data.result) {
                        response.data.result.forEach(row => {
                            merged[row.product_sku] = {
                                in_stock:       row.in_stock       || { store: 0, warehouse: 0, total: 0 },
                                sold:           row.sold           || { store: 0, warehouse: 0, total: 0 },
                                reserved:       row.reserved       || { store: 0, warehouse: 0, total: 0 },
                                layaway:        row.layaway        || { store: 0, warehouse: 0, total: 0 },
                                store_transfer: row.store_transfer || { store: 0, warehouse: 0, total: 0 },
                                available:      row.available      || { store: 0, warehouse: 0, total: 0 },
                            };
                        });
                    }
                } catch (error) {
                    console.error("Error fetching stock breakdown batch:", error);
                }
            }
            this.stockBreakdown = { ...this.stockBreakdown, ...merged };
        },
        
        openStockBreakdown(item, tab = 'in_stock') {
            this.stockModalSku = null;
            this.stockModalTab = tab || 'in_stock';
            this.$nextTick(() => {
                this.stockModalSku = item.sku;
                this.stockModalName = item.product_name || item.sku;
                this.$bvModal.show('modal-stock-breakdown');
            });
        },

        closeStockBreakdown() {
            this.stockModalSku = null;
            this.stockModalName = null;
            this.stockModalTab = 'in_stock';
        },

        // ============================================
        // SALE HELPERS
        // ============================================
        parseSaleDate(d) {
            if (!d) return null;
            let m = moment(d, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
            if (!m.isValid()) m = moment(d);
            return m.isValid() ? m : null;
        },

        hasActiveDiscount(item) {
            if (!item || !(parseFloat(item.special_discount_price) > 0)) return false;

            const now = moment();

            const start = this.parseSaleDate(item.special_discount_start_date);
            if (start && now.isBefore(start.clone().startOf('day'))) {
                return false;
            }

            const end = this.parseSaleDate(item.special_discount_end_date);
            if (!end) return false;
            return !now.isAfter(end.clone().endOf('day'));
        },

        isDiscountExpired(item) {
            const end = this.parseSaleDate(item.special_discount_end_date);
            if (!end) return true;
            return moment().isAfter(end.clone().endOf('day'));
        },

        saleState(item) {
            const hasPrice = parseFloat(item.special_discount_price) > 0;

            if (hasPrice && item.special_discount_start_date) {
                const start = this.parseSaleDate(item.special_discount_start_date);
                if (start && moment().isBefore(start.clone().startOf('day'))) {
                    return 'scheduled';
                }
            }

            if (this.hasActiveDiscount(item)) return 'active';

            if (hasPrice && item.special_discount_end_date) return 'expired';
            return 'none';
        },

        saleDaysLeft(item) {
            const end = this.parseSaleDate(item.special_discount_end_date);
            if (!end) return null;
            return end.clone().startOf('day').diff(moment().startOf('day'), 'days');
        },
        saleDaysLeftLabel(item) {
            const d = this.saleDaysLeft(item);
            if (d === null) return '';
            if (d <= 0) return 'ends today';
            return d === 1 ? '1 day left' : `${d} days left`;
        },
        saleStartsInLabel(item) {
            if (this.saleState(item) !== 'scheduled') return '';
            const start = this.parseSaleDate(item.special_discount_start_date);
            if (!start) return '';
            const days = start.clone().startOf('day').diff(moment().startOf('day'), 'days');
            if (days <= 0) return 'starts today';
            return days === 1 ? 'starts tomorrow' : `starts in ${days} days`;
        },
        saleSavings(item) {
            const diff = (parseFloat(item.selling_price) || 0) - (parseFloat(item.special_discount_price) || 0);
            return Math.max(0, diff).toFixed(2);
        },
        salePillLabel(item) {
            const state = this.saleState(item);
            if (state === 'scheduled') return 'Scheduled';
            if (state === 'expired')   return 'Expired';
            if (state === 'none')      return 'None';
            return item.sale_type || 'Special';
        },
        salePillClass(item) {
            const state = this.saleState(item);
            if (state !== 'active') return 'is-' + state;
            return { Sale: 'is-sale', Clearance: 'is-clearance', Promo: 'is-promo' }[item.sale_type] || 'is-sale';
        },
        calculateDiscountedPrice(item) {
            if (!this.hasActiveDiscount(item)) return item.selling_price;
            return parseFloat(item.special_discount_price) || item.selling_price;
        },
        calculateDiscountPercentage(item) {
            if (!item.special_discount_price || !item.selling_price || item.selling_price <= 0) return 0;
            const d = ((parseFloat(item.selling_price) - parseFloat(item.special_discount_price)) / parseFloat(item.selling_price)) * 100;
            return Math.round(d);
        },
        hasActiveSale(item) { return item.sale_type === 'Sale' && this.hasActiveDiscount(item); },
        hasActiveClearance(item) { return item.sale_type === 'Clearance' && this.hasActiveDiscount(item); },
        hasActivePromo(item) { return item.sale_type === 'Promo' && this.hasActiveDiscount(item); },
        hasScheduledSale(item) { return this.saleState(item) === 'scheduled'; },
        getDiscountLabel(item) {
            if (item.sale_type === 'Promo') {
                const orig = parseFloat(item.selling_price) || 0;
                const now = parseFloat(item.special_discount_price) || 0;
                return `$${Math.max(0, orig - now).toFixed(2)} off`;
            }
            return `${this.calculateDiscountPercentage(item)}% off`;
        },
        formatDate(dateString) {
            const m = this.parseSaleDate(dateString);
            return m ? m.format('MMM D, YYYY') : '';
        },
        formatPriceNum(price) {
            if (!price && price !== 0) return '0.00';
            const num = parseFloat(price);
            return isNaN(num) ? '0.00' : num.toFixed(2);
        },
        suggestedCost(item) {
            return (parseFloat(item.landed_cost) || 0) * 2;
        },

        openItemSaleModal(item) {
            this.managerSaleItem = item;
            const isPromo = item.sale_type === 'Promo';
            const isActive = this.hasActiveDiscount(item);
            const isScheduled = this.saleState(item) === 'scheduled';

            this.managerSaleForm = {
                actionType: item.sale_type || 'Sale',
                discountPercentage: isPromo ? null : (this.calculateDiscountPercentage(item) || 10),
                discountAmount: isPromo
                    ? Math.max(0, (parseFloat(item.selling_price) || 0) - (parseFloat(item.special_discount_price) || 0)) || null
                    : null,
                maxDiscount: item.max_discount_percentage || 10,
                startDate: (isActive || isScheduled) && item.special_discount_start_date
                    ? (this.parseSaleDate(item.special_discount_start_date) || moment()).format('MM-DD-YYYY')
                    : moment().format('MM-DD-YYYY'),
                endDate: (isActive && item.special_discount_end_date)
                    ? (this.parseSaleDate(item.special_discount_end_date) || moment()).format('MM-DD-YYYY')
                    : moment().add(30, 'days').format('MM-DD-YYYY')
            };
            this.managerSalePreview = null;
            this.recalcManagerPreview();
            this.$bvModal.show('modal-manager-sale');
        },
        resetManagerSaleForm() {
            this.managerSaleItem = null;
            this.managerSalePreview = null;
        },
        recalcManagerPreview() {
            if (!this.managerSaleItem) return;
            const orig = parseFloat(this.managerSaleItem.selling_price) || 0;
            if (this.managerSaleForm.actionType === 'Promo') {
                const amt = parseFloat(this.managerSaleForm.discountAmount) || 0;
                if (amt <= 0) { this.managerSalePreview = null; return; }
                this.managerSalePreview = Math.max(0, orig - amt);
            } else {
                const pct = parseFloat(this.managerSaleForm.discountPercentage) || 0;
                if (pct <= 0) { this.managerSalePreview = null; return; }
                this.managerSalePreview = Math.round(orig * (1 - pct / 100) * 100) / 100;
            }
        },
        async saveManagerSale() {
            if (!this.managerSaleItem) return;
            if (!this.managerSaleForm.startDate || !this.managerSaleForm.endDate) {
                this.$bvToast.toast("Please select start and end dates", { title: "Validation Error", variant: "warning", autoHideDelay: 2000 });
                return;
            }
            if (moment(this.managerSaleForm.startDate, 'MM-DD-YYYY').isAfter(moment(this.managerSaleForm.endDate, 'MM-DD-YYYY'))) {
                this.$bvToast.toast("Start date must be before end date", { title: "Validation Error", variant: "warning", autoHideDelay: 2000 });
                return;
            }
            this.savingManagerSale = true;
            try {
                const payload = {
                    product_skus: [this.managerSaleItem.sku],
                    sale_type: this.managerSaleForm.actionType,
                    max_discount_percentage: parseFloat(this.managerSaleForm.maxDiscount) || 0,
                    discount_start_date: this.managerSaleForm.startDate,
                    discount_end_date: this.managerSaleForm.endDate,
                    update_children: false,
                    user_details: {
                        username: this.packet.userDetails.username,
                        user_id: this.packet.userDetails.user_id
                    }
                };
                if (this.managerSaleForm.actionType === 'Promo') {
                    payload.discount_amount = parseFloat(this.managerSaleForm.discountAmount);
                    payload.discount_mode = 'amount';
                } else {
                    payload.discount_percentage = parseFloat(this.managerSaleForm.discountPercentage);
                    payload.discount_mode = 'percentage';
                }
                const res = await new Promise((resolve, reject) => {
                    Requestor.postSyncP("/api/product/change_bulk_product_status/", payload, this, resolve, reject);
                });
                if (res.data.error) throw new Error(res.data.error);
                this.$bvToast.toast(`Saved ${this.managerSaleForm.actionType} for ${this.managerSaleItem.sku}`, { title: 'Success', variant: 'success', autoHideDelay: 2000 });
                this.$bvModal.hide('modal-manager-sale');
                this.fetch_init(false, true);
            } catch (e) {
                this.$bvToast.toast(e.message || 'Error saving sale', { title: 'Error', variant: 'danger', autoHideDelay: 3000 });
            } finally {
                this.savingManagerSale = false;
            }
        },
        removeItemSale(item) {
            if (!confirm(`Remove sale/promo/clearance from ${item.sku}?`)) return;
            const that = this;
            Requestor.postAsyncP(
                "/api/product/change_bulk_product_status/",
                {
                    product_skus: [item.sku],
                    sale_type: null,
                    user_details: {
                        username: this.packet.userDetails.username,
                        user_id: this.packet.userDetails.user_id
                    }
                },
                this,
                function(res) {
                    if (res.data.error) that.$bvToast.toast(res.data.error, { title: 'Error', variant: 'danger' });
                    else {
                        that.$bvToast.toast(`Removed sale from ${item.sku}`, { title: 'Success', variant: 'success' });
                        that.fetch_init(false, true);
                    }
                },
                function(err) {
                    console.error(err);
                    that.$bvToast.toast('Error removing sale', { title: 'Error', variant: 'danger' });
                }
            );
        },

        openBulkSaleModal() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            this.salePrice = null;
            this.saleMaxDiscount = 10;
            this.saleStartDate = moment().format('MM-DD-YYYY');
            this.saleEndDate = null;
            this.updateChildrenSale = false;
            this.saleModalVisible = true;
            this.$bvModal.show('modal-bulk-sale');
        },
        openBulkClearanceModal() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            this.clearancePrice = null;
            this.clearanceMaxDiscount = 25;
            this.clearanceStartDate = moment().format('MM-DD-YYYY');
            this.updateChildrenClearance = false;
            this.clearanceModalVisible = true;
            this.$bvModal.show('modal-bulk-clearance');
        },
        executeBulkSaleChange() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            if (!this.salePrice || this.salePrice <= 0) { this.$bvToast.toast("Please enter a valid sale price", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return; }
            if (!this.saleMaxDiscount || this.saleMaxDiscount < 0 || this.saleMaxDiscount > 100) { this.$bvToast.toast("Please enter a valid max discount", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return; }
            if (!this.saleStartDate) { this.$bvToast.toast("Please select a start date", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return; }
            if (!this.saleEndDate) { this.$bvToast.toast("Please select an end date", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return; }
            if (moment(this.saleStartDate, 'MM-DD-YYYY').isAfter(moment(this.saleEndDate, 'MM-DD-YYYY'))) {
                this.$bvToast.toast("Start date must be before end date", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return;
            }
            this.isLoadingMore = true;
            const path = "/api/product/change_bulk_product_status/";
            const data = {
                product_skus: this.selectedProducts,
                sale_type: 'Sale',
                discount_price: this.salePrice,
                max_discount_percentage: this.saleMaxDiscount,
                discount_start_date: this.saleStartDate,
                discount_end_date: this.saleEndDate,
                update_children: this.updateChildrenSale || false,
                user_details: { username: this.packet.userDetails.username, user_id: this.packet.userDetails.user_id }
            };
            const that = this;
            Requestor.postAsyncP(path, data, this,
                function(res) {
                    that.isLoadingMore = false;
                    if (res.data.error) that.$bvToast.toast(res.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    else {
                        that.$bvToast.toast(`Added ${res.data.result.updated_count || that.selectedProducts.length} products to Sale`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                        that.clearSelection(); that.showSelectionMode = false; that.fetch_init(true, true);
                    }
                },
                function(err) { that.isLoadingMore = false; that.$bvToast.toast("Error adding to Sale", { title: "Error", autoHideDelay: 3000, variant: "danger" }); }
            );
            this.$bvModal.hide('modal-bulk-sale');
        },
        executeBulkClearanceChange() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            if (!this.clearancePrice || this.clearancePrice <= 0) { this.$bvToast.toast("Please enter a valid clearance price", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return; }
            if (!this.clearanceMaxDiscount || this.clearanceMaxDiscount < 0 || this.clearanceMaxDiscount > 100) { this.$bvToast.toast("Please enter a valid max discount", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return; }
            if (!this.clearanceStartDate) { this.$bvToast.toast("Please select a start date", { title: "Validation Error", autoHideDelay: 2000, variant: "warning" }); return; }
            this.isLoadingMore = true;
            const path = "/api/product/change_bulk_product_status/";
            const data = {
                product_skus: this.selectedProducts,
                sale_type: 'Clearance',
                discount_price: this.clearancePrice,
                max_discount_percentage: this.clearanceMaxDiscount,
                discount_start_date: this.clearanceStartDate,
                update_children: this.updateChildrenClearance || false,
                user_details: { username: this.packet.userDetails.username, user_id: this.packet.userDetails.user_id }
            };
            const that = this;
            Requestor.postAsyncP(path, data, this,
                function(res) {
                    that.isLoadingMore = false;
                    if (res.data.error) that.$bvToast.toast(res.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    else {
                        that.$bvToast.toast(`Added ${res.data.result.updated_count || that.selectedProducts.length} products to Clearance`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                        that.clearSelection(); that.showSelectionMode = false; that.fetch_init(true, true);
                    }
                },
                function(err) { that.isLoadingMore = false; that.$bvToast.toast("Error adding to Clearance", { title: "Error", autoHideDelay: 3000, variant: "danger" }); }
            );
            this.$bvModal.hide('modal-bulk-clearance');
        },
        removeBulkSaleClearance() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            if (!confirm(`Remove Sale/Clearance from ${this.selectedProducts.length} product(s)?`)) return;
            this.isLoadingMore = true;
            const path = "/api/product/change_bulk_product_status/";
            const data = {
                product_skus: this.selectedProducts, sale_type: null, update_children: false,
                user_details: { username: this.packet.userDetails.username, user_id: this.packet.userDetails.user_id }
            };
            const that = this;
            Requestor.postAsyncP(path, data, this,
                function(res) {
                    that.isLoadingMore = false;
                    if (res.data.error) that.$bvToast.toast(res.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    else {
                        that.$bvToast.toast(`Removed Sale/Clearance from ${res.data.result.updated_count || that.selectedProducts.length} products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                        that.clearSelection(); that.showSelectionMode = false; that.fetch_init(true, true);
                    }
                },
                function(err) { that.isLoadingMore = false; that.$bvToast.toast("Error removing Sale/Clearance", { title: "Error", autoHideDelay: 3000, variant: "danger" }); }
            );
        },

        async fetchDiscountedProducts() {
            try {
                this.items_being_loaded = true;
                this.isShowingDiscounted = true;
                const path = "/api/product/get_discounted_products/";
                const data = {
                    discount_type: this.discountFilter || 'active', location_id: this.location_id,
                    user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id,
                    offset: 0, limit: this.pageSize
                };
                if (this.category_id) data.category_id = this.category_id;
                if (this.vendor_id) data.vendor_id = this.vendor_id;
                if (this.product_query) data.product_query = this.product_query;
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, data, this, resolve, reject); });
                if (response.data.error) this.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 2000, variant: "danger" });
                else if (response.data.data) {
                    this.allItems = response.data.data.map(item => ({
                        ...item, edit: false,
                        quantity_per_box: item.quantity_per_box || 1, wholesale_price: item.wholesale_price || 0,
                        landed_cost: item.landed_cost || 0, spiff: item.spiff || 0, cubes: item.cubes || 0,
                        in_stock: item.in_stock || 0, reserved: item.reserved || 0, available: item.available || 0,
                        missing: Math.max(0, (item.in_stock || 0) - (item.reserved || 0) - (item.available || 0)),
                        sale_type: item.sale_type || null, max_discount_percentage: item.max_discount_percentage || null,
                        special_discount_start_date: item.special_discount_start_date || null,
                        special_discount_end_date: item.special_discount_end_date || null
                    }));
                    this.updateDisplayedItems();
                    this.totalItemsCount = this.allItems.length;
                    this.hasMore = false;
                    this.$bvToast.toast(`Found ${this.allItems.length} discounted products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                    this.fetchStockBreakdown(this.allItems.map(i => i.sku));
                }
            } catch (error) {
                console.error(error);
                this.$bvToast.toast("Error fetching discounted products", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally { this.items_being_loaded = false; }
        },

        async showReceivedDetails(item) {
            if (!item || !item.sku) { this.$bvToast.toast("Invalid product", { title: "Error", variant: "danger" }); return; }
            this.currentProduct = item; this.isLoadingReceivedDetails = true;
            this.receivedHistory = [];
            this.receivedStats = { total_received: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            try {
                const path = "/api/product/get_received_history/";
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, { product_sku: item.sku, include_history: true }, this, resolve, reject); });
                if (response.data && response.data.success && response.data.result) {
                    if (response.data.result.length > 0) {
                        this.receivedHistory = this.processReceivedHistory(response.data.result);
                        this.receivedStats = this.calculateReceivedStats(response.data.result);
                    }
                    this.$bvModal.show('modal-received-details');
                } else throw new Error(response.data.error || "No data received");
            } catch (error) {
                console.error(error);
                this.$bvToast.toast(error.message || "Error loading received history", { title: "Error", variant: "danger" });
            } finally { this.isLoadingReceivedDetails = false; }
        },
        processReceivedHistory(historyData) {
            const groupedOrders = {};
            historyData.forEach(record => {
                const key = record.order_id + '_' + record.order_type;
                if (!groupedOrders[key]) groupedOrders[key] = { order_id: record.order_id, order_type: record.order_type, product_sku: record.product_sku, quantity: 0, timeline: [] };
                groupedOrders[key].quantity += record.quantity || 0;
                groupedOrders[key].timeline.push({ date: record.order_date, quantity: record.quantity || 0, status: record.status_label || 'Received', created_by: record.created_by, version: record.version || 1, action_type: record.action_type || 'create' });
            });
            return Object.values(groupedOrders);
        },
        calculateReceivedStats(historyData) {
            const stats = { total_received: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            historyData.forEach(record => {
                stats.total_received++;
                stats.total_quantity += record.quantity || 0;
                stats.by_status[record.status_label || 'Received'] = (stats.by_status[record.status_label || 'Received'] || 0) + (record.quantity || 0);
                stats.by_type[record.order_type || 'Unknown'] = (stats.by_type[record.order_type || 'Unknown'] || 0) + (record.quantity || 0);
                if (record.order_date) {
                    const dateKey = moment(record.order_date).format('YYYY-MM-DD');
                    stats.by_date[dateKey] = (stats.by_date[dateKey] || 0) + (record.quantity || 0);
                }
            });
            return stats;
        },
        async showScheduledDetails(item) {
            if (!item || !item.sku) { this.$bvToast.toast("Invalid product", { title: "Error", variant: "danger" }); return; }
            this.currentProduct = item; this.isLoadingScheduledDetails = true;
            this.scheduledHistory = [];
            this.scheduledStats = { total_scheduled: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            try {
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP("/api/product/get_scheduled_history/", { product_sku: item.sku, include_history: true }, this, resolve, reject); });
                if (response.data && response.data.success && response.data.result) {
                    if (response.data.result.length > 0) {
                        this.scheduledHistory = this.processScheduledHistory(response.data.result);
                        this.scheduledStats = this.calculateScheduledStats(response.data.result);
                    }
                    this.$bvModal.show('modal-scheduled-details');
                } else throw new Error(response.data.error || "No data received");
            } catch (error) {
                console.error(error);
                this.$bvToast.toast(error.message || "Error loading scheduled history", { title: "Error", variant: "danger" });
            } finally { this.isLoadingScheduledDetails = false; }
        },
        processScheduledHistory(historyData) {
            const groupedOrders = {};
            historyData.forEach(record => {
                const key = record.order_id + '_' + record.order_type;
                if (!groupedOrders[key]) groupedOrders[key] = { order_id: record.order_id, order_type: record.order_type, product_sku: record.product_sku, quantity: 0, timeline: [] };
                groupedOrders[key].quantity += record.quantity || 0;
                groupedOrders[key].timeline.push({ date: record.order_date, quantity: record.quantity || 0, status: record.status_label || 'Scheduled', created_by: record.created_by, version: record.version || 1, action_type: record.action_type || 'create' });
            });
            return Object.values(groupedOrders);
        },
        calculateScheduledStats(historyData) {
            const stats = { total_scheduled: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            historyData.forEach(record => {
                stats.total_scheduled++;
                stats.total_quantity += record.quantity || 0;
                stats.by_status[record.status_label || 'Scheduled'] = (stats.by_status[record.status_label || 'Scheduled'] || 0) + (record.quantity || 0);
                stats.by_type[record.order_type || 'Unknown'] = (stats.by_type[record.order_type || 'Unknown'] || 0) + (record.quantity || 0);
                if (record.order_date) {
                    const dateKey = moment(record.order_date).format('YYYY-MM-DD');
                    stats.by_date[dateKey] = (stats.by_date[dateKey] || 0) + (record.quantity || 0);
                }
            });
            return stats;
        },
        getStatusVariant(status) {
            const variants = { 'Ordered': 'success', 'Scheduled': 'warning', 'Pending': 'secondary', 'Shipped': 'info', 'Unknown': 'dark' };
            return variants[status] || 'secondary';
        },
        async showOrderedDetails(item) {
            this.currentProduct = item; this.isLoadingOrderDetails = true;
            try {
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP("/api/product/get_ordered_history/", { product_sku: item.sku, include_history: true }, this, resolve, reject); });
                if (response.data.result) {
                    this.orderHistory = this.processOrderHistory(response.data.result);
                    this.orderStats = this.calculateOrderStats(response.data.result);
                    this.$bvModal.show('modal-order-details');
                }
            } catch (error) {
                console.error(error);
                this.$bvToast.toast("Error loading order history", { title: "Error", variant: "danger" });
            } finally { this.isLoadingOrderDetails = false; }
        },
        processOrderHistory(historyData) {
            const groupedOrders = {};
            historyData.forEach(record => {
                const key = record.order_id + '_' + record.order_type;
                if (!groupedOrders[key]) groupedOrders[key] = { order_id: record.order_id, order_type: record.order_type, product_sku: record.product_sku, quantity: 0, timeline: [] };
                groupedOrders[key].quantity += record.quantity;
                groupedOrders[key].timeline.push({ date: record.order_date, quantity: record.quantity, status: record.status_label, created_by: record.created_by, version: record.version || 1 });
            });
            return Object.values(groupedOrders);
        },
        calculateOrderStats(historyData) {
            const stats = { total_orders: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            historyData.forEach(record => {
                stats.total_orders++;
                stats.total_quantity += record.quantity;
                stats.by_status[record.status_label] = (stats.by_status[record.status_label] || 0) + record.quantity;
                stats.by_type[record.order_type] = (stats.by_type[record.order_type] || 0) + record.quantity;
                const dateKey = moment(record.order_date).format('YYYY-MM-DD');
                stats.by_date[dateKey] = (stats.by_date[dateKey] || 0) + record.quantity;
            });
            return stats;
        },
        show_sold_details(item) { this.currentProduct = item; this.$bvModal.show('modal-sold-details'); },

        async fetchCategoryStockStats() {
            if (!this.category_id) { this.$bvToast.toast("Please select a category first", { title: "Warning", variant: "warning", autoHideDelay: 2000 }); return; }
            this.isLoadingCategoryStats = true;
            this.showCategoryStats = false;
            try {
                const data = { category_id: this.category_id, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
                if (this.packet.userDetails.userrole === 'admin') data.location_id = this.location_id;
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP("/api/product/get_category_stock_stats/", data, this, resolve, reject); });
                if (response.data.error) this.$bvToast.toast(response.data.error, { title: "Error", variant: "danger", autoHideDelay: 3000 });
                else {
                    const r = response.data.result;
                    this.categoryStats = {
                        category_name: r.category_name || '',
                        category_code: r.category_code || '',
                        category_id: r.category_id || this.category_id,
                        total_in_stock: r.total_in_stock || 0,
                        total_reserved: r.total_reserved || 0,
                        total_available: r.total_available || 0,
                        total_layawayed: r.total_layawayed || 0,
                        total_missing: r.total_missing || 0,
                        total_sold_so_far: r.total_sold_so_far || 0,
                        total_ordered: r.total_ordered || 0,
                        total_scheduled: r.total_scheduled || 0,
                        total_products: r.total_products || 0,
                        products_in_stock: r.products_in_stock || 0,
                        products_out_of_stock: r.products_out_of_stock || 0,
                        parent_products: r.parent_products || 0,
                        child_products: r.child_products || 0
                    };
                    this.showCategoryStats = true;
                    this.$bvToast.toast(`Stats loaded for ${this.categoryStats.category_name}`, { title: "Success", variant: "success", autoHideDelay: 2000 });
                }
            } catch (error) {
                console.error(error);
                this.$bvToast.toast("Error fetching category statistics", { title: "Error", variant: "danger", autoHideDelay: 3000 });
            } finally { this.isLoadingCategoryStats = false; }
        },

        resetAllFilters() {
            this.showMoreFilters = false;
            this.vendor_id = null; this.category_id = null; this.subcategory_id = null; this.product_query = null;
            this.itemTypeFilter = null; this.itemStatusFilter = null; this.statusFilter = 'all';
            this.discontinuedFilter = null; this.newArrivalFilter = null; this.receivedStatusFilter = null;
            this.typeFilter = null; this.colorFilter = null; this.sizeFilter = null;
            this.materialFilter = null; this.functionalityFilter = null;
            this.bestSellingFilter = null; this.slowestSellingFilter = null; this.spiffFilter = null;
            this.isShowingDiscounted = false; this.discountFilter = 'active';
            this.showCategoryStats = false;
            if (this.packet.userDetails.userrole === 'admin') this.location_id = null;
            else this.location_id = this.user_location_id;
            this.fetch_init(true, true);
        },
        removeFilter(filterKey) {
            switch(filterKey) {
                case 'vendor': this.vendor_id = null; break;
                case 'category': this.category_id = null; break;
                case 'subcategory': this.subcategory_id = null; break;
                case 'search': this.product_query = null; break;
                case 'location': this.location_id = this.user_location_id; break;
                case 'itemType': this.itemTypeFilter = null; break;
                case 'itemStatus': this.itemStatusFilter = null; break;
                case 'status': this.statusFilter = 'all'; break;
                case 'discontinued': this.discontinuedFilter = null; break;
                case 'newArrival': this.newArrivalFilter = null; break;
                case 'receivedStatus': this.receivedStatusFilter = null; break;
                case 'type': this.typeFilter = null; break;
                case 'color': this.colorFilter = null; break;
                case 'size': this.sizeFilter = null; break;
                case 'material': this.materialFilter = null; break;
                case 'functionality': this.functionalityFilter = null; break;
                case 'spiff': this.spiffFilter = null; break;
                case 'bestSelling': this.bestSellingFilter = null; break;
                case 'slowestSelling': this.slowestSellingFilter = null; break;
                case 'discount': this.isShowingDiscounted = false; this.discountFilter = 'active'; break;
            }
            this.showCategoryStats = false;
            this.fetch_init(false, true);
        },
        clearAllFilters() { this.resetAllFilters(); },
        openPrintModal() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("Please select products first", { title: "No Selection", variant: "warning", autoHideDelay: 2000 }); return; }
            if (this.selectedProducts.length > 24) { this.$bvToast.toast(`Maximum 24 products allowed. You selected ${this.selectedProducts.length}`, { title: "Limit Exceeded", variant: "warning", autoHideDelay: 3000 }); return; }
            this.generateBarcodesForPrint();
            this.$bvModal.show('modal-print-barcodes');
        },
        async generateBarcodesForPrint() {
            this.printProducts = [];
            for (const sku of this.selectedProducts) {
                const existingProduct = this.allItems.find(p => p.sku === sku);
                if (existingProduct) {
                    const barcodeDataUrl = await this.generateBarcodeDataUrl(sku);
                    this.printProducts.push({ sku: existingProduct.sku, product_name: existingProduct.product_name, selling_price: existingProduct.selling_price, barcodeDataUrl });
                }
            }
        },
        generateBarcodeDataUrl(sku) {
            return new Promise((resolve) => {
                const existingProduct = this.allItems.find(p => p.sku === sku);
                if (existingProduct && existingProduct.barcode_image) { resolve(existingProduct.barcode_image); return; }
                const canvas = document.createElement('canvas');
                canvas.width = 300; canvas.height = 100;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = '#000000';
                let x = 20;
                for (let i = 0; i < sku.length; i++) {
                    const code = sku.charCodeAt(i);
                    const width = (code % 8) + 2;
                    ctx.fillRect(x, 10, width, 70);
                    x += width + 2;
                }
                ctx.font = 'bold 14px monospace';
                const textWidth = ctx.measureText(sku).width;
                ctx.fillText(sku, canvas.width / 2 - textWidth / 2, 95);
                resolve(canvas.toDataURL('image/png'));
            });
        },
        removePrintItem(index) {
            const removed = this.printProducts[index];
            this.printProducts.splice(index, 1);
            if (removed) {
                this.selectedProducts = this.selectedProducts.filter(sku => sku !== removed.sku);
                this.updateSelectAll();
            }
        },
        clearPrintAll() { this.printProducts = []; this.selectedProducts = []; this.selectAll = false; this.$bvModal.hide('modal-print-barcodes'); },
        resetPrintModal() {
            this.printProducts = []; this.isPrinting = false;
            this.printSettings = { labelSize: '1.5x2', copies: 1, printMethod: 'browser', layout: 'single', showPrice: true, showProductName: true, showSku: true };
        },
        async executePrint() {
            if (this.isPrinting) return;
            this.isPrinting = true;
            try {
                if (this.printSettings.printMethod === 'browser') await this.printViaBrowser();
                else if (this.printSettings.printMethod === 'newWindow') await this.printViaNewWindow();
                else await this.printViaDirect();
                this.$bvToast.toast(`Printed ${this.printAllPages.length} label(s)`, { title: "Print Success", variant: "success", autoHideDelay: 3000 });
                this.$bvModal.hide('modal-print-barcodes');
            } catch (error) {
                console.error(error);
                this.$bvToast.toast(error.message || "Print failed", { title: "Print Error", variant: "danger", autoHideDelay: 5000 });
            } finally { this.isPrinting = false; }
        },
        async printViaBrowser() {
            const dims = this.labelDimensions[this.printSettings.labelSize] || this.labelDimensions['1.5x2'];
            const allLabels = this.printAllPages;
            const printWindow = window.open('', '_blank');
            if (!printWindow) throw new Error('Pop-up blocked. Allow pop-ups to print labels.');
            const labelHTML = allLabels.map((p) => `<div class="print-label" style="width:${dims.w}in;height:${dims.h}in;page-break-after:always;padding:0.1in;text-align:center;box-sizing:border-box;border:1px solid #ccc;display:flex;flex-direction:column;justify-content:center;align-items:center;font-family:Arial,sans-serif;"><img src="${p.barcodeDataUrl}" style="max-width:${dims.w - 0.3}in;max-height:${dims.h - 0.8}in;" />${this.printSettings.showSku ? `<div style="font-weight:bold;font-size:${dims.w >= 2 ? '12pt' : '10pt'};margin-top:0.05in;">${p.sku}</div>` : ''}${this.printSettings.showProductName ? `<div style="font-size:${dims.w >= 2 ? '10pt' : '8pt'};margin-top:0.03in;">${this.truncateText(p.product_name, dims.w >= 2 ? 40 : 25)}</div>` : ''}${this.printSettings.showPrice ? `<div style="font-size:${dims.w >= 2 ? '11pt' : '9pt'};color:#2e7d32;margin-top:0.03in;">$${this.formatPriceNum(p.selling_price)}</div>` : ''}</div>`).join('');
            printWindow.document.write(`<!DOCTYPE html><html><head><title>Print</title><style>@page{size:${dims.w}in ${dims.h}in;margin:0.05in;}</style></head><body>${labelHTML}<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};<\/script></body></html>`);
            printWindow.document.close();
        },
        async printViaNewWindow() {
            const dims = this.labelDimensions[this.printSettings.labelSize] || this.labelDimensions['1.5x2'];
            let content = `<html><head><title>Print</title><style>@page{size:${dims.w}in ${dims.h}in;margin:0.05in;}</style></head><body>`;
            for (const p of this.printAllPages) {
                content += `<div style="width:${dims.w}in;height:${dims.h}in;page-break-after:always;text-align:center;"><img src="${p.barcodeDataUrl}" style="max-width:${dims.w - 0.3}in;" />${this.printSettings.showSku ? `<div style="font-weight:bold;">${p.sku}</div>` : ''}</div>`;
            }
            content += `<script>window.onload=function(){window.print();};<\/script></body></html>`;
            const w = window.open();
            if (!w) throw new Error('Pop-up blocked. Allow pop-ups to print labels.');
            w.document.write(content); w.document.close();
        },
        async printViaDirect() { await this.printViaBrowser(); },
        truncateText(text, maxLength) {
            if (!text) return '';
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength - 3) + '...';
        },

        showBulkImageUpload() { this.bulkImages = []; this.bulkImagePreview = []; this.$bvModal.show('modal-bulk-image-upload'); },
        async handleBulkImageSelect(files) {
            this.bulkImages = []; this.bulkImagePreview = [];
            for (let file of files) {
                const fileName = file.name;
                const sku = fileName.replace(/\.[^/.]+$/, '');
                const base64 = await this.fileToBase64(file);
                this.bulkImages.push({ file, sku, originalName: fileName, preview: base64, status: 'pending', error: null });
                this.bulkImagePreview.push({ sku, preview: base64, fileName });
            }
        },
        async processBulkImageUpload() {
            if (this.bulkImages.length === 0) { this.$bvToast.toast("Please select images", { title: "Warning", autoHideDelay: 2000, variant: "warning" }); return; }
            this.uploadingBulkImages = true;
            this.bulkImageProgress = { total: this.bulkImages.length, processed: 0, successful: 0, failed: 0, current: 0 };
            this.$bvModal.show('modal-bulk-image-progress');
            for (let i = 0; i < this.bulkImages.length; i++) {
                const imageItem = this.bulkImages[i];
                this.bulkImageProgress.current = i + 1;
                this.bulkImageProgress.processed = i;
                const product = this.allItems.find(p => p.sku === imageItem.sku || p.sku.toLowerCase() === imageItem.sku.toLowerCase());
                if (!product) { imageItem.status = 'failed'; imageItem.error = `Product "${imageItem.sku}" not found`; this.bulkImageProgress.failed++; continue; }
                try {
                    const base64Image = await this.fileToBase64(imageItem.file);
                    const response = await new Promise((resolve, reject) => { Requestor.postSyncP("/api/product/upload_product_image/", { product_sku: product.sku, product_id: product.id, image_data: base64Image }, this, resolve, reject); });
                    if (response.data.error) throw new Error(response.data.error);
                    this.updateProductInList(response.data.result);
                    imageItem.status = 'success';
                    this.bulkImageProgress.successful++;
                } catch (error) {
                    imageItem.status = 'failed';
                    imageItem.error = error.message || "Upload failed";
                    this.bulkImageProgress.failed++;
                }
                this.bulkImageProgress.processed = i + 1;
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            this.bulkImageProgress.processed = this.bulkImageProgress.total;
            this.uploadingBulkImages = false;
            this.$bvToast.toast(`Bulk upload: ${this.bulkImageProgress.successful} ok, ${this.bulkImageProgress.failed} failed`, { title: "Complete", variant: this.bulkImageProgress.failed === 0 ? "success" : "warning", autoHideDelay: 5000 });
            this.fetch_init(true, true);
        },
        resetBulkImageUpload() {
            this.bulkImages = []; this.bulkImagePreview = [];
            this.uploadingBulkImages = false;
            this.bulkImageProgress = { total: 0, processed: 0, successful: 0, failed: 0, current: 0 };
        },

        showLowInventoryAlert(product) {
            if (!product) { this.$bvToast.toast("Invalid product", { title: "Error", variant: "danger" }); return; }
            this.currentAlertProduct = product;
            if (product.low_inventory_alert_id) {
                this.alertConfig = {
                    alert_type: product.low_inventory_alert_type || 'threshold',
                    threshold_quantity: product.low_inventory_threshold || 10,
                    notification_methods: product.low_inventory_notification_methods || ['email', 'dashboard'],
                    alert_emails: product.low_inventory_alert_emails || '',
                    notes: product.low_inventory_notes || ''
                };
            } else if (product.properties && product.properties.low_inventory === true) {
                this.alertConfig = { alert_type: 'threshold', threshold_quantity: 5, notification_methods: ['email', 'dashboard'], alert_emails: '', notes: 'Converted from legacy flag' };
            } else {
                this.alertConfig = { alert_type: 'threshold', threshold_quantity: 10, notification_methods: ['email', 'dashboard'], alert_emails: '', notes: '' };
            }
            this.currentInventoryLevel = null;
            this.fetchCurrentInventory(product.id || product.product_id);
            this.showLowInventoryModal = true;
        },
        fetchCurrentInventory(productId) {
            if (!productId) { this.currentInventoryLevel = null; return; }
            const that = this;
            Requestor.postSyncP("/api/inventory/get_product_inventory/", { product_id: productId }, this,
                function(response) {
                    if (response.data && response.data.result && response.data.result.current_stock !== undefined) that.currentInventoryLevel = response.data.result.current_stock;
                    else that.currentInventoryLevel = 0;
                },
                function(error) { that.currentInventoryLevel = null; }
            );
        },
        resetLowInventoryModal() {
            this.alertConfig = { alert_type: 'threshold', threshold_quantity: 10, notification_methods: ['email', 'dashboard'], alert_emails: '', notes: '' };
            this.currentInventoryLevel = null;
            setTimeout(() => { if (!this.showLowInventoryModal) this.currentAlertProduct = null; }, 300);
        },
        saveLowInventoryAlert() {
            if (!this.currentAlertProduct) { this.showLowInventoryModal = false; return; }
            if (!this.isAlertConfigValid) { this.$bvToast.toast("Please fill all required fields", { title: "Validation Error", variant: "warning" }); return; }
            const alertData = {
                product_id: this.currentAlertProduct.id, product_sku: this.currentAlertProduct.sku,
                alert_type: this.alertConfig.alert_type, threshold_quantity: this.alertConfig.threshold_quantity,
                notification_methods: this.alertConfig.notification_methods, alert_emails: this.alertConfig.alert_emails,
                notes: this.alertConfig.notes, created_by: this.packet.userDetails.username
            };
            const that = this;
            Requestor.postSyncP("/api/product/set_low_inventory_alert/", alertData, this,
                function(response) {
                    if (response.data && response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", variant: "danger" });
                    else {
                        that.$bvToast.toast(`Alert configured for ${that.currentAlertProduct.sku}`, { title: "Alert Set", variant: "success" });
                        const index = that.allItems.findIndex(p => p.id === that.currentAlertProduct.id);
                        if (index !== -1) {
                            that.allItems[index].low_inventory_enabled = true;
                            that.allItems[index].low_inventory_alert_id = response.data.result.id;
                            that.allItems[index].low_inventory_threshold = alertData.threshold_quantity;
                            that.allItems = [...that.allItems];
                            that.updateDisplayedItems();
                        }
                        that.showLowInventoryModal = false;
                        that.currentAlertProduct = null;
                    }
                },
                function(error) { that.$bvToast.toast(error.message || "Failed to set alert", { title: "Error", variant: "danger" }); }
            );
        },
        hasLowInventoryAlert(item) {
            if (item.low_inventory_enabled) return true;
            if (item.properties && item.properties.low_inventory === true) return true;
            return false;
        },
        isLowInventoryTriggered(item) {
            if (item.is_low_inventory_triggered !== undefined) return item.is_low_inventory_triggered;
            const available = item.available || 0;
            const threshold = item.low_inventory_threshold || 5;
            return this.hasLowInventoryAlert(item) && available <= threshold;
        },

        downloadBarcodeImage(imageUrl, sku) {
            fetch(imageUrl).then(r => r.blob()).then(blob => {
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.href = url;
                link.download = `barcode_${sku}.png`;
                link.click();
                URL.revokeObjectURL(url);
            }).catch(() => {});
        },
        toggleBarcode() { this.ShowBarcode = !this.ShowBarcode; },
        generateSingleBarcode(item) {
            this.generatingSingleBarcode = true;
            const path = "/api/product/generate_single_barcode/";
            const data = { product_sku: item.sku, product_id: item.id, folder: "product_barcodes" };
            const that = this;
            Requestor.postAsyncP(path, data, this,
                function(response) {
                    that.generatingSingleBarcode = false;
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    else if (response.data.result) {
                        const index = that.allItems.findIndex(i => i.id === item.id || i.sku === item.sku);
                        if (index !== -1) {
                            that.allItems[index].barcode_image = response.data.result.barcode_url;
                            that.allItems = [...that.allItems];
                            that.updateDisplayedItems();
                        }
                        that.$bvToast.toast("Barcode generated", { title: "Success", autoHideDelay: 2000, variant: "success" });
                    }
                },
                function(error) { that.generatingSingleBarcode = false; that.$bvToast.toast("Error generating barcode", { title: "Error", autoHideDelay: 3000, variant: "danger" }); }
            );
        },
        showFullBarcode(item) {
            if (item.barcode_image) {
                this.$bvModal.show('modal-barcode-viewer');
                this.$nextTick(() => {
                    this.currentBarcodeImage = item.barcode_image;
                    this.currentProductName = item.product_name || item.sku;
                    this.currentProductSku = item.sku;
                });
            } else this.$bvToast.toast("No barcode available", { title: "Info", autoHideDelay: 2000, variant: "info" });
        },
        generateAllBarcodes() {
            if (!confirm('Generate barcodes for ALL products without one? This may take minutes.')) return;
            this.generatingBarcodes = true;
            this.barcodeProgress = { total: 0, processed: 0, successful: 0, failed: 0, percentage: 0, message: 'Starting...', errors: [] };
            this.$bvModal.show('modal-barcode-progress');
            const path = "/api/product/generate_barcodes_for_all_skus/";
            const data = { batch_size: 50, skip_existing: true, folder: "product_barcodes", user_details: this.packet.userDetails };
            const that = this;
            Requestor.postAsyncP(path, data, this,
                function(response) {
                    that.generatingBarcodes = false;
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { title: "Error", variant: "danger", autoHideDelay: 3000 });
                        that.barcodeProgress.message = 'Error: ' + response.data.error;
                    } else {
                        const result = response.data.result;
                        that.barcodeProgress.total = result.total;
                        that.barcodeProgress.processed = result.total;
                        that.barcodeProgress.successful = result.successful;
                        that.barcodeProgress.failed = result.failed;
                        that.barcodeProgress.percentage = 100;
                        that.barcodeProgress.errors = result.errors || [];
                        that.barcodeProgress.message = `Done! ${result.successful} generated in ${result.duration_seconds}s`;
                        that.$bvToast.toast(`${result.successful} barcodes generated`, { title: "Complete", variant: result.failed === 0 ? "success" : "warning", autoHideDelay: 5000 });
                        that.fetch_init(true, true);
                    }
                },
                function(error) { that.generatingBarcodes = false; that.$bvToast.toast("Error starting generation", { title: "Error", variant: "danger" }); }
            );
        },

        toggleSelectionMode() {
            this.showSelectionMode = !this.showSelectionMode;
            if (!this.showSelectionMode) this.clearSelection();
        },
        clearSelection() { this.selectedProducts = []; this.selectAll = false; },
        toggleSelectAll(checked) { if (checked) this.selectedProducts = this.filtered_final.map(item => item.sku); else this.selectedProducts = []; },
        updateSelectAll() { this.selectAll = this.filtered_final.length > 0 && this.selectedProducts.length === this.filtered_final.length; },

        openBulkStatusModal(action) { this.bulkStatusAction = action; this.bulkStatusModalVisible = true; this.$bvModal.show('modal-bulk-status'); },
        openBulkDiscontinuedModal(action) {
            this.bulkDiscontinuedAction = action; this.discontinuedReason = ''; this.updateChildrenDiscontinued = false;
            this.discontinuedModalVisible = true;
            this.$bvModal.show('modal-bulk-discontinued');
        },
        executeBulkDiscontinuedChange() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            this.isLoadingMore = true;
            const data = {
                product_skus: this.selectedProducts,
                discontinued: this.bulkDiscontinuedAction,
                update_children: this.updateChildrenDiscontinued || false,
                user_details: { username: this.packet.userDetails.username, user_id: this.packet.userDetails.user_id }
            };
            const that = this;
            Requestor.postAsyncP("/api/product/change_bulk_product_status/", data, this,
                function(res) {
                    that.isLoadingMore = false;
                    if (res.data.error) that.$bvToast.toast(res.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    else {
                        that.$bvToast.toast(`Successfully ${that.bulkDiscontinuedAction ? 'discontinued' : 'restored'} ${res.data.result.updated_count || that.selectedProducts.length} products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                        that.clearSelection(); that.showSelectionMode = false; that.fetch_init(true, true);
                    }
                },
                function(err) { that.isLoadingMore = false; that.$bvToast.toast("Error updating discontinued", { title: "Error", autoHideDelay: 3000, variant: "danger" }); }
            );
            this.$bvModal.hide('modal-bulk-discontinued');
        },
        applyDiscontinuedFilter() {
            if (this.discontinuedFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else this.fetch_init(true);
        },
        executeBulkStatusChange() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            this.isLoadingMore = true;
            this.changeBulkProductStatus(this.selectedProducts, this.bulkStatusAction);
            this.$bvModal.hide('modal-bulk-status');
            this.bulkStatusModalVisible = false;
        },
        changeBulkProductStatus(productSkus, newStatus) {
            const data = {
                product_skus: productSkus, status: newStatus,
                update_children: this.updateChildren || false,
                user_details: { username: this.packet.userDetails.username, user_id: this.packet.userDetails.user_id }
            };
            const that = this;
            Requestor.postAsyncP("/api/product/change_bulk_product_status/", data, this,
                function(res) {
                    that.isLoadingMore = false;
                    if (res.data.error) that.$bvToast.toast(res.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    else {
                        that.$bvToast.toast(`Updated ${res.data.result.updated_count || productSkus.length} products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                        that.clearSelection(); that.showSelectionMode = false; that.fetch_init(true, true);
                    }
                },
                function(err) { that.isLoadingMore = false; that.$bvToast.toast("Error updating statuses", { title: "Error", autoHideDelay: 3000, variant: "danger" }); }
            );
        },
        applyItemStatusFilter() {
            this.currentOffset = 0; this.hasMore = true;
            this.allItems = []; this.displayedItems = [];
            this.fetchProducts(0, this.pageSize, false);
        },
        isOldProduct(item) {
            return (item.sold_so_far && item.sold_so_far > 0) || (item.received && item.received > 0) || (item.last_sale_date && moment().diff(moment(item.last_sale_date), 'days') > 30) || (item.created_at && moment().diff(moment(item.created_at), 'days') > 30) || (item.first_received_date && moment().diff(moment(item.first_received_date), 'days') > 30);
        },

        toggleimage() { this.Showimage = !this.Showimage; },
        toggleMainColumns() { this.showMainColumns = !this.showMainColumns; },
        togglePriceColumns() { this.showPriceColumns = !this.showPriceColumns; },
        toggleDiscountColumns() { this.showDiscountColumns = !this.showDiscountColumns; },
        toggleDetailColumns() { this.showDetailColumns = !this.showDetailColumns; },
        applyDetailFilters() {
            this.currentOffset = 0; this.hasMore = true;
            this.allItems = []; this.displayedItems = [];
            this.fetchProducts(0, this.pageSize, false);
        },
        resetDetailFilters() {
            this.typeFilter = null; this.colorFilter = null; this.sizeFilter = null;
            this.materialFilter = null; this.functionalityFilter = null;
        },
        fetchAllHierarchyOptions(categoryId = null) {
            const data = categoryId ? { category_id: categoryId } : {};
            const that = this;
            Requestor.getSyncP("/api/product/get_hierarchy_options/", data, this,
                function(response) {
                    if (response.data && !response.data.error && response.data.result) {
                        const options = response.data.result;
                        that.typeOptions = [{ value: null, text: 'ALL TYPES' }, ...(options.types || []).map(type => ({ value: type.id, text: type.type_name || type.name || 'Unknown' }))];
                        that.colorOptions = [{ value: null, text: 'ALL COLORS' }, ...(options.colors || []).map(color => ({ value: color.id, text: color.color_name || color.name || 'Unknown' }))];
                        that.sizeOptions = [{ value: null, text: 'ALL SIZES' }, ...(options.sizes || []).map(size => ({ value: size.id, text: size.size_name || size.name || 'Unknown' }))];
                        that.materialOptions = [{ value: null, text: 'ALL MATERIALS' }, ...(options.materials || []).map(material => ({ value: material.id, text: material.material_name || material.name || 'Unknown' }))];
                        that.functionalityOptions = [{ value: null, text: 'ALL FUNCTIONALITIES' }, ...(options.functionalities || []).map(func => ({ value: func.id, text: func.functionality_name || func.name || 'Unknown' }))];
                        that.subcategories = options.subcategories || [];
                        that.packet.subcategoriesdict = that.subcategories.reduce((map, obj) => { map[obj.id] = obj.subcategory || obj.name; return map; }, {});
                    }
                },
                function(error) { console.error(error); }
            );
        },
        fetchFilterOptions() {},

        applySlowestSellingFilter() {
            if (!this.slowestSellingFilter) { this.currentOffset = 0; this.hasMore = false; this.allItems = []; this.displayedItems = []; this.updateDisplayedItems(); return; }
            this.bestSellingFilter = null;
            this.items_being_loaded = true;
            const data = { time_period: this.slowestSellingFilter, limit: 1000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
            const that = this;
            Requestor.getSyncP("/api/product/get_slowest_selling_products/", data, this,
                function(response) {
                    that.items_being_loaded = false;
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    else {
                        that.allItems = response.data.result.map(item => ({ ...item, edit: false, available: item.available || 0, in_stock: item.in_stock || 0, reserved: item.reserved || 0, missing: 0 }));
                        that.updateDisplayedItems();
                        that.totalItemsCount = that.allItems.length;
                        that.hasMore = false;
                        that.fetchStockBreakdown(that.allItems.map(i => i.sku));
                    }
                },
                function(error) { that.items_being_loaded = false; that.$bvToast.toast("Error fetching slowest selling", { title: "Error", autoHideDelay: 1500, variant: "danger" }); }
            );
        },
        applySlowestSellingSort(items) {
            const filter = this.slowestSellingFilter;
            let sortedItems = items.map(item => {
                let received = 0, sold = 0;
                switch (filter) {
                    case 'slowest_7_days': received = item.received_7d || 0; sold = item.sold_7d || 0; break;
                    case 'slowest_30_days': received = item.received_30d || 0; sold = item.sold_30d || 0; break;
                    case 'slowest_60_days': received = item.received_60d || 0; sold = item.sold_60d || 0; break;
                    case 'slowest_90_days': received = item.received_90d || 0; sold = item.sold_90d || 0; break;
                    case 'slowest_all_time': received = item.received || 0; sold = item.sold_so_far || 0; break;
                }
                let score = 0;
                if (received > 0) score = sold === 0 ? received * 1000 : received / sold;
                return { ...item, _slowScore: score };
            });
            return sortedItems.sort((a, b) => b._slowScore - a._slowScore);
        },
        applyBestSellingFilter() {
            if (!this.bestSellingFilter) { this.currentOffset = 0; this.hasMore = false; this.allItems = []; this.displayedItems = []; this.updateDisplayedItems(); return; }
            this.slowestSellingFilter = null;
            this.items_being_loaded = true;
            const data = { time_period: this.bestSellingFilter, limit: 1000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
            const that = this;
            Requestor.getSyncP("/api/product/get_best_selling_products/", data, this,
                function(response) {
                    that.items_being_loaded = false;
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    else {
                        that.allItems = response.data.result.map(item => ({ ...item, edit: false, available: item.available || 0, in_stock: item.in_stock || 0, reserved: item.reserved || 0, missing: 0 }));
                        that.updateDisplayedItems();
                        that.totalItemsCount = that.allItems.length;
                        that.hasMore = false;
                        that.fetchStockBreakdown(that.allItems.map(i => i.sku));
                    }
                },
                function(error) { that.items_being_loaded = false; that.$bvToast.toast("Error fetching best selling", { title: "Error", autoHideDelay: 1500, variant: "danger" }); }
            );
        },
        applyBestSellingSort(items) {
            const filter = this.bestSellingFilter;
            const desc = this.bestSellingSortDesc;
            const byField = (field) => (a, b) => desc ? (b[field] || 0) - (a[field] || 0) : (a[field] || 0) - (b[field] || 0);
            let sortedItems = [...items];
            switch (filter) {
                case '7_days': sortedItems.sort(byField('sold_7d')); break;
                case '30_days': sortedItems.sort(byField('sold_30d')); break;
                case '60_days': sortedItems.sort(byField('sold_60d')); break;
                case '90_days': sortedItems.sort(byField('sold_90d')); break;
                case 'all_time': sortedItems.sort(byField('sold_so_far')); break;
                case 'profit_margin': sortedItems.sort((a, b) => desc ? this.calculateProfitMargin(b.landed_cost, b.selling_price) - this.calculateProfitMargin(a.landed_cost, a.selling_price) : this.calculateProfitMargin(a.landed_cost, a.selling_price) - this.calculateProfitMargin(b.landed_cost, b.selling_price)); break;
            }
            return sortedItems;
        },
        calculateProfitMargin(landedCost, sellingPrice) {
            landedCost = parseFloat(landedCost); sellingPrice = parseFloat(sellingPrice);
            if (!landedCost || !sellingPrice || landedCost <= 0 || sellingPrice <= 0) return 0;
            const profit = sellingPrice - landedCost;
            return Math.round((profit / sellingPrice) * 100 * 10) / 10;
        },

        async fetchProducts(offset = 0, limit = null, append = false) {
            if (this.isLoadingMore && append) return;
            this.isLoadingMore = true;
            if (!append) this.items_being_loaded = true;
            try {
                const path = "/api/product/get_products_list_v3/";
                const data = {
                    location_id: this.location_id, user_role: this.packet.userDetails.userrole,
                    user_location_id: this.user_location_id, offset: offset, limit: limit || this.pageSize
                };
                if (this.discontinuedFilter) data.discontinued = this.discontinuedFilter === 'discontinued';
                if (this.newArrivalFilter) data.is_new_arrival = this.newArrivalFilter === 'new';
                if (this.receivedStatusFilter === 'recently_received') data.recently_received = true;
                else if (this.receivedStatusFilter === 'not_recently_received') data.recently_received = false;
                if (this.typeFilter) data.type_id = this.typeFilter;
                if (this.colorFilter) data.color_id = this.colorFilter;
                if (this.sizeFilter) data.size_id = this.sizeFilter;
                if (this.materialFilter) data.material_id = this.materialFilter;
                if (this.functionalityFilter) data.functionality_id = this.functionalityFilter;
                if (this.itemTypeFilter === 'single') data.is_parent = false;
                else if (this.itemTypeFilter === 'group') data.is_parent = true;
                if (this.subcategory_id) data.subcategory_id = this.subcategory_id;
                if (this.category_id) data.category_id = this.category_id;
                if (this.vendor_id) data.vendor_id = this.vendor_id;
                if (this.product_query) data.product_query = this.product_query;
                if (this.statusFilter !== 'all') data.status = this.statusFilter;

                const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, data, this, resolve, reject); });
                if (response.data.result) {
                    const newItems = response.data.result.map(item => {
                        const expectedAvailable = (item.in_stock || 0) - (item.reserved || 0);
                        const missing = Math.max(0, expectedAvailable - (item.available || 0));
                        return {
                            ...item, edit: false,
                            quantity_per_box: item.quantity_per_box || (item.properties && item.properties.quantity_per_box) || 1,
                            wholesale_price: item.wholesale_price || 0, landed_cost: item.landed_cost || 0,
                            spiff: item.spiff || 0, cubes: item.cubes || 0,
                            in_stock: item.in_stock || 0, reserved: item.reserved || 0,
                            available: item.available || 0, missing: missing,
                            sold_7d: item.sold_7d || 0, sold_30d: item.sold_30d || 0, sold_60d: item.sold_60d || 0,
                            received_7d: item.received_7d || 0, received_30d: item.received_30d || 0, received_60d: item.received_60d || 0,
                            scheduled_7d: item.scheduled_7d || 0, scheduled_30d: item.scheduled_30d || 0, scheduled_60d: item.scheduled_60d || 0,
                            special_discount_percent: item.special_discount_percent || 10,
                            special_discount_start_date: item.special_discount_start_date || null,
                            special_discount_end_date: item.special_discount_end_date || null,
                            sale_type: item.sale_type || null,
                            max_discount_percentage: item.max_discount_percentage || null,
                            image_url: item.image_url || null,
                            discontinued: item.discontinued || false,
                            discontinued_date: item.discontinued_date || null,
                            is_new_arrival: item.is_new_arrival || false,
                            new_arrival_expiry_date: item.new_arrival_expiry_date || null,
                            recently_received: item.recently_received || false,
                        };
                    });
                    if (append) this.allItems = [...this.allItems, ...newItems];
                    else { this.allItems = newItems; this.totalItemsCount = newItems.length; }
                    this.updateDisplayedItems();
                    this.hasMore = newItems.length === this.pageSize;
                    this.currentOffset = offset + newItems.length;
                    this.totalItemsCount = this.allItems.length;

                    this.fetchStockBreakdown(newItems.map(i => i.sku));
                }
            } catch (error) {
                console.error(error);
                this.$bvToast.toast("Error fetching products list", { title: "Error", autoHideDelay: 1500, variant: "danger" });
            } finally {
                this.items_being_loaded = false;
                this.isLoadingMore = false;
            }
        },
        getSubcategoryLabel(subcategoryId) {
            if (!subcategoryId) return '';
            const subcategory = this.subcategories.find(sub => sub.id === subcategoryId);
            return subcategory ? subcategory.subcategory : subcategoryId;
        },
        updateDisplayedItems() { this.displayedItems = this.allItems; },
        async loadMore() {
            if (this.isLoadingMore || !this.hasMore) return;
            await this.fetchProducts(this.currentOffset, this.pageSize, true);
        },
        handleScroll(event) {
            const container = event.target;
            if (container.scrollTop === this.lastScrollTop) return;
            this.lastScrollTop = container.scrollTop;
            const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            if (scrollBottom < 100 && !this.isLoadingMore && this.hasMore) this.loadMore();
        },
        async fetch_init(first, forceFetch = false) {
            if (first) {
                this.vendor_id = null; this.category_id = null; this.subcategory_id = null; this.product_query = null;
                this.bestSellingFilter = null; this.slowestSellingFilter = null; this.statusFilter = 'active';
                this.itemTypeFilter = null; this.itemStatusFilter = null;
                this.discontinuedFilter = null; this.newArrivalFilter = null; this.receivedStatusFilter = null;
                this.resetDetailFilters();
                if (this.packet.userDetails.userrole === 'admin') this.location_id = null;
                else this.location_id = this.user_location_id;
            }
            this.stockBreakdown = {};
            if (first || this.hasActiveFilters || forceFetch) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        async fetch_category() {
            this.resetDetailFilters(); this.showCategoryStats = false;
            if (this.category_id) {
                this.subcategory_id = null;
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else await this.fetch_init(true);
        },
        async fetch_vendor() {
            if (this.vendor_id) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else await this.fetch_init(true);
        },
        applyStatusFilter() {
            if (this.statusFilter === 'not-active') this.statusFilter = 'notactive';
            if (this.statusFilter !== 'all') this.fetch_init(false, true);
            else this.fetch_init(true);
        },
        applySpiffFilter() {
            if (this.spiffFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyReceivedStatusFilter() {
            if (this.receivedStatusFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        async fetch_location() {
            if (this.packet.userDetails.userrole !== 'admin') { this.location_id = this.user_location_id; return; }
            if (this.location_id) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        async fetch_product_name_sku() {
            clearTimeout(this.searchTimeout);
            if (this.product_query && this.product_query.length > 0) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },

        showCsvUpload() { this.csvFile = null; this.csvPreview = []; this.$bvModal.show('modal-csv-upload'); },
        clearCsvUpload() { this.csvFile = null; this.csvPreview = []; this.$bvModal.hide('modal-csv-upload'); },
        onFileSelect(file) {
            if (!file) { this.csvPreview = []; return; }
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csvText = e.target.result;
                    const lines = csvText.split(/\r?\n/);
                    if (lines.length < 2) { this.$bvToast.toast("CSV empty or invalid", { title: "Error", autoHideDelay: 2000, variant: "danger" }); return; }
                    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                    const expectedHeaders = ['sku', 'description', 'category', 'sub category', 'vendor', 'qty per box', 'fob price', 'landed cst', 'whlsl price', 'sell price', 'cubes', 'spiff'];
                    if (headers.length !== expectedHeaders.length) { this.$bvToast.toast(`Expected ${expectedHeaders.length} columns, found ${headers.length}`, { title: "Error", autoHideDelay: 2000, variant: "danger" }); return; }
                    this.csvPreview = [];
                    for (let i = 1; i < Math.min(6, lines.length); i++) {
                        if (lines[i].trim()) {
                            const values = this.parseCSVLine(lines[i]);
                            if (values.length === expectedHeaders.length) {
                                this.csvPreview.push({
                                    sku: values[0], description: values[1], category: values[2], sub_category: values[3], vendor: values[4],
                                    quantity_per_box: values[5], fob_price: values[6], landed_cost: values[7], wholesale_price: values[8],
                                    selling_price: values[9], cubes: values[10], spiff: values[11]
                                });
                            }
                        }
                    }
                } catch (error) { console.error(error); this.$bvToast.toast("Error parsing CSV", { title: "Error", autoHideDelay: 2000, variant: "danger" }); }
            };
            reader.readAsText(file);
        },
        parseCSVLine(line) {
            const result = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') inQuotes = !inQuotes;
                else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
                else current += char;
            }
            result.push(current.trim());
            return result;
        },
        async processCsvUpload() {
            if (!this.csvFile) { this.$bvToast.toast("Please select a CSV file first", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            this.uploadingCsv = true;
            try {
                const csvText = await this.readFileAsText(this.csvFile);
                const lines = csvText.split(/\r?\n/);
                const products = [];
                for (let i = 1; i < lines.length; i++) {
                    if (lines[i].trim()) {
                        const values = this.parseCSVLine(lines[i]);
                        if (values.length === 12) {
                            const categoryId = this.findCategoryId(values[2]);
                            const subcategoryId = this.findSubcategoryId(values[3], categoryId);
                            const vendorId = this.findVendorId(values[4]);
                            const product = {
                                product_sku: values[0].replace(/\s/g, ''), product_name: values[1],
                                category_id: categoryId, subcategory_id: subcategoryId, vendor_id: vendorId,
                                quantity_per_box: parseInt(values[5]) || 1, fob_price: parseFloat(values[6]) || 0,
                                landed_cost: parseFloat(values[7]) || 0, wholesale_price: parseFloat(values[8]) || 0,
                                selling_price: parseFloat(values[9]) || 0, cubes: parseFloat(values[10]) || 0,
                                spiff: parseFloat(values[11]) || 0,
                                properties: { spiff: parseFloat(values[11]) || 0, cubes: parseFloat(values[10]) || 0, quantity_per_box: parseInt(values[5]) || 1 }
                            };
                            if (product.product_sku && product.product_name && product.category_id && product.vendor_id) products.push(product);
                        }
                    }
                }
                if (products.length === 0) throw new Error('No valid products found in CSV.');
                await this.uploadProductsBatch(products);
            } catch (error) {
                console.error(error);
                this.$bvToast.toast(error.message || "Error processing CSV", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally { this.uploadingCsv = false; }
        },
        findSubcategoryId(subcategoryName, categoryId) {
            if (!subcategoryName || !categoryId) return null;
            const cleanSubcategoryName = subcategoryName.trim().toLowerCase();
            const subcategory = this.subcategories.find(subcat => subcat.category_id == categoryId && (subcat.subcategory || '').trim().toLowerCase() === cleanSubcategoryName);
            return subcategory ? subcategory.id : null;
        },
        readFileAsText(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = e => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsText(file);
            });
        },
        findCategoryId(categoryName) {
            if (!categoryName) return null;
            const name = categoryName.trim().toLowerCase();
            const cats = this.packet.categories.filter(cat => cat.value !== null);
            const exact = cats.find(cat => cat.text.trim().toLowerCase() === name);
            if (exact) return exact.value;
            const category = cats.find(cat => cat.text.toLowerCase().includes(name) || name.includes(cat.text.toLowerCase()));
            return category ? category.value : null;
        },
        findVendorId(vendorName) {
            if (!vendorName) return null;
            const cleanVendorName = vendorName.trim().toLowerCase();
            let vendor = this.packet.vendors.find(vend => vend.value !== null && vend.text.trim().toLowerCase() === cleanVendorName);
            if (!vendor) return this.handleVendorMatching(vendorName, cleanVendorName);
            return vendor.value;
        },
        handleVendorMatching(vendorName, cleanVendorName) {
            const vendorMappings = { 'global': 'GLOBAL', 'global trading': 'GLOBAL TRADING' };
            const mappedVendor = vendorMappings[cleanVendorName];
            if (mappedVendor) {
                const vendor = this.packet.vendors.find(vend => vend.text.trim().toLowerCase() === mappedVendor.toLowerCase());
                return vendor ? vendor.value : null;
            }
            return null;
        },
        uploadProductsBatch(products) {
            return new Promise((resolve, reject) => {
                Requestor.postSyncP("/api/product/add_batch_products/", { items: products }, this,
                    (response) => {
                        if (response.data.error) reject(new Error(response.data.error));
                        else {
                            this.$bvToast.toast(`Uploaded ${products.length} products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                            this.clearCsvUpload();
                            this.fetch_init(true);
                            resolve(response.data);
                        }
                    },
                    (error) => reject(error)
                );
            });
        },

        async downloadCSV(downloadAll = false) {
            try {
                this.downloadingExcel = true;
                let dataToExport;
                if (downloadAll) {
                    const response = await this.fetchExportData();
                    dataToExport = response.data.result || [];
                } else dataToExport = this.filtered_final;
                if (dataToExport.length === 0) { this.$bvToast.toast("No data to export", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
                this.generateCSVFile(dataToExport, downloadAll ? 'all' : 'current');
            } catch (error) {
                console.error(error);
                this.$bvToast.toast("Error exporting to CSV", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally { this.downloadingExcel = false; }
        },
        async downloadByCategory() {
            if (!this.category_id) { this.$bvToast.toast("Please select a category first", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            try {
                this.downloadingExcel = true;
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP("/api/product/get_products_list_v3/", { category_id: this.category_id, limit: 10000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id }, this, resolve, reject); });
                if (response.data.result) this.generateCSVFile(response.data.result, 'category');
                else throw new Error(response.data.error || 'Failed');
            } catch (error) { this.$bvToast.toast("Error exporting category", { title: "Error", autoHideDelay: 2000, variant: "danger" }); }
            finally { this.downloadingExcel = false; }
        },
        async downloadByVendor() {
            if (!this.vendor_id) { this.$bvToast.toast("Please select a vendor first", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            try {
                this.downloadingExcel = true;
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP("/api/product/get_products_list_v3/", { vendor_id: this.vendor_id, limit: 10000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id }, this, resolve, reject); });
                if (response.data.result) this.generateCSVFile(response.data.result, 'vendor');
                else throw new Error(response.data.error || 'Failed');
            } catch (error) { this.$bvToast.toast("Error exporting vendor", { title: "Error", autoHideDelay: 2000, variant: "danger" }); }
            finally { this.downloadingExcel = false; }
        },
        fetchExportData() {
            return new Promise((resolve, reject) => {
                const data = { location_id: this.location_id, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id, limit: 10000 };
                if (this.category_id) data.category_id = this.category_id;
                if (this.vendor_id) data.vendor_id = this.vendor_id;
                if (this.product_query) data.product_query = this.product_query;
                if (this.statusFilter !== 'all') data.status = this.statusFilter;
                if (this.discontinuedFilter) data.discontinued = this.discontinuedFilter === 'discontinued';
                if (this.newArrivalFilter) data.is_new_arrival = this.newArrivalFilter === 'new';
                if (this.receivedStatusFilter === 'recently_received') data.recently_received = true;
                else if (this.receivedStatusFilter === 'not_recently_received') data.recently_received = false;
                Requestor.getSyncP("/api/product/get_products_list_v3/", data, this, resolve, reject);
            });
        },
        formatDateTime(dateString) { if (!dateString) return ''; return moment(dateString).format('YYYY-MM-DD HH:mm:ss'); },
        generateCSVFile(exportData, type = 'current') {
            const csvData = this.prepareExportData(exportData);
            const headers = Object.keys(csvData[0] || {});
            let csvContent = headers.join(',') + '\n';
            csvData.forEach(row => {
                const rowData = headers.map(header => {
                    let value = row[header];
                    if (value === 0 || value === '0') return '0';
                    if (typeof value === 'number') return value.toString();
                    if (value === null || value === undefined || value === '') return '';
                    value = value.toString();
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) value = '"' + value.replace(/"/g, '""') + '"';
                    return value;
                });
                csvContent += rowData.join(',') + '\n';
            });
            const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
            let filename = `products_export_${timestamp}`;
            if (type === 'all') filename += '_all_filtered';
            else if (type === 'category') filename += '_category';
            else if (type === 'vendor') filename += '_vendor';
            filename += '.csv';
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.$bvToast.toast(`Exported ${exportData.length} products to CSV`, { title: "Success", autoHideDelay: 2000, variant: "success" });
        },
        prepareExportData(data) {
            return data.map(item => ({
                'SKU': this.handleNull(item.sku, ''),
                'Product Name': this.handleNull(item.product_name, ''),
                'Category': this.packet.categoriesdict[item.category_id] || '',
                'Sub Category': this.getSubcategoryLabel(item.subcategory_id) || '',
                'Vendor': this.packet.vendorsdict[item.vendor_id] || '',
                'Selling Price': this.formatPrice(item.selling_price),
                'Sale Type': this.hasActiveDiscount(item) ? (item.sale_type || 'Special') : '',
                'Sale Price': this.hasActiveDiscount(item) ? this.formatPrice(item.special_discount_price) : '',
                'Sale Starts': this.hasActiveDiscount(item) ? this.formatDate(item.special_discount_start_date) : '',
                'Sale Ends': this.hasActiveDiscount(item) ? this.formatDate(item.special_discount_end_date) : '',
                'In Stock': this.handleNull(item.in_stock, 0),
                'Available': this.handleNull(item.available, 0),
                'Sold So Far': this.handleNull(item.sold_so_far, 0),
                'Ordered': this.handleNull(item.ordered, 0),
                'Scheduled': this.handleNull(item.scheduled, 0),
                'Status': this.handleNull(item.status, ''),
                'Discontinued': item.discontinued ? 'Yes' : 'No',
                'New Arrival': item.is_new_arrival ? 'Yes' : 'No'
            }));
        },
        formatPrice(price) {
            if (!price && price !== 0) return 0;
            const num = parseFloat(price);
            return isNaN(num) ? 0 : Math.round(num * 100) / 100;
        },
        handleNull(value, defaultValue = 0) {
            if (value === null || value === undefined || value === '') return defaultValue;
            return value;
        },

        change_product_status(item, status) {
            const that = this;
            Requestor.postAsyncP("/api/product/change_product_status/", { product_sku: item.sku, status: status }, this,
                function(response) {
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    else {
                        that.$bvToast.toast("Changed product status.", { title: "Success", autoHideDelay: 1500, variant: "success" });
                        item.status = status;
                        that.fetch_init(false, true);
                    }
                },
                function(error) { that.$bvToast.toast("Error changing status", { title: "Error", autoHideDelay: 1500, variant: "danger" }); }
            );
        },
        delete_product(item) {
            if (!confirm(`Delete product ${item.sku}? This cannot be undone.`)) return;
            const that = this;
            Requestor.postAsyncP("/api/product/delete_v1/", item, this,
                function(response) {
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    else {
                        that.$bvToast.toast("Deleted product.", { title: "Success", autoHideDelay: 1500, variant: "success" });
                        that.fetch_init(false, true);
                    }
                },
                function(error) { that.$bvToast.toast("Error deleting product.", { title: "Error", autoHideDelay: 1500, variant: "danger" }); }
            );
        },
        sortby(key) {
            if (key !== this.activeSortKey) this.sort_asc = true;
            const asc = this.sort_asc;
            if (asc) this.displayedItems = Requestor.sort(this.displayedItems, key);
            else this.displayedItems = Requestor.sort_reverse(this.displayedItems, key);
            this.sort_asc = !this.sort_asc;
            this.activeSortKey = key;
            this.activeSortAsc = asc;
        },
        onSortSelect(key) { if (!key) return; this.activeSortKey = null; this.sortby(key); },
        pcStockState(item) {
            const avail = parseFloat(item.available) || 0;
            if (avail <= 0) return 'out';
            if (this.isLowInventoryTriggered(item)) return 'low';
            return 'ok';
        },
        pcIsOpen(item) {
            return item._open !== undefined ? item._open : this.pcExpandAll;
        },
        pcToggle(item) { this.$set(item, '_open', !this.pcIsOpen(item)); },
        pcSetExpandAll(open) {
            this.pcExpandAll = open;
            (this.displayedItems || []).forEach(it => { if (it._open !== undefined) this.$delete(it, '_open'); });
        },
        pcSortClass(key) { return ['pc-head-sort', { 'is-active': this.activeSortKey === key }]; },
        show_item_stats(item) { this.stats_item = item; this.$bvModal.show("modal-product-stats"); },
        close_item_stats() { this.$bvModal.hide("modal-product-stats"); },
        monitor_modal_change(changed_product) {
            let item = this.allItems.filter(d => d.sku == changed_product.product_sku);
            if (item.length > 0) {
                item = item[0];
                this.filtered_items = this.allItems.map(u => u.id !== item.sku ? u : item);
            }
        },

        getProductImage(item) {
            if (!item || !item.image_url) return null;
            let imageUrl = item.image_url.trim();
            if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') return null;
            if (imageUrl.includes('fmapi.myfurnituremecca.com')) return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
            return imageUrl;
        },
        showFullImage(item) {
            if (!item) return;
            const imageUrl = this.getProductImage(item);
            if (imageUrl) {
                this.$bvModal.show('modal-image-viewer');
                this.$nextTick(() => {
                    this.currentFullSizeImage = imageUrl;
                    this.currentProductName = item.product_name || item.sku || 'Product Image';
                });
            } else this.$bvToast.toast("No image available", { title: "Info", autoHideDelay: 2000, variant: "info" });
        },
        openImageUpload(product) {
            this.uploadProduct = product; this.selectedFile = null; this.imagePreview = null;
            this.$bvModal.show('modal-image-upload');
        },
        triggerFileInput() { this.$refs.fileInput.click(); },
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (!file) return;
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) { this.$bvToast.toast('Please select a valid image file', { title: "Invalid File", autoHideDelay: 2000, variant: "warning" }); return; }
            if (file.size > 5 * 1024 * 1024) { this.$bvToast.toast('Image size should be less than 5MB', { title: "File Too Large", autoHideDelay: 2000, variant: "warning" }); return; }
            this.selectedFile = file;
            const reader = new FileReader();
            reader.onload = (e) => { this.imagePreview = e.target.result; };
            reader.readAsDataURL(file);
        },
        clearImage() { this.selectedFile = null; this.imagePreview = null; if (this.$refs.fileInput) this.$refs.fileInput.value = ''; },
        resetImageUpload() {
            this.uploadProduct = null; this.selectedFile = null; this.imagePreview = null; this.uploadingImage = false;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },
        async uploadProductImage() {
            if (!this.selectedFile || !this.uploadProduct) return;
            this.uploadingImage = true;
            try {
                const base64Image = await this.fileToBase64(this.selectedFile);
                const response = await new Promise((resolve, reject) => { Requestor.postSyncP("/api/product/upload_product_image/", { product_sku: this.uploadProduct.sku, product_id: this.uploadProduct.id, image_data: base64Image }, this, resolve, reject); });
                if (response.data.error) throw new Error(response.data.error);
                this.updateProductInList(response.data.result);
                this.$bvToast.toast("Image uploaded", { title: "Success", autoHideDelay: 2000, variant: "success" });
                this.$bvModal.hide('modal-image-upload');
            } catch (error) { this.$bvToast.toast(error.message || "Error uploading image", { title: "Error", autoHideDelay: 2000, variant: "danger" }); }
            finally { this.uploadingImage = false; }
        },
        updateProductInList(updatedProduct) {
            if (!updatedProduct) return;
            const productIndex = this.allItems.findIndex(item => item.id === updatedProduct.id || item.sku === updatedProduct.sku);
            if (productIndex !== -1) {
                this.allItems[productIndex].image_url = updatedProduct.image_url;
                this.$delete(this.allItems[productIndex], '_imgError');
                this.allItems = [...this.allItems];
                this.updateDisplayedItems();
            }
        },
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },
        fetchSubcategories() {
            const that = this;
            Requestor.postSyncP("/api/product/get_subcategories/", {}, this,
                function(response) {
                    if (response.data && !response.data.error) {
                        that.subcategories = response.data.result || [];
                        that.packet.subcategoriesdict = that.subcategories.reduce((map, obj) => { map[obj.id] = obj.subcategory || obj.name; return map; }, {});
                    }
                },
                function(error) { console.error(error); }
            );
        },
        fetchSubcategory() {
            this.showCategoryStats = false;
            if (this.subcategory_id) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else if (this.category_id) {
                this.fetch_category();
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyFilters() {
            if (this.itemTypeFilter !== null) this.fetch_init(false, true);
            else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyNewArrivalFilter() {
            if (this.newArrivalFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else this.fetch_init(true);
        },
        openBulkNewArrivalModal(action) {
            this.bulkNewArrivalAction = action; this.newArrivalDays = 30; this.updateChildrenNewArrival = false;
            this.newArrivalModalVisible = true;
            this.$bvModal.show('modal-bulk-new-arrival');
        },
        executeBulkNewArrivalChange() {
            if (this.selectedProducts.length === 0) { this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" }); return; }
            this.isLoadingMore = true;
            const data = {
                product_skus: this.selectedProducts,
                new_arrival: this.bulkNewArrivalAction,
                new_arrival_days: this.bulkNewArrivalAction ? this.newArrivalDays : 30,
                update_children: this.updateChildrenNewArrival || false,
                user_details: { username: this.packet.userDetails.username, user_id: this.packet.userDetails.user_id }
            };
            const that = this;
            Requestor.postAsyncP("/api/product/change_bulk_product_status/", data, this,
                function(response) {
                    that.isLoadingMore = false;
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                    else {
                        that.$bvToast.toast(`Successfully ${that.bulkNewArrivalAction ? 'marked as new arrival' : 'removed new arrival'} for ${response.data.result.updated_count || that.selectedProducts.length} products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                        that.clearSelection(); that.showSelectionMode = false; that.fetch_init(true, true);
                    }
                },
                function(error) { that.isLoadingMore = false; that.$bvToast.toast("Error updating new arrival", { title: "Error", autoHideDelay: 3000, variant: "danger" }); }
            );
            this.$bvModal.hide('modal-bulk-new-arrival');
        },
        handleResize() { this.isMobile = window.innerWidth < 768; }
    },
    mounted() {
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        clearTimeout(this.searchTimeout);
    },
    created() {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.vendors.splice(0, 0, { value: null, text: "ALL" });
        this.packet.categories.splice(0, 0, { value: null, text: "ALL" });
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
        this.typeOptions = [{ value: null, text: 'ALL TYPES' }];
        this.colorOptions = [{ value: null, text: 'ALL COLORS' }];
        this.sizeOptions = [{ value: null, text: 'ALL SIZES' }];
        this.materialOptions = [{ value: null, text: 'ALL MATERIALS' }];
        this.functionalityOptions = [{ value: null, text: 'ALL FUNCTIONALITIES' }];
        this.fetchAllHierarchyOptions();
        this.fetchSubcategories();
        this.user_location_id = this.packet.userDetails.location_id || this.packet.userDetails.location || this.packet.userDetails.locationId;
        this.locationsDict = {};
        if (this.packet.locations && Array.isArray(this.packet.locations)) {
            this.packet.locations.forEach(loc => {
                const value = loc.value || loc.id || loc.location_id;
                const text = loc.text || loc.name || loc.location_name;
                this.locationsDict[value] = text;
            });
        }
        if (this.user_location_id && this.locationsDict[this.user_location_id]) {
            this.user_location_name = this.locationsDict[this.user_location_id];
        } else {
            this.user_location_name = "Default Location";
        }
        this.items_being_loaded = false;
        this.displayedItems = [];
        this.allItems = [];
        this.fetch_init(true);
    }
};
</script>

<style scoped>
/* ================================================
   TOOLBAR
   ================================================ */
.compact-toolbar {
    display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
    gap: 12px; background: #f8f9fa; padding: 8px 12px; border-radius: 8px;
    margin-bottom: 15px; border: 1px solid #e9ecef;
}
.toolbar-group { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; background: white; padding: 4px 10px; border-radius: 40px; border: 1px solid #e2e6ea; }
.toolbar-label { margin-left: 6px; font-size: 12px; font-weight: 500; }

/* FILTERS */
.filter-card { border: 1px solid #e8eaed; border-radius: 12px; background: #ffffff; overflow: hidden; margin-bottom: 1rem; }
.filter-primary { background: #ffffff; padding: 1.25rem 1.5rem; }
.filter-more { background: #fafbfc; border-top: 1px solid #eff2f5; padding: 1.25rem 1.5rem; }
.filter-label { font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #5f6b7a; margin-bottom: 0.5rem; display: block; }
.filter-label.required::after { content: " *"; color: #c93c30; }
.filter-select, .filter-input { border-radius: 8px; border: 1px solid #e2e6ea; background: #ffffff; font-size: 0.875rem; color: #2c3e50; }
.filter-search { border-radius: 8px; overflow: hidden; }
.filter-search .form-control { border-right: none; border-radius: 8px 0 0 8px; }
.filter-search .btn { border-radius: 0 8px 8px 0; background: #ffffff; border-left: none; color: #8e9aab; }
.filter-toggle-btn { color: #7a8798; font-size: 0.75rem; font-weight: 500; padding: 0.375rem 0.75rem; border-radius: 20px; background: #f5f7fa; }
.filter-badge { font-size: 0.7rem; padding: 0.25rem 0.6rem; font-weight: 500; border-radius: 20px; }
.reset-filters-btn { border-radius: 8px; font-size: 0.75rem; padding: 0.375rem 1rem; border: 1px solid #e2e6ea; background: #ffffff; color: #5f6b7a; }
.filter-active-badges { background: #fefce8; border-top: 1px solid #fef3c7; padding: 0.75rem 1.5rem; font-size: 0.75rem; display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
.filter-badge-active { background: #fef3c7; color: #92400e; font-weight: 500; padding: 0.25rem 0.6rem; border-radius: 20px; font-size: 0.7rem; border: 1px solid #fde68a; }
.filter-stats-actions { background: #e8f4fd; border-top: 1px solid #b8d4e8; padding: 8px 15px; }

/* EDIT MODAL */
.edit-product-form { padding: 4px; }
.edit-identity { display: flex; align-items: center; gap: 12px; padding: 10px; background: #fff; border: 1px solid #e5e1da; border-radius: 10px; }
.edit-identity-img { width: 56px; height: 56px; border-radius: 8px; background: #f6f4f0; display: flex; align-items: center; justify-content: center; overflow: hidden; color: #b9b1a6; flex-shrink: 0; }
.edit-identity-img img { width: 100%; height: 100%; object-fit: contain; }
.edit-identity-meta { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.edit-identity-meta strong { font-size: 14px; }
.edit-identity-sku { font-size: 12px; font-weight: 600; color: #736c63; }
.edit-identity-sku b { color: #34302B; font-weight: 700; }
.edit-tabs .nav-link { font-weight: 700; color: #736c63; border: 0; border-bottom: 3px solid transparent; background: transparent; }
.edit-tabs .nav-link.active { color: #d4620f; background: transparent; border-bottom-color: #ee7a22; }
.edit-tabs .nav-link:hover { border-bottom-color: #f5cfae; }
.edit-footer { border-top: 1px solid #e5e1da; }

/* DIALOGS */
.diver { overflow: auto; width: 100%; height: 600px; border: 1px solid #dee2e6 !important; }
.image-upload-container { padding: 15px; }
.upload-area { border: 2px dashed #dee2e6; border-radius: 10px; padding: 40px 20px; text-align: center; cursor: pointer; background-color: #f8f9fa; }
.upload-placeholder { color: #6c757d; }
.preview-image { max-width: 100%; max-height: 300px; border-radius: 8px; border: 1px solid #dee2e6; object-fit: contain; }
.bulk-image-upload-container { padding: 15px; }
.image-preview-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 15px; max-height: 400px; overflow-y: auto; padding: 10px; border: 1px solid #dee2e6; border-radius: 5px; }
.preview-item { text-align: center; padding: 10px; border: 1px solid #e9ecef; border-radius: 5px; background: #f8f9fa; }
.preview-thumb { max-width: 100%; max-height: 100px; object-fit: contain; margin-bottom: 5px; }
.preview-info { font-size: 12px; }
.preview-info strong { display: block; color: #007bff; }
.preview-info small { display: block; color: #6c757d; font-size: 10px; word-break: break-all; }

.print-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 15px; max-height: 500px; overflow-y: auto; padding: 10px; border: 1px solid #dee2e6; border-radius: 8px; background: #f8f9fa; }
.print-card { background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 10px; text-align: center; }
.print-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.print-number { background: #007bff; color: white; border-radius: 50%; width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; }
.print-barcode-preview { max-width: 100%; max-height: 70px; object-fit: contain; margin-bottom: 10px; }
.print-card-text { font-size: 11px; }
.print-sku { font-weight: bold; font-size: 12px; margin-bottom: 4px; }
.print-name { color: #666; font-size: 10px; margin-bottom: 2px; }
.print-price { color: #28a745; font-weight: bold; font-size: 11px; }

/* ================================================
   PRODUCTS LIST
   ================================================ */
.pc-list {
  --pc-ink: #221F1C; --pc-ink-2: #34302B;
  --pc-orange: #EE7A22; --pc-orange-dark: #D4620F; --pc-orange-soft: #FFF1E4;
  --pc-teal: #0F6E6B; --pc-teal-soft: #E2F2F0;
  --pc-red: #C93C30; --pc-red-soft: #FBE9E7;
  --pc-warn: #9A6A0C; --pc-warn-soft: #FDF3DE;
  --pc-panel: #F7F5F2; --pc-line: #E8E3DB; --pc-muted: #736C63;
  --pc-head: 'Sora', 'Manrope', system-ui, sans-serif;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--pc-ink);
}
.diver.pc-list {
  height: calc(100vh - 230px); min-height: 520px;
  overflow-y: auto; overflow-x: hidden;
  border: 0 !important; background: transparent;
  padding: 0 2px 16px; -webkit-overflow-scrolling: touch;
}
.pc-ico { position: relative !important; display: inline-block !important; width: 1em !important; height: 1em !important; flex-shrink: 0; vertical-align: -0.125em; line-height: 1; }
.pc-ico >>> svg { position: absolute !important; top: 50% !important; left: 50% !important; width: 1em !important; height: 1em !important; margin: 0 !important; transform: translate(-50%, -50%) !important; display: block !important; }
.pc-list >>> svg.b-icon { width: 1em; height: 1em; vertical-align: -0.125em; }

.pc-bar { position: sticky; top: 0; z-index: 7; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; padding: 8px 10px; margin-bottom: 8px; background: rgba(255, 255, 255, 0.97); border: 1px solid var(--pc-line); border-radius: 14px; }
.pc-bar-sort, .pc-bar-view { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pc-bar-label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--pc-muted); }
.pc-bar-label .pc-ico { color: var(--pc-orange); }
.pc-bar-select { height: 36px; min-width: 190px; padding: 0 30px 0 12px; border: 1.5px solid var(--pc-line); border-radius: 10px; background-color: var(--pc-panel); font-size: 14px; font-weight: 700; color: var(--pc-ink); }
.pc-bar-btn { display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 12px; border: 1.5px solid var(--pc-line); border-radius: 10px; background: #fff; font-size: 13px; font-weight: 700; color: var(--pc-ink); cursor: pointer; }
.pc-bar-count { font-size: 13px; font-weight: 700; color: var(--pc-muted); }
.pc-seg { display: inline-flex; padding: 3px; border-radius: 10px; background: var(--pc-panel); border: 1px solid var(--pc-line); }
.pc-seg button { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px; border: 0; border-radius: 8px; background: transparent; font-size: 13px; font-weight: 700; color: var(--pc-muted); cursor: pointer; }
.pc-seg button.is-on { background: var(--pc-ink); color: #fff; }

.pc-head { display: grid; grid-template-columns: var(--pc-cols); align-items: center; column-gap: 16px; padding: 0 15px 0 19px; margin-bottom: 8px; height: 36px; background: var(--pc-ink); border-radius: 12px; }
.pc-head-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pc-head-cell.is-num { justify-content: flex-start; }
.pc-head-sort { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: transparent; color: rgba(255, 255, 255, 0.72); font-size: 11.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; white-space: nowrap; }
.pc-head-sort:hover { color: #fff; }
.pc-head-sort.is-active { color: #FFB06B; }
.pc-head-static { display: inline-flex; align-items: center; color: rgba(255, 255, 255, 0.72); font-size: 11.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; white-space: nowrap; }
.pc-check { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 12px; font-weight: 700; cursor: pointer; }
.pc-check--head { color: #fff; }
.pc-check--inline { color: var(--pc-muted); margin-bottom: 4px; }

.pc-stats { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 12px 14px; margin-bottom: 10px; background: var(--pc-ink); color: #fff; border-radius: 14px; }
.pc-stats-title { display: flex; align-items: center; gap: 10px; min-width: 200px; }
.pc-stats-title > .pc-ico { font-size: 20px; color: #FFB06B; }
.pc-stats-title span { display: block; font-size: 11px; font-weight: 700; color: rgba(255, 255, 255, 0.6); text-transform: uppercase; letter-spacing: 0.06em; }
.pc-stats-title strong { display: block; font-family: var(--pc-head); font-size: 16px; }
.pc-stats-grid { flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(104px, 1fr)); gap: 8px; }
.pc-stats-grid > div { background: rgba(255, 255, 255, 0.07); border-radius: 10px; padding: 7px 10px; }
.pc-stats-grid span { display: block; font-size: 12px; font-weight: 600; color: rgba(255, 255, 255, 0.62); }
.pc-stats-grid b { font-family: var(--pc-head); font-size: 18px; font-variant-numeric: tabular-nums; }
.pc-stats-grid .is-good b { color: #7FDACF; }
.pc-stats-grid .is-bad b { color: #FFB4A8; }

.pc-card { position: relative; margin-bottom: 10px; background: #fff; border: 1px solid var(--pc-line); border-radius: 14px; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.pc-card::before { content: ''; position: absolute; left: -1px; top: 14px; width: 4px; height: 56px; border-radius: 0 4px 4px 0; background: var(--pc-teal); }
.pc-card.is-low::before { background: #E0A21B; }
.pc-card.is-out::before { background: var(--pc-red); }
.pc-card:hover { border-color: #D9D1C5; box-shadow: 0 8px 22px -16px rgba(34, 31, 28, 0.4); }
.pc-card.is-selected { border-color: var(--pc-orange); background: #FFFCF8; }
.pc-card.is-inactive { background: #FBFAF8; }

.pc-sum { display: grid; grid-template-columns: var(--pc-cols); align-items: start; column-gap: 16px; padding: 12px 14px 12px 18px; }
.pc-img { position: relative; width: 250px; height: 200px; border-radius: 12px; background: linear-gradient(160deg, #F7F3EE, #EDE8E1); overflow: hidden; }
.pc-img-btn { position: absolute; inset: 0; width: 100%; height: 100%; padding: 6px; border: 0; background: transparent; cursor: zoom-in; }
.pc-img-btn img { width: 100%; height: 100%; object-fit: contain; display: block; }
.pc-img-empty { position: absolute; inset: 0; color: #B9B1A6; font-size: 28px; }
.pc-img-empty > .pc-ico { position: absolute !important; top: 50% !important; left: 50%; transform: translate(-50%, -50%); }
.pc-img .pc-check { position: absolute; top: 5px; left: 5px; z-index: 2; padding: 2px 0 2px 6px; border-radius: 6px; background: rgba(255, 255, 255, 0.95); }

.pc-prod { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.pc-name { font-family: var(--pc-head); font-size: 15.5px; font-weight: 700; line-height: 1.3; color: var(--pc-ink); word-break: break-word; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pc-prod-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pc-sku { font-size: 16px; font-weight: 600; color: var(--pc-muted); }
.pc-sku b { color: var(--pc-ink-2); font-weight: 700; }
.pc-barcode { height: 28px; padding: 2px 6px; border: 1px solid var(--pc-line); border-radius: 7px; background: #fff; cursor: zoom-in; }
.pc-barcode img { display: block; height: 100%; max-width: 150px; object-fit: contain; }
.pc-barcode-gen { display: inline-flex; align-items: center; gap: 5px; height: 26px; padding: 0 9px; border: 1.5px dashed #E8B48A; border-radius: 7px; background: rgba(255, 241, 228, 0.6); color: var(--pc-orange-dark); font-size: 12px; font-weight: 800; cursor: pointer; }
.pc-badges { display: flex; flex-wrap: wrap; gap: 5px; }
.pc-badges:empty { display: none; }
.pc-badge { display: inline-block; padding: 2px 9px; border-radius: 99px; font-size: 11.5px; font-weight: 800; line-height: 1.4; background: #fff; border: 1px solid var(--pc-line); color: var(--pc-ink); white-space: nowrap; }
.pc-badge.is-dark { background: var(--pc-ink); border-color: var(--pc-ink); color: #fff; }
.pc-badge.is-good { background: var(--pc-teal-soft); border-color: #BFE1DD; color: var(--pc-teal); }
.pc-badge.is-danger { background: var(--pc-red-soft); border-color: #F3C9C3; color: var(--pc-red); }
.pc-badge.is-info { background: #E2EFF7; border-color: #C3DCEC; color: #1F6690; }
.pc-badge.is-orange { background: var(--pc-orange-soft); border-color: #F5CFAE; color: var(--pc-orange-dark); }
.pc-badge.is-warn { background: var(--pc-warn-soft); border-color: #F1DDAE; color: var(--pc-warn); }
.pc-badge.is-muted { background: #EFECE7; color: var(--pc-muted); }

.pc-sum-cell { min-width: 0; }
.pc-sum-k { font-size: 11.5px; font-weight: 700; letter-spacing: 0.03em; color: var(--pc-muted); text-transform: uppercase; }
.pc-sum-v { font-size: 14px; font-weight: 700; color: var(--pc-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
.pc-sum-v:last-child { margin-bottom: 0; }
.pc-sum-sub { color: var(--pc-muted); font-weight: 600; }
.pc-big { font-family: var(--pc-head); font-size: 21px; font-weight: 800; line-height: 1.2; font-variant-numeric: tabular-nums; color: var(--pc-ink); }
.pc-big.is-price { color: var(--pc-orange-dark); }
.pc-sum-note { font-size: 12px; font-weight: 600; color: var(--pc-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pc-sum-costs { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: 4px; font-size: 15px; color: var(--pc-muted); }
.pc-sum-costs span { white-space: nowrap; }
.pc-sum-costs b { color: var(--pc-ink-2); font-weight: 700; font-variant-numeric: tabular-nums; }

/* ============================================ */
/* SALE / PROMO column                           */
/* ============================================ */
.pc-sum-sale {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    border-radius: 10px;
    background: var(--pc-panel);
    border: 1px solid var(--pc-line);
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
}
.pc-sum-sale.is-active    { background: #FFF8F1; border-color: #F5CFAE; }
.pc-sum-sale.is-scheduled { background: #EEF5FB; border-color: #C3DCEC; }
.pc-sum-sale.is-expired   { border-style: dashed; }

.pc-sale-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 0 6px 6px;
    margin-bottom: 2px;
    border-bottom: 1px dashed var(--pc-line);
}
.pc-sale-title {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--pc-muted);
}
.pc-sale-pill {
    padding: 1px 8px;
    border-radius: 99px;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    border: 1px solid transparent;
}
.pc-sale-pill.is-sale      { background: var(--pc-orange); color: #fff; }
.pc-sale-pill.is-clearance { background: var(--pc-red); color: #fff; }
.pc-sale-pill.is-promo     { background: #1F6690; color: #fff; }
.pc-sale-pill.is-scheduled { background: #1F6690; color: #fff; }
.pc-sale-pill.is-expired   { background: #EFECE7; color: var(--pc-muted); border-color: var(--pc-line); }
.pc-sale-pill.is-none      { background: #fff; color: var(--pc-muted); border-color: var(--pc-line); }

.pc-sale-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 6px;
    font-size: 13px;
}
.pc-sale-k {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--pc-muted);
}
.pc-sale-v {
    min-width: 0;
    text-align: right;
    font-weight: 700;
    color: var(--pc-ink-2);
    font-variant-numeric: tabular-nums;
}
.pc-sale-v small { display: block; font-size: 11px; font-weight: 600; }
.pc-sale-was { color: var(--pc-muted); font-weight: 600; font-size: 12px; margin-right: 4px; }
.pc-sale-now { font-family: var(--pc-head); font-size: 15px; font-weight: 800; color: var(--pc-orange-dark); }
.pc-sale-off {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 6px;
    background: var(--pc-orange-soft);
    color: var(--pc-orange-dark);
    font-size: 12px;
    font-weight: 800;
}
.pc-sale-save { color: var(--pc-teal); }
.pc-sale-left { color: var(--pc-muted); }
.pc-sale-left.is-urgent { color: var(--pc-red); font-weight: 800; }

.pc-sale-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    color: var(--pc-muted);
}
.pc-sale-empty > .pc-ico { font-size: 16px; opacity: 0.7; }
.pc-sale-empty b { display: block; font-size: 13px; color: var(--pc-ink-2); }
.pc-sale-empty small { display: block; font-size: 11.5px; font-weight: 600; }

.pc-sale-actions {
    display: flex;
    gap: 6px;
    margin-top: 4px;
    padding: 0 2px;
}
.pc-sale-btn {
    flex: 1 1 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid var(--pc-line);
    border-radius: 7px;
    background: #fff;
    color: var(--pc-orange-dark);
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
}
.pc-sale-btn:hover { border-color: var(--pc-orange); background: var(--pc-orange-soft); }
.pc-sale-btn.is-danger { color: var(--pc-red); }
.pc-sale-btn.is-danger:hover { border-color: var(--pc-red); background: var(--pc-red-soft); }
.pc-sale-btn:focus-visible { outline: 2px solid var(--pc-orange); outline-offset: 1px; }

/* ============================================ */
/* STOCK column — STORES | WH | ALL              */
/* ============================================ */
.pc-sum-stock {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    border-radius: 10px;
    background: var(--pc-panel);
    border: 1px solid var(--pc-line);
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
}
.pc-sum-stock.is-out { border-color: #F3C9C3; background: #FDF6F5; }
.pc-sum-stock.is-low { border-color: #F1DDAE; background: #FDFAF2; }

.pc-stock-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 6px 6px;
    margin-bottom: 2px;
    border-bottom: 1px dashed var(--pc-line);
}
.pc-stock-title {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--pc-muted);
}
.pc-stock-col-lbl {
    flex: 0 0 42px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--pc-muted);
    text-align: right;
    white-space: nowrap;
}
.pc-stock-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px;
    border-radius: 7px;
    font-size: 12.5px;
    line-height: 1.3;
    cursor: pointer;
    transition: background 0.12s ease;
}
.pc-stock-row:hover { background: var(--pc-orange-soft); }
.pc-stock-row:focus-visible { outline: 2px solid var(--pc-orange); outline-offset: 1px; }
.pc-stock-k {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.04em;
    color: var(--pc-muted);
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.pc-stock-v {
    flex: 0 0 42px;
    font-family: var(--pc-head);
    font-size: 13px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: var(--pc-ink-2);
    text-align: right;
}
.pc-stock-v.is-wh  { color: #0F6E6B; }
.pc-stock-v.is-all { color: var(--pc-ink); font-size: 13.5px; }
.pc-stock-v.is-reserved { color: #D4620F; }
.pc-stock-v.is-layaway { color: #8E4A8E; }
.pc-stock-v.is-transfer { color: #1F6690; }
.pc-stock-v.is-available { color: var(--pc-teal); }

/* ============================================ */
/* TOTALS column — vertical list                 */
/* ============================================ */
.pc-sum-totals {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 8px;
    border-radius: 10px;
    background: var(--pc-panel);
    border: 1px solid var(--pc-line);
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
}
.pc-totals-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px;
    padding: 0 6px 6px;
    margin-bottom: 2px;
    border-bottom: 1px dashed var(--pc-line);
}
.pc-totals-title {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--pc-muted);
}
.pc-totals-hint {
    font-size: 10px;
    font-weight: 600;
    color: var(--pc-muted);
    opacity: 0.8;
    white-space: nowrap;
}
.pc-totals-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 5px 6px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.12s ease;
}
.pc-totals-row:hover:not(:disabled) { background: var(--pc-orange-soft); }
.pc-totals-row:hover:not(:disabled) .pc-totals-arrow { opacity: 1; transform: translateX(2px); color: var(--pc-orange-dark); }
.pc-totals-row:focus-visible { outline: 2px solid var(--pc-orange); outline-offset: 1px; }
.pc-totals-row:disabled { cursor: progress; opacity: 0.6; }

.pc-totals-ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    font-size: 12px;
}
.pc-totals-ico.is-ord   { background: #E2EFF7; color: #1F6690; }
.pc-totals-ico.is-sched { background: #F3E8F3; color: #8E4A8E; }
.pc-totals-ico.is-recv  { background: var(--pc-teal-soft); color: var(--pc-teal); }
.pc-totals-ico.is-sold  { background: var(--pc-warn-soft); color: #B07A06; }

.pc-totals-k {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--pc-ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.pc-totals-v {
    flex-shrink: 0;
    min-width: 2ch;
    font-family: var(--pc-head);
    font-size: 15px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    text-align: right;
}
.pc-totals-v.is-ord   { color: #1F6690; }
.pc-totals-v.is-sched { color: #8E4A8E; }
.pc-totals-v.is-recv  { color: var(--pc-teal); }
.pc-totals-v.is-sold  { color: #B07A06; }

.pc-totals-arrow {
    flex-shrink: 0;
    font-size: 11px;
    color: var(--pc-muted);
    opacity: 0.35;
    transition: opacity 0.12s ease, transform 0.12s ease;
}

/* Actions */
.pc-sum-actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; flex-wrap: wrap; }
.pc-icon-btn, .pc-sum-actions >>> .pc-icon-btn { position: relative; width: 34px; height: 34px; padding: 0 !important; border: 1px solid var(--pc-line) !important; border-radius: 9px !important; background: #fff !important; color: var(--pc-ink-2) !important; font-size: 15px; box-shadow: none !important; cursor: pointer; }
.pc-icon-btn > .pc-ico, .pc-sum-actions >>> .pc-icon-btn > .pc-ico { position: absolute !important; top: 50% !important; left: 50%; transform: translate(-50%, -50%); }
.pc-icon-btn:hover, .pc-sum-actions >>> .pc-icon-btn:hover { border-color: var(--pc-orange) !important; color: var(--pc-orange-dark) !important; }
.pc-icon-btn.is-warn { background: var(--pc-warn-soft) !important; border-color: #F1DDAE !important; color: var(--pc-warn) !important; }
.pc-icon-btn.is-danger { background: var(--pc-red) !important; border-color: var(--pc-red) !important; color: #fff !important; }
.pc-toggle { background: var(--pc-panel) !important; }
.pc-card.is-open .pc-toggle { background: var(--pc-ink) !important; border-color: var(--pc-ink) !important; color: #fff !important; }
.pc-btn { display: inline-flex; align-items: center; gap: 5px; height: 34px; padding: 0 12px; border: 1px solid var(--pc-line); border-radius: 9px; background: #fff; font-size: 13px; font-weight: 800; color: var(--pc-ink); cursor: pointer; }
.pc-btn.is-primary { background: var(--pc-orange); border-color: var(--pc-orange); color: #fff; }
.pc-btn.is-primary:hover { background: var(--pc-orange-dark); }
.pc-sum-actions >>> .dropdown-toggle::after { display: none; }
.pc-sum-actions >>> .pc-menu { min-width: 220px; padding: 5px; border: 1px solid #E5E1DA; border-radius: 12px; box-shadow: 0 18px 40px -16px rgba(34, 31, 28, 0.45); }
.pc-sum-actions >>> .pc-menu .dropdown-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; font-size: 14px; font-weight: 600; color: var(--pc-ink); }
.pc-sum-actions >>> .pc-menu .dropdown-item .pc-ico { color: var(--pc-orange); }
.pc-sum-actions >>> .pc-menu .dropdown-item:hover { background: var(--pc-orange-soft); color: #B5520B; }
.pc-sum-actions >>> .pc-menu .pc-menu-danger, .pc-sum-actions >>> .pc-menu .pc-menu-danger .pc-ico { color: var(--pc-red); }
.pc-sum-actions >>> .pc-menu .pc-menu-danger:hover { background: var(--pc-red-soft); }
.pc-sum-actions >>> .pc-menu .dropdown-item.disabled { opacity: 0.5; }

.pc-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 10px; padding: 12px 14px 14px 18px; border-top: 1px solid var(--pc-line); background: #FCFBF9; border-radius: 0 0 14px 14px; }
.pc-sec { min-width: 0; padding: 10px 14px 6px; background: #fff; border: 1px solid var(--pc-line); border-radius: 12px; }
.pc-sec-title { display: flex; align-items: baseline; justify-content: space-between; margin: 0 0 4px; padding-bottom: 6px; border-bottom: 2px solid var(--pc-orange); font-family: var(--pc-head); font-size: 13px; font-weight: 700; color: var(--pc-ink); }
.pc-sec-title small { font-family: 'Manrope', sans-serif; font-size: 11px; font-weight: 600; color: var(--pc-muted); }
.pc-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 6px 0; border-bottom: 1px dashed var(--pc-line); font-size: 13.5px; }
.pc-row:last-child { border-bottom: 0; }
.pc-k { color: var(--pc-muted); font-weight: 600; white-space: nowrap; flex-shrink: 0; }
.pc-v { min-width: 0; font-weight: 700; color: var(--pc-ink); text-align: right; font-variant-numeric: tabular-nums; word-break: break-word; }
.pc-v small { display: block; font-size: 11.5px; font-weight: 600; color: var(--pc-orange-dark); }
.pc-muted { color: var(--pc-muted); font-weight: 600; }
.is-good { color: var(--pc-teal) !important; }
.is-bad { color: var(--pc-red) !important; }
.pc-row--btn { width: calc(100% + 12px); margin: 0 -6px; padding: 6px; border: 0; border-bottom: 1px dashed var(--pc-line); border-radius: 6px; background: transparent; text-align: left; cursor: pointer; }
.pc-row--btn:last-child { border-bottom: 0; }
.pc-row--btn:hover { background: var(--pc-orange-soft); }

.pc-loadmore { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 16px; font-weight: 700; color: var(--pc-muted); }
.pc-end { text-align: center; padding: 10px; font-size: 13px; font-weight: 700; color: var(--pc-muted); }

/* ================================================
   RESPONSIVE
   ================================================ */
@media (max-width: 1199.98px) {
  .pc-head { display: none; }
  .pc-sum {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-template-areas:
      "img  prod  prod  prod  price  act"
      "sale sale  stock stock totals totals";
    column-gap: 12px;
    row-gap: 12px;
  }
  .pc-img { grid-area: img; width: 76px; height: 76px; }
  .pc-prod { grid-area: prod; }
  .pc-sum-price { grid-area: price; }
  .pc-sum-sale { grid-area: sale; }
  .pc-sum-stock { grid-area: stock; }
  .pc-sum-totals { grid-area: totals; }
  .pc-sum-actions { grid-area: act; align-self: start; }
}
.pc-prod-taxonomy {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-top: 2px;
    font-size: 16px;
    font-weight: 600;
    color: var(--pc-muted);
}
.pc-tax-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.pc-tax-item > .pc-ico {
    font-size: 12px;
    color: var(--pc-orange);
    flex-shrink: 0;
}
.pc-tax-sub {
    color: var(--pc-ink-2);
    font-weight: 700;
}
@media (max-width: 767.98px) {
  .diver.pc-list { height: calc(100vh - 150px); min-height: 420px; }
  .pc-bar { padding: 8px; }
  .pc-bar-sort, .pc-bar-view { width: 100%; }
  .pc-bar-select { flex: 1 1 160px; min-width: 0; font-size: 16px; }
  .pc-bar-view { justify-content: space-between; }
  .pc-sum {
    grid-template-columns: 64px minmax(0, 1fr) auto;
    grid-template-areas:
      "img    prod   prod"
      "price  price  act"
      "sale   sale   sale"
      "stock  stock  stock"
      "totals totals totals";
    gap: 10px;
    padding: 12px 12px 12px 16px;
  }
  .pc-img { width: 64px; height: 64px; }
  .pc-name { font-size: 14.5px; }
  .pc-sum-price { padding: 8px 10px; border-radius: 10px; background: var(--pc-panel); }
  .pc-sum-note { white-space: normal; }
  .pc-big { font-size: 18px; }
  .pc-sum-actions { justify-content: flex-end; align-self: center; }
  .pc-icon-btn, .pc-sum-actions >>> .pc-icon-btn { width: 42px; height: 40px; }
  .pc-totals-row, .pc-stock-row { min-height: 40px; }
  .pc-sale-btn { height: 36px; }
  .pc-details { padding: 10px; gap: 8px; }
}
@media (max-width: 399.98px) {
  .pc-sum { grid-template-columns: 56px minmax(0, 1fr) auto; }
  .pc-img { width: 56px; height: 56px; }
}
@media (pointer: coarse) {
  .pc-icon-btn, .pc-sum-actions >>> .pc-icon-btn { width: 42px; height: 40px; }
  .pc-row--btn { min-height: 40px; }
  .pc-bar-select, .pc-bar-btn { height: 42px; }
  .pc-seg button { height: 36px; }
}
@media (prefers-reduced-motion: reduce) {
  .pc-card, .pc-totals-row, .pc-stock-row, .pc-totals-arrow { transition: none; }
}
</style>

<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap');

.pl-page, .pl-modal {
  --pl-ink: #221F1C; --pl-ink-2: #34302B;
  --pl-orange: #EE7A22; --pl-orange-dark: #D4620F; --pl-orange-soft: #FFF1E4;
  --pl-teal: #0F6E6B; --pl-teal-soft: #E2F2F0;
  --pl-red: #C93C30; --pl-red-soft: #FBE9E7;
  --pl-amber: #C98A12; --pl-amber-soft: #FDF3DE;
  --pl-line: #E5E1DA; --pl-mist: #F6F4F0; --pl-muted: #736C63;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--pl-ink);
}
.pl-page .compact-toolbar { background: var(--pl-ink) !important; border: 0 !important; border-radius: 16px !important; padding: 8px 10px !important; margin-bottom: 10px !important; gap: 8px !important; }
.pl-page .compact-toolbar .toolbar-group { background: rgba(255, 255, 255, 0.06) !important; border: 1px solid rgba(255, 255, 255, 0.12) !important; border-radius: 12px !important; padding: 3px 4px !important; gap: 2px !important; }
.pl-page .compact-toolbar .toolbar-group .btn, .pl-page .compact-toolbar .toolbar-group .b-dropdown > .btn { display: inline-flex !important; align-items: center; gap: 6px; min-height: 32px; padding: 5px 10px !important; border: 0 !important; border-radius: 9px !important; background: transparent !important; color: rgba(255, 255, 255, 0.86) !important; font-size: 12.5px !important; font-weight: 700 !important; box-shadow: none !important; }
.pl-page .compact-toolbar .toolbar-group .btn:hover:not(:disabled), .pl-page .compact-toolbar .toolbar-group .b-dropdown > .btn:hover { background: rgba(255, 255, 255, 0.1) !important; color: #fff !important; }
.pl-page .compact-toolbar .toolbar-group .btn-primary, .pl-page .compact-toolbar .toolbar-group .b-dropdown > .btn-primary { background: var(--pl-orange) !important; color: #fff !important; }
.pl-page .compact-toolbar .toolbar-group .btn-primary:hover { background: var(--pl-orange-dark) !important; }
.pl-page .compact-toolbar .toolbar-group .btn:disabled { opacity: 0.4 !important; }
.pl-page .compact-toolbar .badge { background: var(--pl-orange) !important; color: #fff !important; border-radius: 99px; }
.pl-page .btn { border-radius: 10px; font-weight: 700; }
.pl-page .btn-primary, .pl-page .btn-info { background: var(--pl-orange); border-color: var(--pl-orange); color: #fff; }
.pl-page .btn-primary:hover:not(:disabled), .pl-page .btn-info:hover:not(:disabled) { background: var(--pl-orange-dark); border-color: var(--pl-orange-dark); }
.pl-page .btn-success { background: var(--pl-teal); border-color: var(--pl-teal); }
.pl-page .btn-danger { background: var(--pl-red); border-color: var(--pl-red); }
.pl-page .btn-secondary { background: #fff; border-color: var(--pl-line); color: var(--pl-ink); }
.pl-page .filter-card { border: 1px solid var(--pl-line) !important; border-radius: 16px !important; background: #fff !important; overflow: hidden; box-shadow: none !important; }
.pl-page .filter-card .bg-light, .pl-page .filter-card .filter-stats-actions { background: var(--pl-mist) !important; border-color: var(--pl-line) !important; }
.pl-page label, .pl-page .col-form-label { font-size: 12.5px; font-weight: 700; color: var(--pl-muted); }
.pl-page .form-control, .pl-page .custom-select { min-height: 36px; border: 1.5px solid var(--pl-line); border-radius: 10px; font-size: 14px; font-weight: 600; color: var(--pl-ink); background-color: #fff; }
.pl-page .form-control:focus, .pl-page .custom-select:focus { border-color: var(--pl-orange); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18); }
.pl-page .filter-badge-active, .pl-page .badge-info { background: var(--pl-orange-soft) !important; color: var(--pl-orange-dark) !important; border: 1px solid #F5CFAE; border-radius: 99px; font-weight: 700; padding: 5px 10px; }
.pl-page .badge { border-radius: 99px; font-weight: 700; }
.pl-page .badge-primary { background: var(--pl-orange); }
.pl-page .badge-success { background: var(--pl-teal); }
.pl-page .badge-danger { background: var(--pl-red); }
.pl-page .dropdown-menu { border: 1px solid var(--pl-line); border-radius: 12px; padding: 5px; box-shadow: 0 18px 40px -16px rgba(34, 31, 28, 0.45); font-family: inherit; }
.pl-page .dropdown-item { border-radius: 8px; padding: 7px 10px; font-size: 13.5px; font-weight: 600; color: var(--pl-ink); }
.pl-page .dropdown-item:hover, .pl-page .dropdown-item:focus { background: var(--pl-orange-soft); color: #B5520B; }
.pl-page .dropdown-divider { border-color: #ECE8E1; margin: 4px; }

.pl-modal.modal-content { border: 0; border-radius: 18px; overflow: hidden; box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5); }
.pl-modal .modal-header { background: var(--pl-ink); color: #fff; border: 0; padding: 14px 20px; box-shadow: inset 0 -3px 0 var(--pl-orange); }
.pl-modal .modal-header .modal-title { font-family: 'Sora', 'Manrope', sans-serif; font-size: 17px; font-weight: 700; }
.pl-modal .modal-header .close { color: #fff; opacity: 0.8; text-shadow: none; }
.pl-modal .modal-body { background: #F7F5F2; font-size: 14px; }
.pl-modal .modal-footer { background: #fff; border-top: 1px solid var(--pl-line); }
.pl-modal .btn { border-radius: 10px; font-weight: 700; }
.pl-modal .btn-primary, .pl-modal .btn-info { background: var(--pl-orange); border-color: var(--pl-orange); color: #fff; }
.pl-modal .btn-primary:hover:not(:disabled) { background: var(--pl-orange-dark); border-color: var(--pl-orange-dark); }
.pl-modal .btn-success { background: var(--pl-teal); border-color: var(--pl-teal); }
.pl-modal .btn-secondary { background: #fff; border-color: var(--pl-line); color: var(--pl-ink); }
.pl-modal .form-control, .pl-modal .custom-select { border: 1.5px solid var(--pl-line); border-radius: 10px; }
.pl-modal .form-control:focus, .pl-modal .custom-select:focus { border-color: var(--pl-orange); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18); }

@media (max-width: 575.98px) {
  .pl-dialog.modal-dialog { margin: 0; max-width: 100%; min-height: 100%; }
  .pl-dialog .modal-content.pl-modal { border-radius: 0; min-height: 100vh; }
  .pl-page .form-control, .pl-page .custom-select, .pl-modal .form-control, .pl-modal .custom-select { font-size: 16px; }
}
@media (pointer: coarse) {
  .pl-page .compact-toolbar .toolbar-group .btn { min-height: 40px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Products/ProductManagerList.vue